// src/pages/demo/DemoHomeworkDetail.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { homeworkAPI, tutorialAPI, masterSlideshowAPI, studentTutorialAPI, studentSlideshowAPI, costAPI, tutorialAttemptAPI, studentTutorialLinkAPI, lessonAnalysisAPI, activityAPI, studentAPI, translationAPI, homeworkClassLinkAPI, contentLibraryAPI } from '../../services/api';
import { PROFILE_SHAPES, ShapeSVG } from '../../utils/profileShapes';
import ReactMarkdown from 'react-markdown';
import HomeworkDetailGuideOverlay from '../../components/HomeworkDetailGuideOverlay';
import { hasShownThisSession, isProgressiveGuideLoginEligible, markShownThisSession, setGuideUser } from '../../utils/guideSession';
import { PerformanceTrackerChart, SimpleBarChart } from '../PreTeachingReport';

// --- SAFEGUARD: FORCE DEMO MODE ---
const IS_DEMO = true; 

// --- BRAND PALETTE ---
const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    grey: '#F0F2F5',
    danger: '#E07A5F',
    warning: '#F2CC8F',
    success: '#81C784'
};

const WORKSHEET_FONT_OPTIONS = [11, 12, 13, 14, 15, 16, 18, 20];
const DEFAULT_WORKSHEET_FONT_SIZE = 13;
const USE_OPEN_DYSLEXIC_WORKSHEET_FONT = true;

const getWorksheetFontStorageKey = (homeworkId = '', studentId = '') => (
    `classcompass:worksheet-fonts:${homeworkId || 'unknown-homework'}:${studentId || 'unknown-student'}`
);

const getStudentTutorialStudentId = (studentTutorial) => {
    const raw = studentTutorial?.studentId;
    return (raw?._id || raw || studentTutorial?.student?._id || studentTutorial?.student || '').toString();
};

const readWorksheetFontSize = (homeworkId = '', studentId = '') => {
    if (!studentId || typeof window === 'undefined') return DEFAULT_WORKSHEET_FONT_SIZE;
    try {
        const raw = window.localStorage.getItem(getWorksheetFontStorageKey(homeworkId, studentId));
        if (!raw) return DEFAULT_WORKSHEET_FONT_SIZE;
        const parsed = JSON.parse(raw);
        if (Number(parsed)) return Number(parsed);
        if (parsed && typeof parsed === 'object') {
            if (Number(parsed.worksheetFontSize)) return Number(parsed.worksheetFontSize);
            const migratedSectionSize = Object.values(parsed).find(value => Number(value));
            if (migratedSectionSize) return Number(migratedSectionSize);
        }
    } catch {
        // Fall back to the default if local storage is unavailable or contains older invalid data.
    }
    return DEFAULT_WORKSHEET_FONT_SIZE;
};

const writeWorksheetFontSize = (homeworkId = '', studentId = '', fontSize = DEFAULT_WORKSHEET_FONT_SIZE) => {
    if (!studentId || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(getWorksheetFontStorageKey(homeworkId, studentId), JSON.stringify(Number(fontSize) || DEFAULT_WORKSHEET_FONT_SIZE));
    } catch {
        // If local storage is unavailable, keep the in-memory selection for the open preview only.
    }
};


const createCorrelationId = () => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch {
        // fallback below
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// --- CONSTANTS (UPDATED) ---
const TEXT_MODEL = 'gemini-2.5-flash';
// Default demo image model
const IMAGE_MODEL = 'gemini-2.5-flash-image'; 
const ROOT_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const STEP_LABELS = {
    SLIDESHOW: '🖼️ Slideshow',
    EXPLAINER: '📝 Explainer',
    SEQUENCE_1: '🔢 Sequences',
    CATEGORISE_1: '🗂️ Categorise',
};

const getStepLabel = (step = '') => {
    if (STEP_LABELS[step]) return STEP_LABELS[step];
    const glossaryMatch = String(step).match(/^GLOSSARY_(\d+)$/);
    if (glossaryMatch) return `🔑 Key Terms ${glossaryMatch[1]}`;
    const quizMatch = String(step).match(/^QUIZ_(\d+)$/);
    if (quizMatch) return `✅ Quiz ${quizMatch[1]}`;
    return step;
};

const buildPreviewGlossaryPanels = (glossary = [], keyTermsPerMatchingPage = null) => {
    const items = Array.isArray(glossary) ? glossary : [];
    if (items.length === 0) return [];

    if (Number(keyTermsPerMatchingPage) === 1) {
        return items.map(item => ({ terms: [item] }));
    }

    const explicitCount = [2, 4].includes(Number(keyTermsPerMatchingPage)) ? Number(keyTermsPerMatchingPage) : null;
    if (explicitCount) {
        const panels = [];
        for (let i = 0; i < items.length; i += explicitCount) {
            const terms = items.slice(i, i + explicitCount);
            if (terms.length > 0) panels.push({ terms });
        }
        return panels;
    }

    if (items.length <= 4) return [{ terms: items }];

    const firstSize = Math.ceil(items.length / 2);
    return [
        { terms: items.slice(0, firstSize) },
        { terms: items.slice(firstSize) }
    ];
};

const buildPreviewStepShortcuts = (studentTutorial, homework) => {
    const firstChunk = studentTutorial?.generatedContent?.[0] || {};
    const tutorialCustom = studentTutorial?.customTutorialStructure === true;
    const tutorialKeyTerms = Number(studentTutorial?.keyTermsPerMatchingPage);
    const studentProfile = studentTutorial?.studentId && typeof studentTutorial.studentId === 'object'
        ? studentTutorial.studentId
        : null;
    const studentCustom = studentProfile?.customTutorialStructure === true;
    const studentKeyTerms = Number(studentProfile?.keyTermsPerMatchingPage);
    const homeworkKeyTerms = Number(homework?.keyTermsPerMatchingPage);
    const keyTermsPerPage = tutorialCustom && [1, 2, 4].includes(tutorialKeyTerms)
        ? tutorialKeyTerms
        : (studentCustom && [1, 2, 4].includes(studentKeyTerms)
            ? studentKeyTerms
            : ([1, 2, 4].includes(homeworkKeyTerms) ? homeworkKeyTerms : null));
    const glossaryPanels = buildPreviewGlossaryPanels(firstChunk.glossary || [], keyTermsPerPage);
    const steps = ['SLIDESHOW', 'EXPLAINER'];

    const chunks = Array.isArray(studentTutorial?.generatedContent) ? studentTutorial.generatedContent : [];
    const hasSequencing = chunks.some((chunk) => (
        Array.isArray(chunk?.sequencing?.steps) && chunk.sequencing.steps.length > 0
    ));
    const hasCategorisation = chunks.some((chunk) => (
        (Array.isArray(chunk?.categorisation?.itemsA) && chunk.categorisation.itemsA.length > 0)
        || (Array.isArray(chunk?.categorisation?.itemsB) && chunk.categorisation.itemsB.length > 0)
    ));
    if (hasSequencing) steps.push('SEQUENCE_1');
    if (hasCategorisation) steps.push('CATEGORISE_1');

    if (glossaryPanels.length === 0) return [...steps, 'QUIZ_1', 'QUIZ_2'];

    if ([1, 2].includes(Number(keyTermsPerPage))) {
        let quizBlock = 1;
        for (let panelIndex = 1; panelIndex <= glossaryPanels.length; panelIndex += 2) {
            steps.push(`GLOSSARY_${panelIndex}`);
            if (panelIndex + 1 <= glossaryPanels.length) steps.push(`GLOSSARY_${panelIndex + 1}`);
            steps.push(`QUIZ_${quizBlock}`);
            quizBlock += 1;
        }
        return steps;
    }

    for (let panelIndex = 1; panelIndex <= glossaryPanels.length; panelIndex += 1) {
        steps.push(`GLOSSARY_${panelIndex}`);
        if (panelIndex === 1) steps.push('QUIZ_1');
        if (panelIndex === 2) steps.push('QUIZ_2');
    }

    if (!steps.includes('QUIZ_1')) steps.push('QUIZ_1');
    if (!steps.includes('QUIZ_2')) steps.push('QUIZ_2');
    return steps;
};

const filterPreviewStepsByStructure = (steps = [], config = {}) => {
    const filtered = steps.filter((step) => {
        const stepName = String(step || '');
        if (stepName === 'SLIDESHOW') return config.slideshow === true;
        if (stepName === 'EXPLAINER') return config.narrativeExplainer !== false;
        if (stepName.startsWith('SEQUENCE_')) return config.sequences === true;
        if (stepName.startsWith('CATEGORISE_')) return config.categorisation === true;
        if (stepName.startsWith('GLOSSARY_')) return config.keyTerms !== false;
        if (stepName.startsWith('QUIZ_')) return config.mcqs !== false;
        return true;
    });

    return filtered.length > 0 ? filtered : steps;
};

// --- HELPER: Model Name Formatter ---
const formatModelName = (modelId) => {
    if (!modelId) return 'Unknown';
    if (modelId.includes('gemini-2.5-flash-image')) return 'Gemini 2.5 Flash Image';
    if (modelId.includes('gemini-2.0-flash')) return 'Gemini 2.0 Flash';
    if (modelId.includes('gemini-2.5-flash')) return 'Gemini 2.5 Flash';
    if (modelId.includes('runware:111')) return 'Runware Flux (SRPO)'; // +++ CORRECT LABEL +++
    if (modelId.includes('runware:103')) return 'Flux.1 Dev (Runware)';
    if (modelId.includes('runware:101')) return 'Flux.1 Schnell (Runware)';
    if (modelId.includes('runware:100')) return 'SDXL 1.0 (Runware)';
    return modelId;
};


const normalizeLessonTag = (value = '') => value.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());

const sanitizeInlineText = (value = '') => String(value || '')
    .replace(/\*/g, '')
    .replace(/__/g, '')
    .replace(/`/g, '')
    .replace(/\s+/g, ' ')
    .trim();


const capitalizePhrase = (value = '') => {
    const cleaned = sanitizeInlineText(value);
    if (!cleaned) return '';

    return cleaned
        .split(' ')
        .filter(Boolean)
        .map(word => {
            if (word.length <= 1) return word.toUpperCase();
            if (/^[A-Z0-9-]+$/.test(word)) return word;
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
};

const formatTier3VocabularyEntry = (value = '') => {
    const cleaned = sanitizeInlineText(value);
    if (!cleaned) return '';

    const separatorMatch = cleaned.match(/^(.*?)(\s*[:\-–—]\s*)(.+)$/);
    if (!separatorMatch) {
        return capitalizePhrase(cleaned);
    }

    const [, rawTerm, separator, rawDefinition] = separatorMatch;
    const term = capitalizePhrase(rawTerm);
    const definition = sanitizeInlineText(rawDefinition);
    if (!definition) return `${term}${separator}`;

    return `${term}${separator}${definition.charAt(0).toUpperCase()}${definition.slice(1)}`;
};

// --- SUPPLY TEACHER: PROFILE HELPERS ---
const getProfileKey = (student) => {
    if (student.noCustomScaffoldingRequired === true) return '__mainstream__';
    const parts = [];
    if (student.readingAgeLabel) parts.push(`ra:${student.readingAgeLabel}`);
    const senConds = (Array.isArray(student.senConditions) && student.senConditions.length > 0)
        ? student.senConditions.filter(s => s && s !== 'None')
        : (student.senCondition && student.senCondition !== 'None' ? [student.senCondition] : []);
    if (senConds.length > 0) parts.push(`sen:${[...senConds].sort().join(',')}`);
    if (student.eal && student.primaryLanguage) parts.push(`eal:${student.primaryLanguage}`);
    return parts.length > 0 ? parts.join('|') : '__mainstream__';
};

const getStudentSenCondition = (student) => {
    if (Array.isArray(student.senConditions) && student.senConditions.length > 0)
        return student.senConditions.filter(s => s && s !== 'None').join(', ');
    return (student.senCondition && student.senCondition !== 'None') ? student.senCondition : '';
};

const getProfileLabel = (student) => {
    if (student.noCustomScaffoldingRequired === true) return 'Mainstream';
    const parts = [];
    if (student.readingAgeLabel) parts.push(`${student.readingAgeLabel} Reading Age`);
    const senConds = (Array.isArray(student.senConditions) && student.senConditions.length > 0)
        ? student.senConditions.filter(s => s && s !== 'None')
        : (student.senCondition && student.senCondition !== 'None' ? [student.senCondition] : []);
    senConds.forEach(c => parts.push(c));
    if (student.eal && student.primaryLanguage) parts.push(student.primaryLanguage);
    return parts.length > 0 ? parts.join(' + ') : 'Mainstream';
};

const parseFallbackObjectives = (learningObjectivesText = '') => {
    const regex = /(?:^|\n)\s*\d+\.\s*\*\*Learning Objective\*\*:\s*([\s\S]*?)\s*\*\*Misconception\*\*:\s*([\s\S]*?)(?=(?:\n\s*\d+\.\s*\*\*Learning Objective\*\*:\s*)|(?:\n\s*Tier\s*3\s+Vocabulary\s*:)|$)/gi;
    const parsed = [];
    let match;

    while ((match = regex.exec(learningObjectivesText)) !== null) {
        const objective = (match[1] || '').replace(/\s+/g, ' ').trim();
        const misconception = (match[2] || '').replace(/\s+/g, ' ').trim();
        if (objective || misconception) {
            parsed.push({ objective, misconception });
        }
    }

    return parsed.slice(0, 4);
};

const parseFallbackTier3Vocabulary = (learningObjectivesText = '') => {
    const sectionMatch = learningObjectivesText.match(/Tier\s*3\s+Vocabulary\s*:\s*([\s\S]*)$/i);
    if (!sectionMatch) return [];

    const terms = sectionMatch[1]
        .split('\n')
        .map(line => line.replace(/^\s*[-*]\s*/, '').trim())
        .filter(Boolean);

    return Array.from(new Set(terms)).slice(0, 8);
};

// --- HELPER: Render Markdown ---
const RenderMarkdown = ({ children, inline = false }) => (
    <ReactMarkdown 
        components={{ 
            p: inline ? 'span' : 'p',
            em: ({node, ...props}) => <strong style={{fontWeight:'800', color: BRAND.charcoal}} {...props} />,
            strong: ({node, ...props}) => <strong style={{fontWeight:'800', color: BRAND.charcoal}} {...props} />
        }}
    >
        {children}
    </ReactMarkdown>
);

// --- MODAL COMPONENT ---
const ModelSelectionModal = ({ isOpen, onClose, onConfirm, actionName, showImageOptions }) => {
    const [textModel, setTextModel] = useState(TEXT_MODEL); 
    const [imageModel, setImageModel] = useState(IMAGE_MODEL); 

    useEffect(() => {
        if (isOpen) {
            setTextModel(TEXT_MODEL);
            setImageModel(IMAGE_MODEL);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (showImageOptions) {
            onConfirm({ textModel, imageModel });
        } else {
            onConfirm({ textModel });
        }
    };

    const RadioOption = ({ label, value, price, desc, current, onChange, color = BRAND.teal }) => (
        <label className={`model-option ${current === value ? 'selected' : ''}`}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <input type="radio" name={showImageOptions && (value.includes('runware')) ? 'imageModel' : 'textModel'} value={value} checked={current === value} onChange={onChange} />
                <strong style={{marginLeft:'10px', color: BRAND.charcoal}}>{label}</strong>
                {!IS_DEMO && <span style={{marginLeft:'auto', fontSize:'0.85em', color: color, fontWeight:'bold'}}>{price}</span>}
            </div>
            <div style={{marginLeft:'26px', fontSize:'0.85em', color:'#555', marginTop:'4px'}}>{desc}</div>
        </label>
    );

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(45, 64, 66, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
            }}
        >
            <div className="dash-card" style={{ padding: '30px', width: '650px', maxWidth: '95%', background: 'white', maxHeight: '90vh', overflowY: 'auto' }}>
                <h3 style={{marginTop: 0, fontFamily:'Nunito, sans-serif', color: BRAND.charcoal}}>Select AI Models</h3>
                <p style={{marginBottom:'20px', color: '#5D7375'}}>Configure settings for: <strong>{actionName}</strong></p>
                
                <h4 style={{marginBottom:'10px', borderBottom:'2px solid #eee', paddingBottom:'5px', color: BRAND.charcoal}}>
                    {showImageOptions ? '1. Script/Text Model' : 'Text Generation Model'}
                </h4>
                
                <div style={{ marginBottom: '25px' }}>
                    <RadioOption label="Gemini 2.0 Flash" value="gemini-2.0-flash" price="$0.10 / 1M" desc="Best Value. Fast inference." current={textModel} onChange={(e) => setTextModel(e.target.value)} color={BRAND.success} />
                    <RadioOption label="Gemini 2.5 Flash" value="gemini-2.5-flash" price="$0.30 / 1M" desc="Balanced reasoning." current={textModel} onChange={(e) => setTextModel(e.target.value)} color={BRAND.warning} />
                </div>

                {showImageOptions && (
                    <>
                        <h4 style={{marginBottom:'10px', borderBottom:'2px solid #eee', paddingBottom:'5px', color: BRAND.charcoal}}>
                            2. Image Generation Model
                        </h4>
                        <div style={{ marginBottom: '20px', maxHeight: '300px', overflowY: 'auto', paddingRight: '5px' }}>
                            <RadioOption label="Gemini 2.5 Flash Image (Default)" value="gemini-2.5-flash-image" price="$0.0390 / img" desc="Google-native image generation default for demo flows." current={imageModel} onChange={(e) => setImageModel(e.target.value)} color={BRAND.warning} />
                            <RadioOption label="Runware FLUX.1 (SRPO)" value="runware:111@1" price="$0.0006 / img" desc="High quality Flux option at low cost." current={imageModel} onChange={(e) => setImageModel(e.target.value)} color={BRAND.success} />
                            <RadioOption label="Runware SDXL 1.0" value="runware:100@1" price="$0.0006 / img" desc="Standard base model." current={imageModel} onChange={(e) => setImageModel(e.target.value)} color={BRAND.success} />
                            <RadioOption label="Runware FLUX.1 [dev]" value="runware:101@1" price="$0.0038 / img" desc="Alternative Flux option." current={imageModel} onChange={(e) => setImageModel(e.target.value)} color={BRAND.warning} />
                        </div>
                    </>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <button onClick={onClose} className="btn-base" style={{background:'#F0F2F5', color: BRAND.charcoal}}>Cancel</button>
                    <button onClick={handleConfirm} className="btn-base btn-teal">
                        {showImageOptions ? 'Generate Slideshow' : 'Generate Content'}
                    </button>
                </div>
            </div>
        </div>
    );
};



const GlossaryDirectionModal = ({ isOpen, student, selection, onSelectionChange, onCancel, onConfirm }) => {
    if (!isOpen || !student) return null;

    const language = student.primaryLanguage || 'Primary Language';

    return (
        <div
            onClick={onCancel}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(45, 64, 66, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
            }}
        >
            <div className="dash-card" onClick={(e) => e.stopPropagation()} style={{ padding: '28px', width: '560px', maxWidth: '95%', background: 'white' }}>
                <h3 style={{ marginTop: 0, marginBottom: '8px', color: BRAND.charcoal }}>Choose Language Direction</h3>
                <p style={{ marginTop: 0, marginBottom: '18px', color: '#5D7375' }}>
                    Select how <strong>{student.name}</strong> should see tutorial content and glossary support.
                </p>

                <label className={`model-option ${selection === 'PRIMARY_TEXT_ENGLISH_GLOSSARY' ? 'selected' : ''}`} style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                            type="radio"
                            name="glossaryDirection"
                            value="PRIMARY_TEXT_ENGLISH_GLOSSARY"
                            checked={selection === 'PRIMARY_TEXT_ENGLISH_GLOSSARY'}
                            onChange={(e) => onSelectionChange(e.target.value)}
                        />
                        <strong style={{ marginLeft: '10px', color: BRAND.charcoal }}>{`${language} with English Glossary`}</strong>
                    </div>
                    <div style={{ marginLeft: '26px', fontSize: '0.85em', color: '#555', marginTop: '4px' }}>
                        Student reads in {language}; glossary/reference remains in English.
                    </div>
                </label>

                <label className={`model-option ${selection === 'ENGLISH_TEXT_PRIMARY_GLOSSARY' ? 'selected' : ''}`}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                            type="radio"
                            name="glossaryDirection"
                            value="ENGLISH_TEXT_PRIMARY_GLOSSARY"
                            checked={selection === 'ENGLISH_TEXT_PRIMARY_GLOSSARY'}
                            onChange={(e) => onSelectionChange(e.target.value)}
                        />
                        <strong style={{ marginLeft: '10px', color: BRAND.charcoal }}>{`English with ${language} Glossary`}</strong>
                    </div>
                    <div style={{ marginLeft: '26px', fontSize: '0.85em', color: '#555', marginTop: '4px' }}>
                        Student reads in English; glossary/reference uses {language}.
                    </div>
                </label>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                    <button onClick={onCancel} className="btn-base" style={{ background: '#F0F2F5', color: BRAND.charcoal }}>Cancel</button>
                    <button onClick={onConfirm} className="btn-base btn-teal">Continue</button>
                </div>
            </div>
        </div>
    );
};

const CostSummaryBox = ({ title, summary }) => {
    return (
        <div className="dash-card" style={{ flex: 1, minWidth: '250px', padding: '20px', background: 'white' }}>
            <h4 style={{marginTop:0, color: BRAND.charcoal, textTransform:'uppercase', fontSize:'0.85em', letterSpacing:'1px', fontWeight:'800'}}>{title}</h4>
            <p style={{ margin: '5px 0', fontSize: '1.2em' }}>
                <strong>Total: <span style={{color: BRAND.teal}}>£{summary.totalCost.toFixed(6)}</span></strong>
            </p>
            
            <div style={{ fontSize: '0.9em', color: '#5D7375', fontWeight:'500' }}>
                <p style={{ margin: '5px 0' }}>Input Tokens: {summary.totalInput.toLocaleString()}</p>
                <p style={{ margin: '5px 0' }}>Output Tokens: {summary.totalOutput.toLocaleString()}</p>
                {summary.totalImage > 0 && <p style={{ margin: '5px 0' }}>Images: {summary.totalImage.toLocaleString()}</p>}
                {summary.totalChar > 0 && <p style={{ margin: '5px 0' }}>Characters: {summary.totalChar.toLocaleString()}</p>}
            </div>
            
            {summary.models.length > 0 && (
                <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '2px solid #eee' }}>
                    <h5 style={{ marginBottom: '5px', marginTop:0, fontSize:'0.8em', color:'#999' }}>BREAKDOWN</h5>
                    {summary.models.map(modelItem => (
                        <div key={modelItem.model} style={{ marginBottom: '5px', fontSize: '0.85em', color: '#555', fontWeight:'600' }}>
                            {formatModelName(modelItem.model)} <span style={{color:'#999', fontWeight:'400'}}>({modelItem.count}x)</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const SlideshowCostSummaryBox = ({ script, image, audio }) => {
    const totalInput = script.totalInput + image.totalInput + audio.totalInput;
    const totalOutput = script.totalOutput + image.totalOutput + audio.totalOutput;
    const totalImage = image.totalImage;
    const totalChar = audio.totalChar;
    const totalCost = script.totalCost + image.totalCost + audio.totalCost;

    const allModels = [
        ...script.models.map(m => ({ ...m, type: 'Script' })),
        ...image.models.map(m => ({ ...m, type: 'Image' })),
        ...audio.models.map(m => ({ ...m, type: 'Audio' })),
    ];

    return (
        <div className="dash-card" style={{ flex: 1, minWidth: '250px', padding: '20px', background: 'white' }}>
            <h4 style={{marginTop:0, color: BRAND.charcoal, textTransform:'uppercase', fontSize:'0.85em', letterSpacing:'1px', fontWeight:'800'}}>Student Slideshows (Total)</h4>
            
            <p style={{ margin: '5px 0', fontSize: '1.2em' }}>
                <strong>Total: <span style={{color: BRAND.teal}}>£{totalCost.toFixed(6)}</span></strong>
            </p>

            <div style={{ fontSize: '0.9em', color: '#5D7375', fontWeight:'500', marginBottom:'15px' }}>
                <p style={{ margin: '5px 0' }}>Script: £{script.totalCost.toFixed(6)}</p>
                <p style={{ margin: '5px 0' }}>Image: £{image.totalCost.toFixed(6)}</p>
                <p style={{ margin: '5px 0' }}>Audio: £{audio.totalCost.toFixed(6)}</p>
            </div>

            <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '2px solid #eee' }}>
                <h5 style={{ marginBottom: '5px', marginTop:0, fontSize:'0.8em', color:'#999' }}>USAGE</h5>
                <div style={{ fontSize: '0.85em', color: '#555', fontWeight:'600' }}>
                    <p style={{margin:'2px 0'}}>Tokens: {totalInput.toLocaleString()} / {totalOutput.toLocaleString()}</p>
                    <p style={{margin:'2px 0'}}>Images: {totalImage.toLocaleString()}</p>
                    <p style={{margin:'2px 0'}}>Chars: {totalChar.toLocaleString()}</p>
                </div>
            </div>
            
            {allModels.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                    <h5 style={{ marginBottom: '5px', fontSize:'0.8em', color:'#999' }}>MODELS</h5>
                    {allModels.map(modelItem => (
                        <div key={`${modelItem.model}-${modelItem.type}`} style={{ fontSize: '0.85em', color: '#555', fontWeight:'600', marginBottom:'4px' }}>
                            {formatModelName(modelItem.model)} ({modelItem.type}) <span style={{color:'#999'}}>({modelItem.count}x)</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};



const getMcqList = (mcqData) => {
    if (!mcqData) return [];
    if (Array.isArray(mcqData)) return mcqData;
    return [
        ...(mcqData.easy || []),
        ...(mcqData.medium || []),
        ...(mcqData.hard || [])
    ];
};

export const TutorialReviewModal = ({
    isOpen,
    onClose,
    studentName,
    tutorial,
    slideshows = [],
    requireApproval = false,
    alreadyApproved = false,
    existingRejectionReason = '',
    existingFollowOnTask = '',
    onApprove,
    onReport
}) => {
    const scrollContainerRef = useRef(null);
    const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
    const [reportReason, setReportReason] = useState('');
    const [showReportReasonInput, setShowReportReasonInput] = useState(false);
    const [followOnTask, setFollowOnTask] = useState('');

    useEffect(() => {
        if (isOpen) {
            setHasScrolledToEnd(false);
            setReportReason(existingRejectionReason || '');
            setShowReportReasonInput(false);
            setFollowOnTask(existingFollowOnTask || '');
        }
    }, [isOpen, tutorial?._id]);

    if (!isOpen || !tutorial) return null;

    const resolveImageUrl = (imageUrl) => {
        if (!imageUrl) return '';
        return imageUrl.startsWith('http') ? imageUrl : `${ROOT_BASE_URL}/${String(imageUrl).replace(/^\/+/, '')}`;
    };

    const handleScroll = () => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const isAtEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
        if (isAtEnd) {
            setHasScrolledToEnd(true);
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(45, 64, 66, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
            }}
        >
            <div
                className="dash-card"
                onClick={(e) => e.stopPropagation()}
                style={{ padding: '24px', width: '900px', maxWidth: '95%', maxHeight: '90vh', background: 'white', display: 'flex', flexDirection: 'column' }}
            >
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
                    <h3 style={{margin:0, color: BRAND.charcoal}}>Review: {studentName}</h3>
                    <button onClick={onClose} className="btn-base" style={{background:'#F0F2F5', color: BRAND.charcoal}}>Close</button>
                </div>

                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    style={{ overflowY: 'auto', paddingRight: '8px', flex: 1 }}
                >
                    {(() => {
                        const translatedChunks = tutorial.generatedContent || [];
                        const englishChunks = tutorial.englishGeneratedContent || [];
                        const hasSideBySide = Array.isArray(englishChunks) && englishChunks.length === translatedChunks.length && englishChunks.length > 0;
                        const finalLanguage = tutorial.contentLanguage || 'English';
                        const leftChunkLabel = 'English review version';
                        const rightChunkLabel = finalLanguage.toLowerCase() === 'english'
                            ? 'Student version'
                            : `${finalLanguage} student version`;

                        const leftChunkForIndex = (idx) => englishChunks[idx] || translatedChunks[idx];
                        const rightChunkForIndex = (idx) => translatedChunks[idx] || englishChunks[idx];

                        const renderSlideshowSummary = (idx) => {
                            const chunkSlideshow = slideshows.find(ss => ss.chunkIndex === idx && ss.status === 'COMPLETED');
                            if (!chunkSlideshow || !Array.isArray(chunkSlideshow.slides) || chunkSlideshow.slides.length === 0) return null;

                            return (
                                <div style={{marginBottom:'12px'}}>
                                    <strong style={{color: BRAND.teal}}>Slideshow Summary</strong>
                                    <div style={{display:'grid', gridTemplateColumns:'repeat(4, minmax(150px, 1fr))', gap:'10px', marginTop:'8px'}}>
                                        {chunkSlideshow.slides.map((slide, slideIdx) => {
                                            const studentFacingCaption = String(slide.text || '').trim();
                                            const sourceCaption = String(slide.originalText || '').trim();
                                            let originalCaption = studentFacingCaption || sourceCaption || 'No caption available.';
                                            let translatedCaption = String(slide.translatedText || '').trim();

                                            if (!translatedCaption && sourceCaption && sourceCaption !== originalCaption) {
                                                translatedCaption = sourceCaption;
                                            }

                                            if (!translatedCaption && studentFacingCaption && studentFacingCaption !== originalCaption) {
                                                translatedCaption = studentFacingCaption;
                                            }

                                            const showBothCaptions = Boolean(
                                                translatedCaption
                                                && String(originalCaption).trim() !== String(translatedCaption).trim()
                                            );

                                            return (
                                                <div key={slideIdx} style={{background:'white', border:'1px solid #eee', borderRadius:'8px', padding:'8px'}}>
                                                    {slide.imageUrl ? (
                                                        <img
                                                            src={resolveImageUrl(slide.imageUrl)}
                                                            alt={`Slide ${slideIdx + 1}`}
                                                            style={{width:'100%', height:'auto', objectFit:'contain', borderRadius:'6px', border:'1px solid #ddd', background:'#fff'}}
                                                        />
                                                    ) : (
                                                        <div style={{width:'100%', minHeight:'100px', display:'flex', alignItems:'center', justifyContent:'center', background:'#f6f6f6', borderRadius:'6px', color:'#999', fontSize:'0.85em'}}>No image</div>
                                                    )}
                                                    <div style={{marginTop:'6px', fontSize:'0.8em', color:'#555', lineHeight:1.35}}>
                                                        {showBothCaptions ? (
                                                            <>
                                                                <div style={{fontSize:'0.75rem', fontWeight:'800', color:'#64748B', textTransform:'uppercase', letterSpacing:'0.5px'}}>Original Caption</div>
                                                                <RenderMarkdown inline>{originalCaption}</RenderMarkdown>
                                                                <div style={{fontSize:'0.75rem', fontWeight:'800', color:'#64748B', textTransform:'uppercase', letterSpacing:'0.5px', marginTop:'6px'}}>Translated Caption</div>
                                                                <RenderMarkdown inline>{translatedCaption}</RenderMarkdown>
                                                            </>
                                                        ) : (
                                                            <RenderMarkdown inline>{originalCaption}</RenderMarkdown>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        };

                        const renderChunkContent = (chunk, languageLabel) => {
                            const chunkMcqs = getMcqList(chunk?.mcqs);
                            const seq = chunk?.sequencing;
                            const cat = chunk?.categorisation;

                            return (
                                <div style={{background:'white', border:'1px solid #eee', borderRadius:'10px', padding:'12px'}}>
                                    <div style={{fontSize:'0.8rem', fontWeight:'800', color:'#64748B', textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:'8px'}}>{languageLabel}</div>

                                    <div style={{marginBottom:'12px'}}>
                                        <strong style={{color: BRAND.teal}}>Narrative Explainer (+ Fill in the Gaps)</strong>
                                        <div style={{background: BRAND.cream, padding:'12px', borderRadius:'8px', marginTop:'8px'}}>
                                            <RenderMarkdown>{chunk?.adjustedExplainer || chunk?.explainer || 'No explainer available.'}</RenderMarkdown>
                                        </div>
                                    </div>

                                    {chunk?.glossary && chunk.glossary.length > 0 && (
                                        <div style={{marginBottom:'12px'}}>
                                            <strong style={{color: BRAND.teal}}>Key Terminology</strong>
                                            <div style={{marginTop:'8px'}}>
                                                {chunk.glossary.map((item, i) => (
                                                    <div key={i} style={{background:'white', border:'1px solid #eee', padding:'10px', borderRadius:'8px', marginBottom:'8px'}}>
                                                        <div style={{fontWeight:'700'}}><RenderMarkdown inline>{item.term}</RenderMarkdown></div>
                                                        <div style={{color:'#555'}}><RenderMarkdown inline>{item.definition}</RenderMarkdown></div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{marginBottom:'12px'}}>
                                        <strong style={{color: BRAND.teal}}>Practice Questions (MCQ)</strong>
                                        <div style={{marginTop:'8px'}}>
                                            {chunkMcqs.length > 0 ? chunkMcqs.map((mcq, i) => (
                                                <div key={i} style={{background:'white', border:'1px solid #eee', padding:'10px', borderRadius:'8px', marginBottom:'8px'}}>
                                                    <div style={{fontWeight:'700'}}>Q: <RenderMarkdown inline>{mcq.question}</RenderMarkdown></div>
                                                    <ul style={{margin:'6px 0 0 16px'}}>
                                                        {mcq.options && typeof mcq.options === 'object' ? Object.entries(mcq.options).map(([key, val]) => (
                                                            <li key={key} style={{color: key === mcq.correctAnswer ? BRAND.success : BRAND.charcoal, fontWeight: key === mcq.correctAnswer ? '700' : '400'}}>{key}: <RenderMarkdown inline>{val}</RenderMarkdown></li>
                                                        )) : <li>No options available</li>}
                                                    </ul>
                                                </div>
                                            )) : <p style={{color:'#666', margin:'8px 0'}}>No structured MCQs available.</p>}
                                        </div>
                                    </div>

                                    {seq?.steps?.length > 0 && (
                                        <div style={{marginBottom:'12px'}}>
                                            <strong style={{color:'#7C3AED'}}>Sequencing</strong>
                                            <div style={{fontSize:'0.82em', color:'#5D7375', fontStyle:'italic', margin:'4px 0 6px 0'}}>{seq.title || 'Put the steps in the correct order'}</div>
                                            <div style={{marginTop:'4px'}}>
                                                {seq.steps.map((step, i) => (
                                                    <div key={i} style={{display:'flex', alignItems:'center', gap:'8px', background:'#F5F3FF', border:'1px solid #DDD6FE', padding:'7px 10px', borderRadius:'8px', marginBottom:'5px', fontSize:'0.88em'}}>
                                                        <span style={{background:'#7C3AED', color:'white', fontWeight:'800', fontSize:'0.78em', minWidth:'20px', height:'20px', borderRadius:'999px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>{i + 1}</span>
                                                        <RenderMarkdown inline>{step}</RenderMarkdown>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {cat?.itemsA?.length > 0 && (
                                        <div style={{marginBottom:'12px'}}>
                                            <strong style={{color:'#16A34A'}}>Categorisation</strong>
                                            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'8px'}}>
                                                <div style={{background:'#F0FDF4', border:'1.5px solid #4ADE80', borderRadius:'8px', padding:'8px 10px'}}>
                                                    <div style={{fontWeight:'800', fontSize:'0.85em', color:'#16A34A', marginBottom:'6px'}}>{cat.categoryA}</div>
                                                    {(cat.itemsA || []).map((item, i) => (
                                                        <div key={i} style={{fontSize:'0.85em', padding:'3px 0', color: BRAND.charcoal}}><RenderMarkdown inline>{item}</RenderMarkdown></div>
                                                    ))}
                                                </div>
                                                <div style={{background:'#FFFBEB', border:'1.5px solid #F2CC8F', borderRadius:'8px', padding:'8px 10px'}}>
                                                    <div style={{fontWeight:'800', fontSize:'0.85em', color:'#92400E', marginBottom:'6px'}}>{cat.categoryB}</div>
                                                    {(cat.itemsB || []).map((item, i) => (
                                                        <div key={i} style={{fontSize:'0.85em', padding:'3px 0', color: BRAND.charcoal}}><RenderMarkdown inline>{item}</RenderMarkdown></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            );
                        };

                        return translatedChunks.map((chunk, idx) => (
                            <div key={idx} style={{marginBottom:'20px', padding:'15px', border:'2px solid #E0E0E0', borderRadius:'10px', background:'#FCFCFC'}}>
                                <h4 style={{marginTop:0, color: BRAND.charcoal}}>Chunk {idx + 1}</h4>

                                {renderSlideshowSummary(idx)}

                                {hasSideBySide ? (
                                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
                                        {renderChunkContent(leftChunkForIndex(idx), leftChunkLabel)}
                                        {renderChunkContent(rightChunkForIndex(idx), rightChunkLabel)}
                                    </div>
                                ) : (
                                    renderChunkContent(chunk, 'Student version')
                                )}
                            </div>
                        ));
                    })()}
                </div>

                {requireApproval && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid #E5E7EB', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ fontSize: '0.9em', color: '#555' }}>
                            {alreadyApproved
                                ? 'Approved. You can now send this tutorial.'
                                : (hasScrolledToEnd
                                    ? 'You have reached the end. Click APPROVE to enable sending, or REPORT to reject this content.'
                                    : 'Scroll to the end of the content to enable APPROVE or REPORT.')}
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontFamily: 'Nunito', fontWeight: '800', color: BRAND.charcoal, marginBottom: '6px' }}>
                                {showReportReasonInput ? 'Report reason' : 'Add a follow-on task'}
                            </label>
                            <textarea
                                value={showReportReasonInput ? reportReason : followOnTask}
                                onChange={(e) => {
                                    if (showReportReasonInput) {
                                        setReportReason(e.target.value);
                                    } else {
                                        setFollowOnTask(e.target.value);
                                    }
                                }}
                                placeholder={showReportReasonInput
                                    ? 'Explain why this content should be rejected...'
                                    : 'Optional: add a task for the student to complete after finishing this tutorial...'}
                                rows={3}
                                style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: '8px', padding: '10px', fontFamily: 'inherit' }}
                            />
                            {showReportReasonInput && (
                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                    Rejection reason is required to submit report.
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            {!showReportReasonInput ? (
                                <button
                                    className="btn-base"
                                    onClick={() => { setReportReason(followOnTask || existingRejectionReason || ''); setShowReportReasonInput(true); }}
                                    disabled={alreadyApproved || !hasScrolledToEnd}
                                    style={{
                                        background: alreadyApproved || !hasScrolledToEnd ? '#FBC6C5' : BRAND.coral,
                                        color: 'white',
                                        cursor: alreadyApproved || !hasScrolledToEnd ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    REPORT
                                </button>
                            ) : (
                                <>
                                    <button
                                        className="btn-base"
                                        onClick={() => {
                                            setShowReportReasonInput(false);
                                            setReportReason(existingRejectionReason || '');
                                        }}
                                        style={{ background: '#F0F2F5', color: BRAND.charcoal }}
                                    >
                                        Cancel Report
                                    </button>
                                    <button
                                        className="btn-base"
                                        onClick={() => {
                                            const replacementReason = reportReason.trim();
                                            setFollowOnTask(replacementReason);
                                            onReport && onReport(replacementReason);
                                        }}
                                        disabled={alreadyApproved || !hasScrolledToEnd || !reportReason.trim()}
                                        style={{
                                            background: alreadyApproved || !hasScrolledToEnd || !reportReason.trim() ? '#FBC6C5' : BRAND.coral,
                                            color: 'white',
                                            cursor: alreadyApproved || !hasScrolledToEnd || !reportReason.trim() ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        Submit Report
                                    </button>
                                </>
                            )}
                            <button
                                className="btn-base"
                                onClick={() => onApprove && onApprove(followOnTask)}
                                disabled={alreadyApproved || !hasScrolledToEnd}
                                style={{
                                    background: alreadyApproved || !hasScrolledToEnd ? '#D1D5DB' : BRAND.success,
                                    color: 'white',
                                    cursor: alreadyApproved || !hasScrolledToEnd ? 'not-allowed' : 'pointer'
                                }}
                            >
                                APPROVE
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};



const buildEnglishAnswerNormalizer = (tutorial = null) => {
    if (!tutorial?.translationApplied) {
        return (answer = {}) => answer;
    }

    const lookup = new Map();
    const translatedChunks = tutorial.generatedContent || [];
    const englishChunks = tutorial.englishGeneratedContent || [];

    translatedChunks.forEach((translatedChunk, chunkIndex) => {
        const englishChunk = englishChunks[chunkIndex];
        if (!translatedChunk || !englishChunk) return;

        (translatedChunk.glossary || []).forEach((translatedItem, glossaryIndex) => {
            const englishItem = (englishChunk.glossary || [])[glossaryIndex];
            if (!translatedItem?.term || !englishItem?.term) return;
            lookup.set(`match:${translatedItem.term.toLowerCase()}`, {
                questionText: `Match: ${englishItem.term}`,
                correctAnswer: englishItem.definition || ''
            });
        });

        ['easy', 'medium', 'hard'].forEach((difficulty) => {
            const translatedMcqs = translatedChunk?.mcqs?.[difficulty] || [];
            const englishMcqs = englishChunk?.mcqs?.[difficulty] || [];

            translatedMcqs.forEach((translatedMcq, questionIndex) => {
                const englishMcq = englishMcqs[questionIndex];
                if (!translatedMcq?.question || !englishMcq?.question) return;

                const optionLookup = new Map();
                ['A', 'B', 'C', 'D'].forEach((key) => {
                    const translatedOption = translatedMcq?.options?.[key];
                    const englishOption = englishMcq?.options?.[key];
                    if (translatedOption && englishOption) {
                        optionLookup.set(String(translatedOption), String(englishOption));
                    }
                });

                lookup.set(`mcq:${translatedMcq.question.toLowerCase()}`, {
                    questionText: englishMcq.question,
                    correctAnswer: englishMcq?.options?.[englishMcq.correctAnswer] || englishMcq.correctAnswer,
                    studentAnswerLookup: optionLookup
                });
            });
        });
    });

    return (answer = {}) => {
        const safeQuestion = String(answer.questionText || '');
        if (safeQuestion.startsWith('Match:')) {
            const translatedTerm = safeQuestion.replace('Match:', '').trim().toLowerCase();
            const glossaryLookup = lookup.get(`match:${translatedTerm}`);
            if (!glossaryLookup) return answer;
            return {
                ...answer,
                questionText: glossaryLookup.questionText,
                correctAnswer: glossaryLookup.correctAnswer || answer.correctAnswer
            };
        }

        const mcqLookup = lookup.get(`mcq:${safeQuestion.toLowerCase()}`);
        if (!mcqLookup) return answer;

        return {
            ...answer,
            questionText: mcqLookup.questionText,
            studentAnswer: mcqLookup.studentAnswerLookup?.get(String(answer.studentAnswer || '')) || answer.studentAnswer,
            correctAnswer: mcqLookup.correctAnswer || answer.correctAnswer
        };
    };
};

const buildAttemptReportFallback = (answers = []) => {
    const safeAnswers = Array.isArray(answers) ? answers : [];
    const glossaryAnswers = safeAnswers.filter(a => a.questionText && a.questionText.startsWith('Match:'));
    const contentAnswers = safeAnswers.filter(a => !a.questionText || !a.questionText.startsWith('Match:'));

    const totalContent = contentAnswers.length;
    const correctContent = contentAnswers.filter(a => a.isCorrect).length;
    const substantiveRate = totalContent > 0 ? Math.round((correctContent / totalContent) * 100) : 0;

    const totalGlossary = glossaryAnswers.length;
    const firstTryGlossary = glossaryAnswers.filter(a => (a.studentAnswer || '').includes('Matched in 1 try')).length;
    const keyTermsRate = totalGlossary > 0 ? Math.round((firstTryGlossary / totalGlossary) * 100) : 0;

    const goodCandidates = safeAnswers.filter(a => a.questionText?.startsWith('Match:') ? (a.studentAnswer || '').includes('Matched in 1 try') : a.isCorrect === true);
    const reviewCandidates = safeAnswers.filter(a => a.questionText?.startsWith('Match:') ? !(a.studentAnswer || '').includes('Matched in 1 try') : a.isCorrect === false);

    return {
        totalAnswers: safeAnswers.length,
        substantiveRate,
        keyTermsRate,
        goodItem: goodCandidates[0] || null,
        reviewItem: reviewCandidates[0] || null
    };
};

const AttemptsReviewModal = ({ isOpen, onClose, studentName, attempts = [], tutorial = null, lessonNumber, homeworkNumber, homeworkId = '', isContentLibrary = false }) => {
    const [selectedAttempt, setSelectedAttempt] = useState(null);
    const getAttemptMetrics = (attempt) => {
        const answers = attempt?.answers || [];
        const mcq = answers.filter(a => a.questionText && !a.questionText.startsWith('Match:'));
        const glossary = answers.filter(a => a.questionText && a.questionText.startsWith('Match:'));
        const correctMcq = mcq.filter(a => a.isCorrect).length;
        const substantive = mcq.length > 0 ? Math.round((correctMcq / mcq.length) * 100) : 0;
        const firstTry = glossary.filter(a => (a.studentAnswer || '').includes('Matched in 1 try')).length;
        const keyTerms = glossary.length > 0 ? Math.round((firstTry / glossary.length) * 100) : 0;
        const averageTime = answers.length > 0
            ? Math.round(answers.reduce((sum, a) => sum + (Number(a.timeTaken) || 0), 0) / answers.length)
            : 0;
        return { substantive, keyTerms, averageTime };
    };
    useEffect(() => {
        if (!isOpen) {
            setSelectedAttempt(null);
        }
    }, [isOpen]);

    const normalizeAnswerToEnglish = useMemo(() => buildEnglishAnswerNormalizer(tutorial), [tutorial]);
    const normalizedAttempts = useMemo(
        () => (attempts || []).map((attempt) => ({
            ...attempt,
            answers: (attempt?.answers || []).map((answer) => normalizeAnswerToEnglish(answer))
        })),
        [attempts, normalizeAnswerToEnglish]
    );

    if (!isOpen) return null;

    const selectedAnswers = selectedAttempt?.answers || [];
    const mcqAnswers = selectedAnswers.filter(a => a.questionText && !a.questionText.startsWith('Match:'));
    const glossaryAnswers = selectedAnswers.filter(a => a.questionText && a.questionText.startsWith('Match:'));

    const correctMcq = mcqAnswers.filter(a => a.isCorrect).length;
    const substantiveRate = mcqAnswers.length > 0 ? Math.round((correctMcq / mcqAnswers.length) * 100) : 0;
    const firstTryGlossary = glossaryAnswers.filter(a => (a.studentAnswer || '').includes('Matched in 1 try')).length;
    const keyTermsRate = glossaryAnswers.length > 0 ? Math.round((firstTryGlossary / glossaryAnswers.length) * 100) : 0;
    const averageTimeTaken = selectedAnswers.length > 0
        ? Math.round(selectedAnswers.reduce((sum, a) => sum + (Number(a.timeTaken) || 0), 0) / selectedAnswers.length)
        : 0;

    const performanceData = [{ rate: 0, difficulty: 'start' }];
    let correctSoFar = 0;
    mcqAnswers.forEach((ans, index) => {
        if (ans.isCorrect) correctSoFar++;
        const rate = Math.round((correctSoFar / (index + 1)) * 100);
        performanceData.push({ rate, difficulty: ans.difficulty || 'medium' });
    });

    const mcqTimeData = mcqAnswers.map((a, i) => ({
        label: `Q${i + 1}`,
        value: typeof a.timeTaken === 'number' ? a.timeTaken : Number(a.timeTaken)
    }));

    const glossaryTimeData = glossaryAnswers.map((a, i) => {
        const rawText = (a.questionText || '').replace('Match: ', '').replace(/\*/g, '');
        const label = rawText.length > 18 ? `${rawText.slice(0, 18)}...` : rawText;
        return {
            label: `T${i + 1}: ${label}`,
            value: typeof a.timeTaken === 'number' ? a.timeTaken : Number(a.timeTaken)
        };
    });

    return (
        <div onClick={onClose} style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(45, 64, 66, 0.6)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000 }}>
            <div className="dash-card" onClick={(e)=>e.stopPropagation()} style={{ padding:'24px', width:'760px', maxWidth:'95%', maxHeight:'90vh', overflowY:'auto', background:'white' }}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
                    <h3 style={{margin:0, color:BRAND.charcoal}}>Attempts: {studentName}</h3>
                    <button onClick={onClose} className="btn-base" style={{background:'#F0F2F5', color: BRAND.charcoal}}>Close</button>
                </div>

                {normalizedAttempts.length === 0 ? (
                    <p style={{color:'#777', fontStyle:'italic'}}>No attempts recorded yet.</p>
                ) : normalizedAttempts.map((attempt, idx) => {
                    const metrics = getAttemptMetrics(attempt);
                    return (
                    <div key={attempt._id || idx} style={{border:'1px solid #E0E0E0', borderRadius:'8px', padding:'12px', marginBottom:'10px', background:'#FCFCFC'}}>
                        <div style={{display:'flex', justifyContent:'space-between', gap:'8px', flexWrap:'wrap', marginBottom:'8px'}}>
                            <strong>Attempt {attempts.length - idx}</strong>
                            <span style={{fontSize:'0.85em', color:'#666'}}>{new Date(attempt.startedAt).toLocaleString()}</span>
                        </div>
                        <div style={{fontSize:'0.9em', color:'#555', marginBottom:'6px'}}>
                            Status: <strong>{attempt.status}</strong> • Substantive Accuracy: <strong>{metrics.substantive}%</strong> • Key Terms Mastery: <strong>{metrics.keyTerms}%</strong> • Avg Time: <strong>{metrics.averageTime}s</strong>
                        </div>
                        {attempt.answers && attempt.answers.length > 0 && (
                            <button className="btn-base" onClick={() => setSelectedAttempt({ ...attempt, _attemptNumber: attempts.length - idx })} style={{background: BRAND.teal, color:'white', marginTop:'6px'}}>
                                View Report
                            </button>
                        )}
                    </div>
                    );
                })}

            </div>

            {selectedAttempt && (
                <div onClick={() => setSelectedAttempt(null)} style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(45, 64, 66, 0.55)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1100 }}>
                    <div className="dash-card" onClick={(e)=>e.stopPropagation()} style={{ padding:'24px', width:'1100px', maxWidth:'95%', maxHeight:'90vh', overflowY:'auto', background:'white' }} id="attempt-report-printable">
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px'}}>
                            <div>
                                <h4 style={{margin:0, color: BRAND.charcoal}}>
                                    {lessonNumber != null ? `Lesson ${lessonNumber}: ` : ''}
                                    {homeworkNumber != null ? `Tutorial ${homeworkNumber} — ` : ''}
                                    Attempt {selectedAttempt._attemptNumber}
                                </h4>
                                <p style={{margin:'4px 0 0 0', fontWeight:'700', color: BRAND.teal, fontSize:'1em'}}>{studentName}</p>
                            </div>
                            <div style={{display:'flex', gap:'8px'}} className="no-print">
                                <button className="btn-base" onClick={() => {
                                    const el = document.getElementById('attempt-report-printable');
                                    if (!el) return;
                                    const clone = el.cloneNode(true);
                                    clone.id = 'print-clone';
                                    clone.style.cssText = 'position:static;background:white;padding:24px;';
                                    clone.querySelectorAll('.no-print').forEach(n => n.remove());
                                    document.body.appendChild(clone);
                                    document.body.classList.add('printing-report');
                                    window.print();
                                    document.body.classList.remove('printing-report');
                                    document.body.removeChild(clone);
                                    activityAPI.logEvent('ACTION', 'PRINT_REPORT', {
                                        action: 'PRINT_REPORT',
                                        homeworkId,
                                        isContentLibrary,
                                        route: window.location.pathname,
                                    }).catch(e => console.warn('Failed to log PRINT_REPORT event:', e));
                                }} style={{background: BRAND.teal, color:'white'}}>🖨 Print</button>
                                <button className="btn-base" onClick={() => setSelectedAttempt(null)} style={{background:'#F0F2F5', color: BRAND.charcoal}}>Close</button>
                            </div>
                        </div>
                        <p style={{margin:'8px 0 10px 0', color:'#555'}}>
                            Substantive Accuracy: <strong>{substantiveRate}%</strong> • Key Terms Mastery: <strong>{keyTermsRate}%</strong> • Average Time Taken: <strong>{averageTimeTaken}s</strong>
                        </p>

                        <PerformanceTrackerChart data={performanceData} />
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(420px, 1fr))', gap:'20px'}}>
                            <SimpleBarChart data={mcqTimeData} title="Time Taken: MCQs (seconds)" color={BRAND.teal} yLabel="Seconds" />
                            <SimpleBarChart data={glossaryTimeData} title="Time Taken: Key Terms (seconds)" color={BRAND.coral} yLabel="Seconds" />
                        </div>

                        <h3 style={{ color: BRAND.charcoal, fontFamily: 'Nunito', fontWeight: '800', marginTop: '25px', marginBottom: '15px' }}>Question-Level Breakdown</h3>
                        {mcqAnswers.length === 0 ? (
                            <div style={{marginTop:'6px', color:'#777', fontSize:'0.9em'}}>No MCQ answer data recorded for this attempt.</div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', marginBottom:'10px' }}>
                                {mcqAnswers.map((ans, i) => (
                                    <div key={`qb-${i}`} style={{
                                        padding: '20px',
                                        borderRadius: '12px',
                                        border: `2px solid ${BRAND.charcoal}`,
                                        borderLeft: `8px solid ${ans.isCorrect ? BRAND.teal : BRAND.coral}`,
                                        background: '#fff',
                                        boxShadow: '4px 4px 0px rgba(0,0,0,0.05)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between'
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: '800', marginBottom: '10px', color: BRAND.charcoal, fontFamily: 'Nunito' }}>
                                                Q{i + 1}: <span style={{fontWeight:'600', fontFamily:'Inter'}}><RenderMarkdown inline>{ans.questionText}</RenderMarkdown></span>
                                            </div>
                                            <div style={{ fontSize: '0.95em', color: ans.isCorrect ? BRAND.teal : BRAND.coral, marginBottom: '5px', fontWeight: 'bold' }}>
                                                <span style={{color: '#888', fontWeight: 'normal'}}>Student:</span> <RenderMarkdown inline>{ans.studentAnswer}</RenderMarkdown>
                                            </div>
                                            {!ans.isCorrect && (
                                                <div style={{ fontSize: '0.95em', color: BRAND.charcoal }}>
                                                    <span style={{color: '#888'}}>Correct:</span> <strong><RenderMarkdown inline>{ans.correctAnswer}</RenderMarkdown></strong>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ marginTop: '15px', fontSize: '0.85em', color: '#888', textAlign: 'right', fontWeight: '600' }}>
                                            ⏱ {Number(ans.timeTaken) || 0}s
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const renderInlineMarkdown = (text = '') => {
    const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
            ? <strong key={i}>{part.slice(2, -2)}</strong>
            : part
    );
};

const WorksheetModal = ({ isOpen, onClose, inline = false, profileLabel, homeworkTitle, worksheetTitle, lessonNumber, homeworkNumber, shapeIndex, senCondition, studentTutorial, worksheetComponents = [], fallbackSequencing = null, fallbackCategorisation = null, homeworkId = '', isContentLibrary = false }) => {
    // William Ford worksheets should always use Open-Dyslexic formatting, regardless of individual SEN flags.
    const isDyslexic = USE_OPEN_DYSLEXIC_WORKSHEET_FONT || (senCondition || '').toLowerCase().includes('dyslexia');
    const studentId = getStudentTutorialStudentId(studentTutorial);
    const [worksheetFontSize, setWorksheetFontSize] = useState(DEFAULT_WORKSHEET_FONT_SIZE);
    const printContentRef = useRef(null);

    useEffect(() => {
        setWorksheetFontSize(readWorksheetFontSize(homeworkId, studentId));
    }, [homeworkId, studentId]);

    const updateWorksheetFontSize = (nextSize) => {
        const parsedSize = Number(nextSize) || DEFAULT_WORKSHEET_FONT_SIZE;
        setWorksheetFontSize(parsedSize);
        writeWorksheetFontSize(homeworkId, studentId, parsedSize);
    };

    useEffect(() => {
        if (!isDyslexic) return;
        const id = 'opendyslexic-font';
        if (document.getElementById(id)) return;
        const style = document.createElement('style');
        style.id = id;
        style.textContent = `@font-face { font-family: 'Open-Dyslexic'; src: url('https://fonts.cdnfonts.com/s/29616/open-dyslexic.woff') format('woff'); font-weight: normal; font-style: normal; }`;
        document.head.appendChild(style);
    }, [isDyslexic]);
    const isEal = studentTutorial?.contentLanguage && studentTutorial.contentLanguage !== 'English';
    const englishChunks = isEal && Array.isArray(studentTutorial?.englishGeneratedContent) && studentTutorial.englishGeneratedContent.length > 0
        ? studentTutorial.englishGeneratedContent
        : (studentTutorial?.generatedContent || []);
    const translatedChunks = isEal ? (studentTutorial?.generatedContent || []) : [];

    const allGlossary = englishChunks.flatMap(c => c.glossary || []).filter(g => g.term && g.definition).slice(0, 10);
    const allGlossaryTranslated = translatedChunks.flatMap(c => c.glossary || []).filter(g => g.term && g.definition).slice(0, 10);

    const allMcqs = englishChunks.flatMap(c => [
        ...(c?.mcqs?.easy || []),
        ...(c?.mcqs?.medium || []),
    ]).filter(q => q.question && q.options).slice(0, 8);
    const allMcqsTranslated = translatedChunks.flatMap(c => [
        ...(c?.mcqs?.easy || []),
        ...(c?.mcqs?.medium || []),
    ]).filter(q => q.question && q.options).slice(0, 8);

    // Fill-in-gaps: pick first chunk where glossary terms appear in the explainer
    const fillInGapsData = React.useMemo(() => {
        for (const c of englishChunks) {
            const text = c.adjustedExplainer || c.explainer || '';
            const glossary = c.glossary || [];
            if (!text || !glossary.length) continue;
            const plain = text.replace(/\*\*/g, '');
            const found = glossary.filter(({ term }) => {
                if (!term) return false;
                const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                return new RegExp(`(?<![\\w])${escaped}(?![\\w])`, 'i').test(plain);
            }).map(g => g.term);
            if (found.length >= 2) return { text, terms: found };
        }
        return null;
    }, [englishChunks]);

    // Sequences: collect all chunks with sequencing data
    const allSequencing = englishChunks.flatMap(c =>
        c.sequencing?.steps?.length > 0 ? [{ title: c.sequencing.title || 'Put the steps in the correct order', steps: c.sequencing.steps }] : []
    );

    // Categorisation: collect all chunks with categorisation data
    const allCategorisation = englishChunks.flatMap(c =>
        c.categorisation?.itemsA?.length > 0 ? [c.categorisation] : []
    );

    // For OA homework, tutorial generatedContent lacks seq/cat — use fallback from lesson analysis
    const effectiveSequencing = allSequencing.length > 0 ? allSequencing : (
        fallbackSequencing?.steps?.length > 0
            ? [{ title: fallbackSequencing.title || 'Put the steps in the correct order', steps: fallbackSequencing.steps }]
            : []
    );
    const effectiveCategorisation = allCategorisation.length > 0 ? allCategorisation : (
        (fallbackCategorisation && !fallbackCategorisation.isNA && fallbackCategorisation.itemsA?.length > 0)
            ? [fallbackCategorisation]
            : []
    );

    // Fixed seed — all worksheets use the same shuffle so one answer key covers all profiles
    const WS_SEED = 7919;
    const lcgShuffle = (arr, seed) => {
        const a = [...arr]; let s = seed;
        for (let i = a.length - 1; i > 0; i--) {
            s = (s * 1664525 + 1013904223) & 0xffffffff;
            const j = Math.abs(s) % (i + 1);
            [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
    };

    const shuffledDefs = React.useMemo(() => {
        return lcgShuffle(allGlossary.map((item, i) => ({ ...item, _origIdx: i })), WS_SEED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allGlossary.length]);

    // Seeded shuffle for sequence steps
    const shuffledSequenceSteps = React.useMemo(() => {
        if (!effectiveSequencing[0]?.steps) return [];
        return lcgShuffle(effectiveSequencing[0].steps.map((s, i) => ({ text: s, origIdx: i })), WS_SEED + 7);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveSequencing.length]);

    // Seeded shuffle for categorisation items
    const shuffledCatItems = React.useMemo(() => {
        const cat = effectiveCategorisation[0];
        if (!cat) return [];
        return lcgShuffle([
            ...(cat.itemsA || []).map(t => ({ text: t, cat: 'A' })),
            ...(cat.itemsB || []).map(t => ({ text: t, cat: 'B' })),
        ], WS_SEED + 13);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveCategorisation.length]);

    // Pre-process fill-in-gaps (seeded word bank shuffle) so answer key can access it
    const fillInGapsProcessed = React.useMemo(() => {
        if (!fillInGapsData) return null;
        let display = fillInGapsData.text.replace(/\*\*/g, '');
        const blankedTerms = [];
        fillInGapsData.terms.forEach(term => {
            const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            if (new RegExp(`(?<![\\w])(${escaped})(?![\\w])`, 'gi').test(display)) {
                blankedTerms.push(term);
                display = display.replace(new RegExp(`(?<![\\w])(${escaped})(?![\\w])`, 'gi'), '▢▢▢');
            }
        });
        return { display, blankedTerms, wordBank: lcgShuffle([...blankedTerms], WS_SEED + 3) };
    }, [fillInGapsData]);

    // MCQ: rotate options so correct answer sits at a fixed letter per question index
    // Q0→A, Q1→B, Q2→C, Q3→D, Q4→A, ... — universal answer key: A B C D A B C D
    const reorderMcqOptions = (q, qIdx) => {
        const correct = q.answer || q.correctAnswer;
        const target = String.fromCharCode(65 + (qIdx % 4));
        if (!correct || !q.options?.[correct] || correct === target) return q.options;
        const opts = { ...q.options };
        [opts[target], opts[correct]] = [opts[correct], opts[target]];
        return opts;
    };

    // Build ordered, enabled sections from worksheetComponents
    const enabledComponents = worksheetComponents.length > 0
        ? worksheetComponents.filter(c => c.enabled)
        : [{ id: 'key_terms' }, { id: 'mcq' }]; // fallback to original two

    const handlePrint = () => {
        const content = printContentRef.current;
        if (!content) return;
        const win = window.open('', '_blank');
        if (!win?.document) return;
        win.document.write(`<!DOCTYPE html><html><head><title>Worksheet</title>
        <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800&family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
            ${isDyslexic ? `@font-face { font-family: 'Open-Dyslexic'; src: url('https://fonts.cdnfonts.com/s/29616/open-dyslexic.woff') format('woff'); font-weight: normal; font-style: normal; }` : ''}
            * { box-sizing: border-box; }
            body { font-family: ${isDyslexic ? "'Open-Dyslexic'" : "'Inter'"}, sans-serif; color: #2D4042; margin: 0; padding: 16mm 18mm; background: white; font-size: ${worksheetFontSize}px; line-height: ${isDyslexic ? '1.9' : '1.5'}; letter-spacing: ${isDyslexic ? '0.05em' : 'normal'}; }
            .ws-header { background: #F0FDFA; border: 2px solid #75B9BE; border-radius: 12px; padding: 14px 18px; margin-bottom: 18px; }
            .ws-subtitle { font-size: 0.75em; color: #5D7375; font-weight: 600; margin-bottom: 4px; letter-spacing: 0.5px; text-transform: uppercase; }
            .ws-title { font-family: 'Nunito', sans-serif; font-size: 1.35em; font-weight: 800; color: #2D4042; margin: 0 0 10px 0; }
            .ws-fields { display: flex; gap: 32px; font-size: 0.82em; font-weight: 600; color: #2D4042; }
            .ws-field-label { color: #75B9BE; margin-right: 4px; }
            .ws-field-line { border-bottom: 2px solid #2D4042; display: inline-block; width: 130px; }
            .section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
            .section-badge { background: #75B9BE; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.75em; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
            .section-title { font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 1em; color: #2D4042; }
            .section-hint { font-size: 0.75em; color: #5D7375; font-style: italic; margin: 0 0 10px 0; }
            .match-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 20px; }
            .term-row { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; padding: 5px 8px; background: #F9FAFB; border: 1.5px solid #E5E7EB; border-radius: 8px; font-size: 0.88em; }
            .term-num { background: #2D4042; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.8em; min-width: 20px; height: 20px; border-radius: 999px; display: flex; align-items: center; justify-content: center; }
            .term-blank { border: 1.5px solid #75B9BE; border-radius: 6px; width: 28px; height: 22px; display: inline-block; background: white; }
            .term-name { font-weight: 700; color: #2D4042; }
            .def-row { display: flex; align-items: flex-start; gap: 7px; margin-bottom: 8px; font-size: 0.88em; }
            .def-letter { background: #75B9BE; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.8em; min-width: 22px; height: 22px; border-radius: 999px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
            .def-text { color: #374151; line-height: 1.4; }
            .section-divider { border: none; border-top: 2px dashed #E5E7EB; margin: 18px 0; }
            .q-block { margin-bottom: 12px; padding: 9px 12px; background: #F9FAFB; border: 1.5px solid #E5E7EB; border-radius: 10px; }
            .q-num { background: #F28482; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.78em; padding: 2px 8px; border-radius: 999px; display: inline-block; margin-right: 6px; }
            .q-text { font-weight: 600; font-size: 0.88em; color: #2D4042; margin-bottom: 6px; }
            .opts { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 12px; padding-left: 4px; }
            .opt { font-size: 0.83em; display: flex; gap: 5px; align-items: flex-start; color: #374151; }
            .opt-key { background: #F0F2F5; color: #2D4042; font-weight: 700; min-width: 20px; height: 20px; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 0.85em; flex-shrink: 0; }
        </style></head><body>${content.innerHTML}</body></html>`);
        win.document.close();
        const printAndClose = () => { win.focus(); win.print(); win.close(); };
        if (win.document.fonts?.ready) {
            win.document.fonts.ready.then(printAndClose).catch(printAndClose);
        } else {
            setTimeout(printAndClose, 250);
        }
        activityAPI.logEvent('ACTION', 'PRINT_WORKSHEET', {
            action: 'PRINT_WORKSHEET',
            homeworkId,
            isContentLibrary,
            route: window.location.pathname,
        }).catch(e => console.warn('Failed to log PRINT_WORKSHEET event:', e));
    };

    const handlePrintAnswerKey = () => {
        const win = window.open('', '_blank');
        if (!win?.document) return;

        // Build answer key sections
        const sections = [];

        enabledComponents.forEach((comp, sIdx) => {
            const badge = `Section ${String.fromCharCode(65 + sIdx)}`;

            if (comp.id === 'key_terms' && shuffledDefs.length > 0) {
                const rows = allGlossary.map((item, i) => {
                    const letterIdx = shuffledDefs.findIndex(d => d._origIdx === i);
                    const letter = letterIdx >= 0 ? String.fromCharCode(65 + letterIdx) : '?';
                    return `<tr><td style="padding:4px 8px;font-weight:700">${i + 1}. ${item.term}</td><td style="padding:4px 8px;text-align:center;font-weight:800;color:#75B9BE;font-size:1.1em">${letter}</td></tr>`;
                }).join('');
                sections.push(`<h3 style="font-family:Nunito,sans-serif;font-weight:800;color:#2D4042;margin:0 0 6px 0">${badge} — Key Terms Matching</h3>
                    <table style="width:100%;border-collapse:collapse;margin-bottom:16px;font-size:0.88em">
                        <thead><tr style="background:#F0FDFA"><th style="padding:5px 8px;text-align:left;color:#75B9BE;font-size:0.78em;text-transform:uppercase">Term</th><th style="padding:5px 8px;color:#75B9BE;font-size:0.78em;text-transform:uppercase">Answer</th></tr></thead>
                        <tbody>${rows}</tbody>
                    </table>`);
            }

            if (comp.id === 'mcq' && allMcqs.length > 0) {
                const rows = allMcqs.map((q, i) => {
                    const answer = String.fromCharCode(65 + (i % 4));
                    return `<tr style="${i % 2 === 0 ? 'background:#F9FAFB' : ''}"><td style="padding:4px 8px;font-weight:600">Q${i + 1}. ${q.question.replace(/\*\*/g, '')}</td><td style="padding:4px 8px;text-align:center;font-weight:800;color:#F28482;font-size:1.1em">${answer}</td></tr>`;
                }).join('');
                sections.push(`<h3 style="font-family:Nunito,sans-serif;font-weight:800;color:#2D4042;margin:0 0 6px 0">${badge} — Questions (MCQ)</h3>
                    <table style="width:100%;border-collapse:collapse;margin-bottom:16px;font-size:0.88em">
                        <thead><tr style="background:#FFF3F3"><th style="padding:5px 8px;text-align:left;color:#F28482;font-size:0.78em;text-transform:uppercase">Question</th><th style="padding:5px 8px;color:#F28482;font-size:0.78em;text-transform:uppercase">Answer</th></tr></thead>
                        <tbody>${rows}</tbody>
                    </table>`);
            }

            if (comp.id === 'fill_in_gaps' && fillInGapsProcessed) {
                const { blankedTerms } = fillInGapsProcessed;
                const rows = blankedTerms.map((term, i) => `<tr style="${i % 2 === 0 ? 'background:#FFFBEB' : ''}"><td style="padding:4px 8px;font-weight:600">Blank ${i + 1}</td><td style="padding:4px 8px;font-weight:700;color:#92400E">${term}</td></tr>`).join('');
                sections.push(`<h3 style="font-family:Nunito,sans-serif;font-weight:800;color:#2D4042;margin:0 0 6px 0">${badge} — Fill in the Gaps</h3>
                    <table style="width:100%;border-collapse:collapse;margin-bottom:16px;font-size:0.88em">
                        <thead><tr style="background:#FFFBEB"><th style="padding:5px 8px;text-align:left;color:#92400E;font-size:0.78em;text-transform:uppercase">Blank</th><th style="padding:5px 8px;text-align:left;color:#92400E;font-size:0.78em;text-transform:uppercase">Answer</th></tr></thead>
                        <tbody>${rows}</tbody>
                    </table>`);
            }

            if (comp.id === 'sequences' && shuffledSequenceSteps.length > 0) {
                const rows = shuffledSequenceSteps.map((step, i) => `<tr style="${i % 2 === 0 ? 'background:#F5F3FF' : ''}"><td style="padding:4px 8px;font-weight:600">${step.text.replace(/\*\*/g, '')}</td><td style="padding:4px 8px;text-align:center;font-weight:800;color:#7C3AED;font-size:1.1em">${step.origIdx + 1}</td></tr>`).join('');
                sections.push(`<h3 style="font-family:Nunito,sans-serif;font-weight:800;color:#2D4042;margin:0 0 6px 0">${badge} — Sequences</h3>
                    <p style="font-size:0.78em;color:#5D7375;font-style:italic;margin:0 0 6px 0">Correct order number for each item as shown on the worksheet.</p>
                    <table style="width:100%;border-collapse:collapse;margin-bottom:16px;font-size:0.88em">
                        <thead><tr style="background:#F5F3FF"><th style="padding:5px 8px;text-align:left;color:#7C3AED;font-size:0.78em;text-transform:uppercase">Step (as shown)</th><th style="padding:5px 8px;color:#7C3AED;font-size:0.78em;text-transform:uppercase">Order</th></tr></thead>
                        <tbody>${rows}</tbody>
                    </table>`);
            }

            if (comp.id === 'categorisation' && shuffledCatItems.length > 0 && effectiveCategorisation[0]) {
                const cat = effectiveCategorisation[0];
                const aItems = shuffledCatItems.map((item, i) => item.cat === 'A' ? i + 1 : null).filter(Boolean);
                const bItems = shuffledCatItems.map((item, i) => item.cat === 'B' ? i + 1 : null).filter(Boolean);
                sections.push(`<h3 style="font-family:Nunito,sans-serif;font-weight:800;color:#2D4042;margin:0 0 6px 0">${badge} — Categorisation</h3>
                    <table style="width:100%;border-collapse:collapse;margin-bottom:16px;font-size:0.88em">
                        <tr style="background:#F0FDFA"><th style="padding:6px 10px;text-align:left;color:#75B9BE;font-weight:800">${cat.categoryA}</th><th style="padding:6px 10px;text-align:left;color:#92400E;font-weight:800;background:#FFF8E1">${cat.categoryB}</th></tr>
                        <tr><td style="padding:6px 10px;vertical-align:top">${aItems.join(', ')}</td><td style="padding:6px 10px;vertical-align:top">${bItems.join(', ')}</td></tr>
                    </table>`);
            }
        });

        win.document.write(`<!DOCTYPE html><html><head><title>Answer Key — ${homeworkTitle}</title>
        <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800&family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
        <style>* { box-sizing: border-box; } body { font-family: 'Inter', sans-serif; color: #2D4042; margin: 0; padding: 16mm 18mm; background: white; font-size: 12px; line-height: 1.5; } @media print { body { padding: 12mm 14mm; } }</style>
        </head><body>
            <div style="background:#FFF3CD;border:2px solid #F2CC8F;border-radius:10px;padding:12px 16px;margin-bottom:20px;display:flex;align-items:center;gap:10px">
                <span style="font-family:Nunito,sans-serif;font-weight:800;font-size:0.78em;background:#F2CC8F;color:#2D4042;padding:3px 10px;border-radius:999px">TEACHER ONLY</span>
                <div>
                    <div style="font-family:Nunito,sans-serif;font-weight:800;font-size:1.1em;color:#2D4042">Answer Key — ${homeworkTitle}</div>
                    <div style="font-size:0.75em;color:#5D7375">Universal answer key — valid for all student profiles</div>
                </div>
            </div>
            ${sections.join('<hr style="border:none;border-top:2px dashed #E5E7EB;margin:16px 0">')}
        </body></html>`);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); win.close(); }, 500);
        activityAPI.logEvent('ACTION', 'PRINT_ANSWER_KEY', {
            action: 'PRINT_ANSWER_KEY',
            homeworkId,
            isContentLibrary,
            route: window.location.pathname,
        }).catch(e => console.warn('Failed to log PRINT_ANSWER_KEY event:', e));
    };

    if (!isOpen && !inline) return null;

    const SectionHeader = ({ badge, title }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <span style={{ background: BRAND.teal, color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.72em', padding: '3px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>{badge}</span>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.95em', color: BRAND.charcoal }}>{title}</span>
        </div>
    );

    const paperContent = (
        <div id="worksheet-print-content" ref={printContentRef} style={{ background: 'white', padding: '16mm 18mm', fontFamily: isDyslexic ? "'Open-Dyslexic', sans-serif" : "'Inter', sans-serif", color: BRAND.charcoal, fontSize: `${worksheetFontSize}px`, lineHeight: isDyslexic ? '1.9' : '1.5', letterSpacing: isDyslexic ? '0.05em' : undefined }}>

                    {/* Header card */}
                    <div style={{ background: '#F0FDFA', border: `2px solid ${BRAND.teal}`, borderRadius: '12px', padding: '14px 18px', marginBottom: '18px', display: 'flex', alignItems: 'stretch', gap: '16px' }}>
                        {/* Left: title top, name/date bottom */}
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '8px' }}>
                            <div>
                                <div style={{ fontSize: '0.72em', color: '#5D7375', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '3px' }}>
                                    {homeworkTitle}
                                </div>
                                <h2 style={{ margin: 0, fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '1.35em', color: BRAND.charcoal }}>
                                    {worksheetTitle || (lessonNumber != null ? `Lesson ${lessonNumber}: ` : '') + `Worksheet ${homeworkNumber != null ? homeworkNumber : ''}`}
                                </h2>
                            </div>
                            <div style={{ display: 'flex', gap: '28px', fontSize: '0.82em', fontWeight: '600', color: BRAND.charcoal }}>
                                {shapeIndex != null
                                    ? <span><span style={{ color: BRAND.teal }}>Name: </span><span style={{ borderBottom: `2px solid ${BRAND.charcoal}`, paddingBottom: '1px', display: 'inline-block', width: '130px' }}>&nbsp;</span></span>
                                    : <span><span style={{ color: BRAND.teal }}>Student: </span>{profileLabel}</span>
                                }
                                <span><span style={{ color: BRAND.teal }}>Date: </span><span style={{ borderBottom: `2px solid ${BRAND.charcoal}`, paddingBottom: '1px', display: 'inline-block', width: '90px' }}>&nbsp;</span></span>
                            </div>
                        </div>
                        {/* Right: logo + shape, vertically centred */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                            <img src={require('../classlabs.png')} alt="ClassLabs" style={{ height: '41px' }} />
                            {shapeIndex != null && (() => {
                                const shapeData = PROFILE_SHAPES[shapeIndex % PROFILE_SHAPES.length];
                                return (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: shapeData.bg, border: `2px solid ${shapeData.color}`, borderRadius: '8px', padding: '4px' }}>
                                        <ShapeSVG name={shapeData.name} color={shapeData.color} size={26} />
                                    </span>
                                );
                            })()}
                        </div>
                    </div>

                    {(() => {
                        // Map component id → availability check
                        const hasContent = {
                            key_terms: allGlossary.length > 0,
                            mcq: allMcqs.length > 0,
                            fill_in_gaps: Boolean(fillInGapsData),
                            sequences: effectiveSequencing.length > 0,
                            categorisation: effectiveCategorisation.length > 0,
                        };

                        const sectionsToRender = enabledComponents.filter(c => hasContent[c.id]);
                        if (sectionsToRender.length === 0) {
                            return (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
                                    <div style={{ fontSize: '2em', marginBottom: '8px' }}>📝</div>
                                    <p style={{ margin: 0, fontStyle: 'italic' }}>No worksheet content yet — generate the tutorial first.</p>
                                </div>
                            );
                        }

                        return sectionsToRender.map((comp, sIdx) => {
                            const badge = `Section ${String.fromCharCode(65 + sIdx)}`;
                            const isLast = sIdx === sectionsToRender.length - 1;

                            let content = null;

                            if (comp.id === 'key_terms') {
                                content = (
                                    <>
                                        <SectionHeader badge={badge} title="Key Terms Matching" />
                                        <p style={{ fontSize: '0.75em', color: '#5D7375', fontStyle: 'italic', margin: '0 0 10px 0' }}>Match each term on the left with its correct definition on the right. Write the letter in the box.</p>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                                            <div>
                                                {allGlossary.map((item, i) => (
                                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '7px', padding: '5px 9px', background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: '8px', fontSize: '0.88em' }}>
                                                        <span style={{ background: BRAND.charcoal, color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.78em', minWidth: '20px', height: '20px', borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                                                        <span style={{ border: `1.5px solid ${BRAND.teal}`, borderRadius: '5px', width: '26px', height: '20px', display: 'inline-block', background: 'white', flexShrink: 0 }} />
                                                        <span style={{ fontWeight: '700', color: BRAND.charcoal }}>
                                                            {renderInlineMarkdown(item.term)}
                                                            {isEal && allGlossaryTranslated[i]?.term && <span style={{ fontWeight: '500', color: '#5D7375' }}> ({renderInlineMarkdown(allGlossaryTranslated[i].term)})</span>}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div>
                                                {shuffledDefs.map((item, i) => (
                                                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginBottom: '7px', fontSize: '0.88em' }}>
                                                        <span style={{ background: BRAND.teal, color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.78em', minWidth: '22px', height: '22px', borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>{String.fromCharCode(65 + i)}</span>
                                                        <span style={{ color: '#374151', lineHeight: '1.4' }}>
                                                            {renderInlineMarkdown(item.definition)}
                                                            {isEal && allGlossaryTranslated[item._origIdx]?.definition && <span style={{ color: '#5D7375', fontStyle: 'italic' }}> ({renderInlineMarkdown(allGlossaryTranslated[item._origIdx].definition)})</span>}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                );
                            }

                            if (comp.id === 'mcq') {
                                content = (
                                    <>
                                        <SectionHeader badge={badge} title="Questions" />
                                        {allMcqs.map((q, i) => {
                                            const reorderedOpts = reorderMcqOptions(q, i);
                                            return (
                                                <div key={i} style={{ marginBottom: '10px', padding: '9px 12px', background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: '10px' }}>
                                                    <div style={{ fontWeight: '600', fontSize: '0.88em', color: BRAND.charcoal, marginBottom: '6px' }}>
                                                        <span style={{ background: BRAND.coral, color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.78em', padding: '2px 8px', borderRadius: '999px', marginRight: '7px' }}>Q{i + 1}</span>
                                                        {renderInlineMarkdown(q.question)}
                                                        {isEal && allMcqsTranslated[i]?.question && <span style={{ fontWeight: '500', color: '#5D7375', fontStyle: 'italic' }}> ({renderInlineMarkdown(allMcqsTranslated[i].question)})</span>}
                                                    </div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 12px', paddingLeft: '4px' }}>
                                                        {['A', 'B', 'C', 'D'].filter(k => reorderedOpts?.[k]).map(k => (
                                                            <div key={k} style={{ fontSize: '0.83em', display: 'flex', gap: '5px', alignItems: 'flex-start', color: '#374151' }}>
                                                                <span style={{ background: '#F0F2F5', color: BRAND.charcoal, fontWeight: '700', minWidth: '20px', height: '20px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85em', flexShrink: 0 }}>{k}</span>
                                                                <span>
                                                                    {renderInlineMarkdown(reorderedOpts[k])}
                                                                    {isEal && allMcqsTranslated[i]?.options?.[k] && <span style={{ color: '#5D7375', fontStyle: 'italic' }}> ({renderInlineMarkdown(allMcqsTranslated[i].options[k])})</span>}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                );
                            }

                            if (comp.id === 'fill_in_gaps' && fillInGapsProcessed) {
                                const { display: fDisplay, wordBank } = fillInGapsProcessed;
                                content = (
                                    <>
                                        <SectionHeader badge={badge} title="Fill in the Gaps" />
                                        <p style={{ fontSize: '0.75em', color: '#5D7375', fontStyle: 'italic', margin: '0 0 10px 0' }}>Use the word bank to fill in the missing words.</p>
                                        <div style={{ padding: '10px 14px', background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: '10px', fontSize: '0.88em', lineHeight: '2', marginBottom: '10px', color: BRAND.charcoal }}>
                                            {fDisplay.split('▢▢▢').map((part, i, arr) => (
                                                <React.Fragment key={i}>
                                                    {part}
                                                    {i < arr.length - 1 && <span style={{ display: 'inline-block', borderBottom: `2px solid ${BRAND.charcoal}`, width: `${Math.round(worksheetFontSize * 7)}px`, marginBottom: '-4px', marginLeft: '2px', marginRight: '2px' }} />}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            <span style={{ fontSize: '0.78em', fontWeight: '700', color: '#5D7375', alignSelf: 'center' }}>Word bank:</span>
                                            {wordBank.map((term, i) => (
                                                <span key={i} style={{ padding: '3px 10px', background: '#F0FDFA', border: `1.5px solid ${BRAND.teal}`, borderRadius: '6px', fontSize: '0.82em', fontWeight: '700', color: BRAND.charcoal }}>{term}</span>
                                            ))}
                                        </div>
                                    </>
                                );
                            }

                            if (comp.id === 'sequences' && effectiveSequencing[0]) {
                                const seq = effectiveSequencing[0];
                                content = (
                                    <>
                                        <SectionHeader badge={badge} title="Sequences" />
                                        <p style={{ fontSize: '0.75em', color: '#5D7375', fontStyle: 'italic', margin: '0 0 6px 0' }}>{seq.title} — write the correct order number (1, 2, 3…) in the box next to each step.</p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            {shuffledSequenceSteps.map((step, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: '8px', fontSize: '0.88em' }}>
                                                    <span style={{ border: `1.5px solid ${BRAND.teal}`, borderRadius: '5px', width: '26px', height: '22px', display: 'inline-block', background: 'white', flexShrink: 0 }} />
                                                    <span style={{ color: BRAND.charcoal, lineHeight: '1.4' }}>{renderInlineMarkdown(step.text)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                );
                            }

                            if (comp.id === 'categorisation' && effectiveCategorisation[0]) {
                                const cat = effectiveCategorisation[0];
                                const allItems = shuffledCatItems;
                                content = (
                                    <>
                                        <SectionHeader badge={badge} title="Categorisation" />
                                        <p style={{ fontSize: '0.75em', color: '#5D7375', fontStyle: 'italic', margin: '0 0 8px 0' }}>Sort each item into the correct category by writing its number in the right column.</p>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                            <div style={{ background: '#F0FDFA', border: `1.5px solid ${BRAND.teal}`, borderRadius: '8px', padding: '8px 10px' }}>
                                                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.88em', color: BRAND.teal, marginBottom: '6px', textAlign: 'center' }}>{cat.categoryA}</div>
                                                <div style={{ minHeight: '60px', borderTop: `1px dashed ${BRAND.teal}`, paddingTop: '6px' }} />
                                            </div>
                                            <div style={{ background: '#FFF8E1', border: '1.5px solid #F2CC8F', borderRadius: '8px', padding: '8px 10px' }}>
                                                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.88em', color: '#92400E', marginBottom: '6px', textAlign: 'center' }}>{cat.categoryB}</div>
                                                <div style={{ minHeight: '60px', borderTop: '1px dashed #F2CC8F', paddingTop: '6px' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {allItems.map((item, i) => (
                                                <span key={i} style={{ padding: '4px 10px', background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: '6px', fontSize: '0.82em', fontWeight: '600', color: BRAND.charcoal }}>
                                                    <span style={{ fontWeight: '800', marginRight: '4px' }}>{i + 1}.</span>{renderInlineMarkdown(item.text)}
                                                </span>
                                            ))}
                                        </div>
                                    </>
                                );
                            }

                            if (!content) return null;
                            return (
                                <React.Fragment key={comp.id}>
                                    <div style={{ marginBottom: isLast ? 0 : '18px' }}>{content}</div>
                                    {!isLast && <hr style={{ border: 'none', borderTop: '2px dashed #E5E7EB', margin: '16px 0' }} />}
                                </React.Fragment>
                            );
                        });
                    })()}
        </div>
    );

    if (inline) {
        return (
            <div style={{ border: '1px solid #E5E7EB', borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
                {paperContent}
            </div>
        );
    }

    const fontControls = !inline && (
        <div style={{ background: '#F8FAFC', borderBottom: '1.5px solid #E5E7EB', padding: '10px 18px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: BRAND.charcoal, fontSize: '0.86em', fontWeight: '800' }}>
                <span>Worksheet text size</span>
                <select
                    value={worksheetFontSize}
                    onChange={(e) => updateWorksheetFontSize(e.target.value)}
                    style={{ border: '1.5px solid #CBD5E1', borderRadius: '8px', padding: '4px 7px', background: 'white', color: BRAND.charcoal, fontWeight: '700' }}
                >
                    {WORKSHEET_FONT_OPTIONS.map(size => <option key={size} value={size}>{size}px</option>)}
                </select>
            </label>
            <span style={{ color: '#5D7375', fontSize: '0.78em', fontWeight: '600' }}>Saved for this student.</span>
        </div>
    );

    return (
        <div
            onClick={onClose}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45,64,66,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '24px 20px' }}
        >
            <div onClick={e => e.stopPropagation()} style={{ width: '210mm', maxWidth: '100%', borderRadius: '16px', overflow: 'hidden', boxShadow: '6px 6px 0px rgba(45,64,66,0.25)' }}>
                <div style={{ background: BRAND.charcoal, padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '800', color: 'white', fontSize: '1em' }}>📄 Worksheet Preview</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handlePrintAnswerKey} className="btn-base" style={{ background: '#F2CC8F', color: BRAND.charcoal, border: `2px solid ${BRAND.charcoal}`, borderBottom: `4px solid ${BRAND.charcoal}` }}>Answer Key</button>
                        <button onClick={handlePrint} className="btn-base btn-teal">Print / Save PDF</button>
                        <button onClick={onClose} className="btn-base" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1.5px solid rgba(255,255,255,0.3)' }}>Close</button>
                    </div>
                </div>
                {fontControls}
                {paperContent}
            </div>
        </div>
    );
};

const stripMdPreview = (s = '') => String(s).replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/#+\s*/g, '').trim();
const escapePreviewRegex = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const parsePreviewVocabularyEntry = (entry = '') => {
    const cleaned = sanitizeInlineText(entry);
    if (!cleaned) return null;

    const [rawTerm, ...definitionParts] = cleaned.split(/\s*[:–—-]\s*/);
    const term = sanitizeInlineText(rawTerm);
    if (!term) return null;

    return {
        term,
        definition: sanitizeInlineText(definitionParts.join(': '))
    };
};

const buildStructurePreviewChunk = ({ contentSelections = {}, sortedSourceTags = [], fallbackChunk = null }) => {
    const selectedObjectives = [];
    const selectedVocabulary = [];

    sortedSourceTags.forEach(tag => {
        const normalizedTag = normalizeLessonTag(tag);
        selectedObjectives.push(...(contentSelections.objectivesByLesson?.[normalizedTag] || []));
        selectedVocabulary.push(...(contentSelections.vocabularyByLesson?.[normalizedTag] || []));
    });

    const objectiveLines = selectedObjectives
        .map(item => sanitizeInlineText(item?.objective))
        .filter(Boolean)
        .slice(0, 3);

    const misconceptionLines = selectedObjectives
        .map(item => sanitizeInlineText(item?.misconception))
        .filter(Boolean)
        .slice(0, 2);

    const glossary = selectedVocabulary
        .map(parsePreviewVocabularyEntry)
        .filter(Boolean)
        .filter((item, index, arr) => arr.findIndex(candidate => candidate.term.toLowerCase() === item.term.toLowerCase()) === index)
        .slice(0, 6);

    if (objectiveLines.length === 0 && glossary.length === 0) {
        return fallbackChunk;
    }

    const vocabularySentence = glossary.length > 0
        ? `Key terms include ${glossary.map(item => item.term).join(', ')}.`
        : '';
    const misconceptionSentence = misconceptionLines.length > 0
        ? `Common misconceptions to address: ${misconceptionLines.join(' ')}.`
        : '';

    return {
        adjustedExplainer: [
            ...objectiveLines,
            misconceptionSentence,
            vocabularySentence,
            fallbackChunk?.adjustedExplainer ? stripMdPreview(fallbackChunk.adjustedExplainer).slice(0, 180) : ''
        ].filter(Boolean).join(' '),
        glossary: glossary.length > 0 ? glossary : (fallbackChunk?.glossary || [])
    };
};

const StructurePreview = ({ componentId, chunk, slideshows }) => {
    const teal = '#75B9BE';
    const charcoal = '#2D4042';

    if (componentId === 'slideshow') {
        const slideCount = slideshows && slideshows.length > 0 ? slideshows.length : 3;
        const firstSlide = slideshows?.[0]?.slides?.[0];
        const caption = firstSlide?.caption || firstSlide?.narration || firstSlide?.text || null;
        return (
            <div>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: charcoal, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Slideshow Preview</div>
                <div style={{ background: '#F0FDFA', border: `2px solid ${teal}`, borderRadius: '8px', height: '110px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <div style={{ fontSize: '0.65rem', color: teal, fontWeight: '700', position: 'absolute', top: '8px', left: '10px' }}>Slide 1 of {slideCount}</div>
                    <div style={{ fontSize: '1.4rem' }}>▶</div>
                    <div style={{ fontSize: '0.65rem', color: teal, fontWeight: '600', marginTop: '4px' }}>Play Slide</div>
                    <div style={{ position: 'absolute', bottom: '8px', display: 'flex', gap: '4px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: teal }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#CBD5E1' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#CBD5E1' }} />
                    </div>
                </div>
                {caption && (
                    <div style={{ marginTop: '6px', fontSize: '0.65rem', color: '#374151', lineHeight: 1.4, background: '#F8FAFC', borderRadius: '4px', padding: '5px 7px', border: '1px solid #E5E7EB' }}>
                        {stripMdPreview(caption).slice(0, 100)}{stripMdPreview(caption).length > 100 ? '…' : ''}
                    </div>
                )}
            </div>
        );
    }

    if (componentId === 'narrative') {
        const cleaned = stripMdPreview(chunk?.adjustedExplainer || '');
        const hasNarrativeContent = cleaned.length > 0;
        const text = cleaned || 'Concept explanation text appears here once tutorials are generated.';
        const glossary = hasNarrativeContent ? (chunk?.glossary || []).filter(item => item?.term).slice(0, 4) : [];
        return (
            <div style={{ background: BRAND.cream, border: `2px solid ${charcoal}`, borderRadius: '16px', padding: '14px 13px', boxShadow: '4px 4px 0 rgba(45,64,66,0.10)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px dashed #E0E0E0', paddingBottom: '8px', marginBottom: '10px' }}>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.88rem', fontWeight: '900', color: charcoal }}>📝 Narrative Explainer</div>
                    <span style={{ border: `1.5px solid ${teal}`, color: teal, background: 'white', borderRadius: '999px', padding: '3px 8px', fontSize: '0.58rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{hasNarrativeContent ? 'Preview' : 'Example'}</span>
                </div>
                {hasNarrativeContent ? (
                    <div style={{ fontSize: '0.78rem', color: charcoal, lineHeight: 1.65, maxHeight: '122px', overflow: 'hidden', fontFamily: 'Inter, sans-serif' }}>
                        {text.slice(0, 260)}{text.length > 260 ? '…' : ''}
                    </div>
                ) : (
                    <div>
                        <div style={{ height: '10px', background: '#E5E7EB', borderRadius: '4px', marginBottom: '6px', width: '92%' }} />
                        <div style={{ height: '10px', background: '#E5E7EB', borderRadius: '4px', marginBottom: '6px', width: '85%' }} />
                        <div style={{ height: '10px', background: '#E5E7EB', borderRadius: '4px', marginBottom: '6px', width: '78%' }} />
                        <div style={{ marginTop: '8px', fontSize: '0.68rem', color: '#9AACAE', fontStyle: 'italic' }}>Preview available after tutorials are generated</div>
                    </div>
                )}
                {glossary.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '10px' }}>
                        {glossary.map((item, index) => (
                            <span key={`${item.term}-${index}`} style={{ background: 'white', border: `1.5px solid ${teal}`, color: charcoal, borderRadius: '999px', padding: '3px 8px', fontSize: '0.62rem', fontWeight: '800' }}>{item.term}</span>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (componentId === 'fill_in_gaps') {
        const glossary = chunk?.glossary || [];
        const explainer = stripMdPreview(chunk?.adjustedExplainer || '');
        const hasExplainerContent = explainer.length > 0 && glossary.length > 0;
        const terms = glossary.map(g => sanitizeInlineText(g.term || '')).filter(Boolean).slice(0, 4);
        const displayTerms = hasExplainerContent ? terms : ['Key idea', 'Example', 'Reason'];
        const previewText = hasExplainerContent ? explainer.slice(0, 170) : 'Students drag the selected key vocabulary into gaps in the explainer text.';
        const safeTerms = hasExplainerContent ? terms.map(escapePreviewRegex).filter(Boolean) : [];
        const gappedPreview = safeTerms.length > 0
            ? previewText.replace(new RegExp(`(${safeTerms.join('|')})`, 'gi'), '___')
            : previewText;
        return (
            <div style={{ background: BRAND.cream, border: `2px solid ${charcoal}`, borderRadius: '16px', padding: '14px 13px', boxShadow: '4px 4px 0 rgba(45,64,66,0.10)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px dashed #E0E0E0', paddingBottom: '8px', marginBottom: '10px' }}>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.88rem', fontWeight: '900', color: charcoal }}>✏️ Fill in the Gaps</div>
                    <span style={{ border: `1.5px solid ${BRAND.coral}`, color: BRAND.coral, background: 'white', borderRadius: '999px', padding: '3px 8px', fontSize: '0.58rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{hasExplainerContent ? 'Drag task' : 'Example'}</span>
                </div>
                {!hasExplainerContent && (
                    <div style={{ fontSize: '0.68rem', color: '#9AACAE', fontStyle: 'italic', marginBottom: '8px' }}>Preview available after tutorials are generated</div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', padding: '10px', background: '#F8F9FA', borderRadius: '10px', border: '2px dashed #D0D5D6', marginBottom: '12px', minHeight: '42px' }}>
                    {displayTerms.map((term, index) => (
                        <span key={`${term}-${index}`} style={{ padding: '5px 11px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '900', background: BRAND.white, color: hasExplainerContent ? charcoal : '#9AACAE', border: `2px solid ${hasExplainerContent ? charcoal : '#C8D3D4'}`, boxShadow: hasExplainerContent ? '2px 2px 0 rgba(45,64,66,0.12)' : 'none' }}>{term}</span>
                    ))}
                </div>
                <div style={{ fontSize: '0.78rem', lineHeight: 2.35, color: hasExplainerContent ? charcoal : '#9AACAE', fontFamily: 'Inter, sans-serif', fontStyle: hasExplainerContent ? 'normal' : 'italic' }}>
                    {gappedPreview.split(/(___)/g).map((part, index) => part === '___' ? (
                        <span key={index} style={{ display: 'inline-block', minWidth: '58px', padding: '0 9px', lineHeight: 1.9, margin: '0 3px', borderRadius: '6px', border: '2px solid #B0C4C6', background: '#F8F9FA', color: '#9AACAE', fontWeight: '800', textAlign: 'center' }}>___</span>
                    ) : <span key={index}>{part}</span>)}{hasExplainerContent && explainer.length > 170 ? '…' : ''}
                </div>
            </div>
        );
    }

    if (componentId === 'keyterms') {
        const glossary = chunk?.glossary && chunk.glossary.length > 0 ? chunk.glossary.slice(0, 3) : null;
        const placeholderTerms = [
            { term: 'Osmosis', definition: 'Movement of water across a membrane' },
            { term: 'Diffusion', definition: 'Spreading of particles from high to low' },
            { term: 'Active transport', definition: 'Movement against concentration gradient' },
        ];
        const rows = glossary
            ? glossary.map(g => ({ term: stripMdPreview(g.term || g.word || ''), definition: stripMdPreview(g.definition || g.meaning || '') }))
            : placeholderTerms;
        const letters = ['A', 'B', 'C'];
        return (
            <div>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: charcoal, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔑 Key-Terms Matching</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {rows.map((r, i) => (
                            <div key={i} style={{ background: '#F0FDFA', border: `1px solid ${teal}`, borderRadius: '4px', padding: '4px 6px', fontSize: '0.65rem', fontWeight: '700', color: charcoal }}>
                                {i + 1}. {r.term.slice(0, 22)}{r.term.length > 22 ? '…' : ''}
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {rows.map((r, i) => (
                            <div key={i} style={{ background: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: '4px', padding: '4px 6px', fontSize: '0.65rem', color: '#374151' }}>
                                {letters[i]}. {r.definition.slice(0, 30)}{r.definition.length > 30 ? '…' : ''}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (componentId === 'mcq') {
        const mcqs = chunk?.mcqs;
        const firstMcq = mcqs?.easy?.[0] || mcqs?.medium?.[0] || null;
        const question = stripMdPreview(firstMcq?.question || 'Which of the following best describes osmosis?');
        const rawOptions = firstMcq?.options;
        const fallbackOptions = ['Movement of water', 'Movement of ions', 'Active transport', 'Endocytosis'];
        const safeOptions = Array.isArray(rawOptions) && rawOptions.length > 0
            ? rawOptions
            : (rawOptions && typeof rawOptions === 'object' && !Array.isArray(rawOptions) ? Object.values(rawOptions) : fallbackOptions);
        const optLabels = ['A', 'B', 'C', 'D'];
        return (
            <div>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: charcoal, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>✅ Multiple Choice</div>
                <div style={{ fontSize: '0.7rem', color: charcoal, fontWeight: '700', marginBottom: '6px', lineHeight: 1.4 }}>
                    {question.slice(0, 80)}{question.length > 80 ? '…' : ''}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {safeOptions.slice(0, 4).map((opt, i) => {
                        const optStr = stripMdPreview(typeof opt === 'string' ? opt : String(opt));
                        return (
                            <div key={i} style={{ background: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: '4px', padding: '3px 7px', fontSize: '0.65rem', color: '#374151', display: 'flex', gap: '5px' }}>
                                <span style={{ fontWeight: '800', color: teal }}>{optLabels[i]}.</span>
                                <span>{optStr.slice(0, 35)}{optStr.length > 35 ? '…' : ''}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (componentId === 'categorisation') {
        const cat = chunk?.categorisation;
        const nameA = stripMdPreview(cat?.categoryA || 'Category A');
        const nameB = stripMdPreview(cat?.categoryB || 'Category B');
        const itemsA = (cat?.itemsA || ['Item one', 'Item two']).slice(0, 2);
        const itemsB = (cat?.itemsB || ['Item three', 'Item four']).slice(0, 2);
        return (
            <div>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: charcoal, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🗂️ Categorisation</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {[{ name: nameA, items: itemsA }, { name: nameB, items: itemsB }].map((col, ci) => (
                        <div key={ci}>
                            <div style={{ background: teal, borderRadius: '4px', padding: '3px 6px', fontSize: '0.65rem', fontWeight: '800', color: 'white', textAlign: 'center', marginBottom: '4px' }}>
                                {col.name.slice(0, 20)}{col.name.length > 20 ? '…' : ''}
                            </div>
                            {col.items.map((item, ii) => (
                                <div key={ii} style={{ background: '#F0FDFA', border: `1px solid ${teal}`, borderRadius: '4px', padding: '3px 6px', fontSize: '0.65rem', color: charcoal, marginBottom: '3px' }}>
                                    {stripMdPreview(typeof item === 'string' ? item : item.text || '').slice(0, 24)}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (componentId === 'sequences') {
        const seq = chunk?.sequencing;
        const rawSteps = seq?.steps && seq.steps.length > 0 ? seq.steps.slice(0, 4) : [
            'First step of the process',
            'Second step occurs here',
            'Third step follows',
            'Final step completes it',
        ];
        return (
            <div>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: charcoal, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔢 Sequences</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {rawSteps.map((step, i) => {
                        const stepText = stripMdPreview(typeof step === 'string' ? step : step.text || step.step || '');
                        return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '20px', height: '20px', border: `1.5px solid ${teal}`, borderRadius: '4px', flexShrink: 0, background: 'white' }} />
                                <div style={{ fontSize: '0.65rem', color: '#374151', background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '3px 6px', flex: 1 }}>
                                    {stepText.slice(0, 40)}{stepText.length > 40 ? '…' : ''}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (componentId === 'score') {
        return (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: charcoal, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏆 Score Screen</div>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', border: `3px solid ${teal}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                    <span style={{ fontSize: '1rem', fontWeight: '800', color: teal }}>87%</span>
                </div>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: charcoal }}>Well done!</div>
            </div>
        );
    }

    return null;
};

const DemoHomeworkDetail = ({ onLogout, user, libraryPreview = false, homeworkId: homeworkIdProp, libraryEntryId, libraryLessonTitle = '', jcqMode = false, jcqBucketId = '', jcqReturnPath = '', jcqLessonMeta = {}, hideLibraryAddAction = false, forceBuildControlsUnlocked = false, jcqEmbeddedBuildMode = false, onStudentTutorialsLoaded, onStudentSlideshowsLoaded }) => {
    const guideUserId = user?.uid || user?.hashedPassword || null;

    useEffect(() => {
        setGuideUser(guideUserId);
    }, [guideUserId]);
    const { homeworkId: homeworkIdParam } = useParams();
    const homeworkId = homeworkIdProp || homeworkIdParam;
    const navigate = useNavigate();
    const location = useLocation();
    const ns = location.pathname.startsWith('/lite/') ? '/lite' : location.pathname.startsWith('/demo/') ? '/demo' : '';
    const getClassProfilesPath = useCallback((scheduleId) => {
        if (!scheduleId || ns === '/demo') return ns === '/lite' ? '/lite/dashboard' : '/dashboard';
        if (ns === '/lite') return `/lite/profiles/${scheduleId}`;
        return `/students/${scheduleId}`;
    }, [ns]);
    const _uid = user?.uid;
    const spKey = _uid ? `studentProfileMode_${_uid}` : null;
    const [classLinkModal, setClassLinkModal] = useState({ isOpen: false, url: '', loading: false });
    const [printAllModal, setPrintAllModal] = useState({ isOpen: false, selections: {}, answerKeySelected: false, answerKeyCopies: 1, supplyMode: false });
    const [addToDashboardState, setAddToDashboardState] = useState({ loading: false, done: false, error: '' });
    const [homework, setHomework] = useState(null);
    const isSupplyTeacherMode = libraryPreview || homework?.lessonPlanId?.studentProfileMode === 'supply_teacher';
    const isOakHomework = homework?.lessonPlanId?.isOakAcademy === true;
    const hasHomeworkStudents = Array.isArray(homework?.students) && homework.students.length > 0;
    const [tutorialContent, setTutorialContent] = useState(null);
    const [masterSlideshowStatus, setMasterSlideshowStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [guideConfig, setGuideConfig] = useState({ isOpen: false, mode: 'manual', autoStep: null });
    const [guideExtendedHeight, setGuideExtendedHeight] = useState(null);

    const handleGuideClose = () => {
        setGuideConfig(prev => ({ ...prev, isOpen: false }));
        setGuideExtendedHeight(null);
    };
    const [error, setError] = useState('');
    const [studentTutorials, setStudentTutorials] = useState([]);
    const [studentSlideshows, setStudentSlideshows] = useState([]);

    const hasTutorialsGenerated = studentTutorials.some(st => {
        if (st.status !== 'COMPLETED') return false;
        const relevantSlideshows = studentSlideshows.filter(ss => ss.studentTutorialId?.toString() === st._id?.toString());
        const expectedChunkCount = st.generatedContent?.length;
        return expectedChunkCount && relevantSlideshows.length === expectedChunkCount && relevantSlideshows.every(ss => ss.status === 'COMPLETED');
    });
    useEffect(() => {
        if (jcqEmbeddedBuildMode || loading || !hasHomeworkStudents || !isProgressiveGuideLoginEligible(guideUserId)) return;
        if (!hasTutorialsGenerated && !hasShownThisSession('hd_pre')) {
            markShownThisSession('hd_pre');
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep: 'pre' });
        } else if (hasTutorialsGenerated && !hasShownThisSession('hd_post')) {
            markShownThisSession('hd_post');
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep: 'post' });
        }
    }, [jcqEmbeddedBuildMode, loading, hasHomeworkStudents, hasTutorialsGenerated, guideUserId]);
    const [generatingStudentSlideshows, setGeneratingStudentSlideshows] = useState({});
    const [costSummary, setCostSummary] = useState({ totalCost: 0, breakdown: [] });
    const reportedSlideshowFailuresRef = useRef(new Set());
    const hasAutoExpandedRef = useRef(false);
    
    // Automation State
    const _ssQueueKey = `automation_queue_${homeworkId}`;
    const [automationQueue, setAutomationQueue] = useState(() => {
        if (libraryPreview) { sessionStorage.removeItem(_ssQueueKey); return {}; }
        try { return JSON.parse(sessionStorage.getItem(_ssQueueKey) || '{}'); } catch { return {}; }
    });
    const [progressText, setProgressText] = useState({});
    const [pendingGlossaryModes, setPendingGlossaryModes] = useState({});
    const [pendingGlossaryRememberChoices, setPendingGlossaryRememberChoices] = useState({});
    const [savedGlossaryModes, setSavedGlossaryModes] = useState({});

    // Pre-populate glossary mode choices from student profile data so we never ask again after first save
    useEffect(() => {
        if (!homework?.students) return;
        const allowedModes = ['PRIMARY_TEXT_ENGLISH_GLOSSARY', 'ENGLISH_TEXT_PRIMARY_GLOSSARY'];
        setSavedGlossaryModes(prev => {
            const updates = {};
            homework.students.forEach(student => {
                const id = student?._id;
                if (id && student.glossaryDirectionPrompted === true && allowedModes.includes(student.glossaryLanguageMode) && !prev[id]) {
                    updates[id] = student.glossaryLanguageMode;
                }
            });
            return Object.keys(updates).length ? { ...prev, ...updates } : prev;
        });
    }, [homework]);
    const [glossaryDirectionModal, setGlossaryDirectionModal] = useState({
        isOpen: false,
        student: null,
        selectedMode: 'PRIMARY_TEXT_ENGLISH_GLOSSARY',
        rememberChoice: true
    });
    const [pendingGenerateAllEalQueue, setPendingGenerateAllEalQueue] = useState([]);
    const [pendingGenerateAllLaunches, setPendingGenerateAllLaunches] = useState([]);


    const allowedGlossaryModes = ['PRIMARY_TEXT_ENGLISH_GLOSSARY', 'ENGLISH_TEXT_PRIMARY_GLOSSARY'];

    const getRememberedGlossaryModeForStudent = (student) => {
        const studentId = student?._id;
        if (!studentId) return null;

        const fromSessionState = savedGlossaryModes[studentId];
        if (allowedGlossaryModes.includes(fromSessionState)) {
            return fromSessionState;
        }

        const fromStudentProfile = student?.glossaryLanguageMode;
        const wasPromptedBefore = student?.glossaryDirectionPrompted === true;
        if (wasPromptedBefore && allowedGlossaryModes.includes(fromStudentProfile)) {
            return fromStudentProfile;
        }

        return null;
    };

    // Modal State
    const [modalState, setModalState] = useState({ isOpen: false, actionName: '', showImageOptions: false, callback: null });
    const [reviewModal, setReviewModal] = useState({ isOpen: false, tutorial: null, studentName: '', slideshows: [], requireApproval: false, onApprove: null, onReport: null, tutorialId: null });
    const [expandedTutorialRows, setExpandedTutorialRows] = useState({});
    const [expandedWorksheetRows, setExpandedWorksheetRows] = useState({});
    const [approvedTutorialIds, setApprovedTutorialIds] = useState({});
    const [rejectedTutorials, setRejectedTutorials] = useState({});
    const [hasClickedIndividualGenerate, setHasClickedIndividualGenerate] = useState(false);
    const [isContentExpanded, setIsContentExpanded] = useState(false);
    const [testLengthMinutes, setTestLengthMinutes] = useState(15);
    const testLengthSaveTimerRef = useRef(null);
    const [tutorialReportData, setTutorialReportData] = useState(null);
    const [selectedBreakdown, setSelectedBreakdown] = useState(null);
    const detailsRef = useRef(null);
    const [scaffoldingModal, setScaffoldingModal] = useState({ isOpen: false, title: '', content: null });
    const [worksheetModal, setWorksheetModal] = useState({ isOpen: false, profileLabel: '', studentTutorial: null, shapeIndex: null, senCondition: '' });
    const [tutorialDeliveryStatus, setTutorialDeliveryStatus] = useState({});
    const [attemptsModal, setAttemptsModal] = useState({ isOpen: false, studentName: '', attempts: [], studentTutorialId: null });
    const [tutorialAttemptSummaries, setTutorialAttemptSummaries] = useState({});
    const [previewJumpSteps, setPreviewJumpSteps] = useState({});
    const [previewStepPlans, setPreviewStepPlans] = useState({});
    const iframeRefs = useRef({});

    // Sync the Test Length input with the homework's saved duration, and persist
    // edits so the published JCQ tests (and the report's extra-time cutoff) adapt
    // to whatever length the teacher sets here.
    useEffect(() => {
        const savedMins = Number(homework?.durationMinutes);
        if (Number.isFinite(savedMins) && savedMins > 0) setTestLengthMinutes(savedMins);
    }, [homework?._id, homework?.durationMinutes]);

    useEffect(() => () => clearTimeout(testLengthSaveTimerRef.current), []);

    const handleTestLengthChange = (rawValue) => {
        const next = Number(rawValue);
        const clamped = Number.isFinite(next) ? Math.max(5, Math.min(180, next)) : 15;
        setTestLengthMinutes(clamped);
        if (!homeworkId) return;
        clearTimeout(testLengthSaveTimerRef.current);
        testLengthSaveTimerRef.current = setTimeout(() => {
            homeworkAPI.updateDuration(homeworkId, clamped).catch((err) => {
                console.error('Failed to save test length:', err);
            });
        }, 600);
    };

    useEffect(() => {
        if (!libraryPreview) return;
        const handleMessage = (event) => {
            if (event.data?.type === 'TUTORIAL_STEP_CHANGED' && event.data?.step) {
                // Find student by tutorial ID to update the correct row's breadcrumb state
                const matchedStudent = studentTutorials.find(t => t._id?.toString() === event.data.studentTutorialId?.toString())?.studentId;
                const studentId = matchedStudent?._id || matchedStudent;
                if (studentId) {
                    const sIdStr = studentId.toString();
                    setPreviewJumpSteps(prev => ({ ...prev, [sIdStr]: event.data.step }));
                    if (Array.isArray(event.data.steps) && event.data.steps.length > 0) {
                        setPreviewStepPlans(prev => ({ ...prev, [sIdStr]: event.data.steps }));
                    }
                }
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [libraryPreview, jcqEmbeddedBuildMode, studentTutorials]);

    const handleBreadcrumbClick = (studentId, step) => {
        const iframe = iframeRefs.current[studentId];
        if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type: 'JUMP_TO_STEP', step }, '*');
        }
        setPreviewJumpSteps(prev => ({ ...prev, [studentId]: step }));
    };

    const [linkModal, setLinkModal] = useState({
        isOpen: false,
        url: '',
        studentName: '',
        studentTutorialId: '',
        email: '',
        sentEmail: '',
        sendingEmail: false,
        error: '',
        emailError: ''
    });
    const [classTutorialStats, setClassTutorialStats] = useState({
        classSize: 0,
        sentCount: 0,
        attemptedCount: 0,
        completedCount: 0,
        attemptedPctOfSent: 0,
        completedPctOfClass: 0,
        avgSubstantiveCompleted: 0,
        avgKeyTermsCompleted: 0
    });


    const [analysisByLessonTag, setAnalysisByLessonTag] = useState({});
    const [oakKeywords, setOakKeywords] = useState([]);
    const [contentSelections, setContentSelections] = useState({ objectivesByLesson: {}, vocabularyByLesson: {} });
    const [savingSelections, setSavingSelections] = useState(false);
    const [primaryLanguageOptions, setPrimaryLanguageOptions] = useState([]);
    const [savingScaffolding, setSavingScaffolding] = useState(false);
    const [editingVocab, setEditingVocab] = useState(null); // { tag, index, value }
    const [addingVocabTag, setAddingVocabTag] = useState(null);
    const [newVocabValue, setNewVocabValue] = useState('');

    // --- Tutorial Structure State ---
    const _regCode = (user?.registrationCodeUsed || '').trim().toUpperCase().replace(/\s+/g, ' ');
    const _isWilliamFordAccount = _regCode === 'WILLIAM FORD R_2026';

    // --- Worksheet Design State ---
    const [worksheetComponents, setWorksheetComponents] = useState([
        { id: 'fill_in_gaps', label: 'Fill in the Gaps', icon: '✏️', enabled: true },
        { id: 'sequences', label: 'Sequences', icon: '🔢', enabled: !_isWilliamFordAccount },
        { id: 'categorisation', label: 'Categorisation', icon: '🗂️', enabled: !_isWilliamFordAccount },
        { id: 'mcq', label: 'Multiple Choice Questions', icon: '☑️', enabled: true },
        { id: 'key_terms', label: 'Key Terms Matching', icon: '🔑', enabled: true },
    ]);
    const [worksheetDragIndex, setWorksheetDragIndex] = useState(null);
    const [worksheetDragOverIndex, setWorksheetDragOverIndex] = useState(null);
    const [hoveredStructure, setHoveredStructure] = useState(null);
    const [structureHoverPos, setStructureHoverPos] = useState({ x: 0, y: 0 });
    const [structureConfig, setStructureConfig] = useState({
        slideshow: !jcqEmbeddedBuildMode,
        narrativeExplainer: true,
        fillInGaps: jcqEmbeddedBuildMode ? true : !_isWilliamFordAccount,
        keyTerms: true,
        mcqs: true,
        categorisation: jcqEmbeddedBuildMode ? true : !_isWilliamFordAccount,
        sequences: jcqEmbeddedBuildMode ? true : !_isWilliamFordAccount,
        showScore: jcqEmbeddedBuildMode ? true : !_isWilliamFordAccount
    });

    const handleStructureToggle = (key) => {
        if (areObjectiveSelectionsLocked) return;
        setStructureConfig(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const calculateAttemptMetrics = (attempt) => {
        const answers = attempt?.answers || [];
        const mcq = answers.filter(a => a.questionText && !a.questionText.startsWith('Match:'));
        const glossary = answers.filter(a => a.questionText && a.questionText.startsWith('Match:'));
        const correctMcq = mcq.filter(a => a.isCorrect).length;
        const substantive = mcq.length > 0 ? Math.round((correctMcq / mcq.length) * 100) : 0;
        const firstTry = glossary.filter(a => (a.studentAnswer || '').includes('Matched in 1 try')).length;
        const keyTerms = glossary.length > 0 ? Math.round((firstTry / glossary.length) * 100) : 0;
        const averageTime = answers.length > 0
            ? Math.round(answers.reduce((sum, a) => sum + (Number(a.timeTaken) || 0), 0) / answers.length)
            : 0;
        return { substantive, keyTerms, averageTime };
    };

    const buildApprovedMap = (tutorials = []) => {
        const map = {};
        (tutorials || []).forEach((tutorial) => {
            const tutorialId = tutorial?._id?.toString();
            if (!tutorialId) return;
            if (tutorial.reviewApprovedAt) {
                map[tutorialId] = true;
            }
        });
        return map;
    };

    const buildRejectedMap = (tutorials = []) => {
        const map = {};
        (tutorials || []).forEach((tutorial) => {
            const tutorialId = tutorial?._id?.toString();
            if (!tutorialId) return;
            if (tutorial.reviewRejectedAt) {
                map[tutorialId] = {
                    rejectedAt: tutorial.reviewRejectedAt,
                    reason: tutorial.reviewRejectionReason || ''
                };
            }
        });
        return map;
    };

    // Map each representative student _id -> shape index (supply teacher mode)
    const profileShapeMap = useMemo(() => {
        if (!isSupplyTeacherMode || !homework?.students) return {};
        const sorted = [...homework.students].sort((a, b) => {
            const ka = getProfileKey(a), kb = getProfileKey(b);
            if (ka === '__mainstream__') return -1;
            if (kb === '__mainstream__') return 1;
            return ka.localeCompare(kb);
        });
        const map = {};
        sorted.forEach((student, i) => { map[student._id] = i; });
        return map;
    }, [isSupplyTeacherMode, homework?.students]);

    const handleAddToDashboard = async () => {
        if (!libraryEntryId || addToDashboardState.loading || addToDashboardState.done) return;
        setAddToDashboardState({ loading: true, done: false, error: '' });
        try {
            const spMode = localStorage.getItem(spKey) || 'default';
            const res = await contentLibraryAPI.addToDashboard(libraryEntryId, { studentProfileMode: spMode });
            setAddToDashboardState({ loading: false, done: true, error: '' });
            if (jcqMode && jcqReturnPath) {
                navigate(jcqReturnPath, {
                    state: {
                        jcqReturnBucketId: jcqBucketId,
                        jcqLessonPlan: {
                            id: String(res.data.lessonPlanId),
                            homeworkId: res.data.homeworkId ? String(res.data.homeworkId) : '',
                            homeworkScheduleId: res.data.homeworkScheduleId ? String(res.data.homeworkScheduleId) : '',
                            previewHomeworkId: homeworkId ? String(homeworkId) : '',
                            libraryEntryId: libraryEntryId ? String(libraryEntryId) : '',
                            title: jcqLessonMeta.unitTitle || libraryLessonTitle,
                            subject: jcqLessonMeta.platformSubject || '',
                            yearGroup: jcqLessonMeta.yearGroup || '',
                        }
                    }
                });
            } else {
                navigate('/demo/dashboard');
            }
        } catch (err) {
            setAddToDashboardState({ loading: false, done: false, error: err.response?.data?.error || 'Failed to add to dashboard.' });
        }
    };

    const handleGetClassLink = async () => {
        setClassLinkModal({ isOpen: true, url: '', loading: true });
        try {
            const res = await homeworkClassLinkAPI.create(homeworkId);
            setClassLinkModal({ isOpen: true, url: res.data.url, loading: false });
        } catch {
            setClassLinkModal({ isOpen: false, url: '', loading: false });
            alert('Failed to create class link. Make sure at least one tutorial has been approved.');
        }
    };

    const handleOpenPrintAllModal = async () => {
        // Fetch ALL students for this schedule to get accurate profile counts
        let scheduleStudents = [];
        try {
            const res = await studentAPI.getForSchedule(homework?.homeworkScheduleId);
            scheduleStudents = res.data || [];
        } catch { scheduleStudents = homework?.students || []; }

        // Count students per profile key from the full schedule list
        const profileCounts = {};
        scheduleStudents.forEach(s => {
            const key = getProfileKey(s);
            profileCounts[key] = (profileCounts[key] || 0) + 1;
        });

        // Build one entry per representative student in homework.students
        const selections = {};
        (homework?.students || []).forEach(student => {
            const key = getProfileKey(student);
            const st = studentTutorials.find(t =>
                t.studentId?.toString() === student._id?.toString()
            );
            const stId = st?._id?.toString();
            const isApproved = !!(st?.status === 'COMPLETED' && stId && approvedTutorialIds[stId]);
            selections[key] = {
                selected: isApproved,
                copies: profileCounts[key] || 1,
                tutorial: isApproved ? st : null,
                label: getProfileLabel(student),
                shapeIndex: profileShapeMap[student._id] ?? 0,
                senCondition: getStudentSenCondition(student),
                studentId: student._id?.toString(),
            };
        });
        const anyApproved = Object.values(selections).some(s => s.selected);
        setPrintAllModal({ isOpen: true, selections, answerKeySelected: anyApproved, answerKeyCopies: 1, supplyMode: true });
    };

    const handleOpenNonSupplyPrintAllModal = () => {
        const selections = {};
        (homework?.students || []).forEach(student => {
            const st = studentTutorials.find(t => t.studentId?.toString() === student._id?.toString());
            const hasCompleted = st?.status === 'COMPLETED';
            selections[student._id] = {
                selected: hasCompleted,
                copies: 1,
                tutorial: hasCompleted ? st : null,
                label: student.name,
                shapeIndex: null,
                senCondition: getStudentSenCondition(student),
                studentId: student._id?.toString(),
            };
        });
        const anySelected = Object.values(selections).some(s => s.selected);
        setPrintAllModal({ isOpen: true, selections, answerKeySelected: anySelected, answerKeyCopies: 1, supplyMode: false });
    };

    const handlePrintAllWorksheets = (selections) => {
        const approvedTutorials = Object.values(selections).filter(s => s.selected && s.tutorial);
        if (approvedTutorials.length === 0) return;

        const enabledComps = worksheetComponents.length > 0
            ? worksheetComponents.filter(c => c.enabled)
            : [{ id: 'key_terms' }, { id: 'mcq' }];

        const WS_SEED = 7919;
        const lcg = (arr, seed) => {
            const a = [...arr]; let s = seed;
            for (let i = a.length - 1; i > 0; i--) {
                s = (s * 1664525 + 1013904223) & 0xffffffff;
                [a[i], a[Math.abs(s) % (i + 1)]] = [a[Math.abs(s) % (i + 1)], a[i]];
            }
            return a;
        };
        const reorderMcqOpts = (q, qIdx) => {
            const correct = q.answer || q.correctAnswer;
            const target = String.fromCharCode(65 + (qIdx % 4));
            if (!correct || !q.options?.[correct] || correct === target) return q.options;
            const opts = { ...q.options };
            [opts[target], opts[correct]] = [opts[correct], opts[target]];
            return opts;
        };
        const esc = t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const stripMd = t => String(t || '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
        const classLabsLogo = require('../classlabs.png');
        const shapeSvgHtml = (name, color, size = 26) => {
            if (!name) return '';
            const common = `fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"`;
            if (name === 'circle') return `<svg width="${size}" height="${size}" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" ${common}/></svg>`;
            if (name === 'square') return `<svg width="${size}" height="${size}" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2" ${common}/></svg>`;
            if (name === 'triangle') return `<svg width="${size}" height="${size}" viewBox="0 0 24 24"><path d="M12 5 L20 19 H4 Z" ${common}/></svg>`;
            if (name === 'star') return `<svg width="${size}" height="${size}" viewBox="0 0 24 24"><path d="M12 3 L14.7 8.5 L20.7 9.3 L16.3 13.6 L17.4 19.6 L12 16.7 L6.6 19.6 L7.7 13.6 L3.3 9.3 L9.3 8.5 Z" ${common}/></svg>`;
            if (name === 'diamond') return `<svg width="${size}" height="${size}" viewBox="0 0 24 24"><path d="M12 3 L21 12 L12 21 L3 12 Z" ${common}/></svg>`;
            if (name === 'hexagon') return `<svg width="${size}" height="${size}" viewBox="0 0 24 24"><path d="M8 4 H16 L21 12 L16 20 H8 L3 12 Z" ${common}/></svg>`;
            return '';
        };

        const getPrintWorksheetFontSize = (selection) => (
            readWorksheetFontSize(homeworkId, selection?.studentId || getStudentTutorialStudentId(selection?.tutorial))
        );

        const buildWorksheet = (selection) => {
            const st = selection.tutorial;
            const senCondition = selection.senCondition || '';
            const isDyslexicStudent = USE_OPEN_DYSLEXIC_WORKSHEET_FONT || (senCondition || '').toLowerCase().includes('dyslexia');
            const printFontSize = getPrintWorksheetFontSize(selection);
            const blankLineWidth = Math.round(printFontSize * 7);
            const chunks = st.englishGeneratedContent?.length ? st.englishGeneratedContent : (st.generatedContent || []);
            const allGlossary = chunks.flatMap(c => c.glossary || []);
            const allMcqs = chunks.flatMap(c => getMcqList(c?.mcqs));
            const rawSequencing = chunks.flatMap(c => c.sequencing?.steps?.length > 0 ? [c.sequencing] : []);
            const rawCat = chunks.flatMap(c => c.categorisation?.itemsA?.length > 0 ? [c.categorisation] : []);
            const allSequencing = rawSequencing.length > 0 ? rawSequencing : (
                oaFallbackSequencing?.steps?.length > 0
                    ? [{ title: oaFallbackSequencing.title || 'Put the steps in the correct order', steps: oaFallbackSequencing.steps }]
                    : []
            );
            const allCat = rawCat.length > 0 ? rawCat : (
                (oaFallbackCategorisation && !oaFallbackCategorisation.isNA && oaFallbackCategorisation.itemsA?.length > 0)
                    ? [oaFallbackCategorisation]
                    : []
            );

            const shuffledDefs = lcg(allGlossary.map((item, i) => ({ ...item, _origIdx: i })), WS_SEED);
            const shuffledSeqSteps = allSequencing[0]
                ? lcg(allSequencing[0].steps.map((s, i) => ({ text: s, origIdx: i })), WS_SEED + 7)
                : [];
            const shuffledCatItems = allCat[0]
                ? lcg([...(allCat[0].itemsA || []).map(t => ({ text: t, cat: 'A' })), ...(allCat[0].itemsB || []).map(t => ({ text: t, cat: 'B' }))], WS_SEED + 13)
                : [];

            let figData = null;
            for (const c of chunks) {
                const text = (c.adjustedExplainer || c.explainer || '').replace(/\*\*/g, '');
                const glossary = c.glossary || [];
                const found = glossary.filter(({ term }) => term && new RegExp(`(?<![\\w])${esc(term)}(?![\\w])`, 'i').test(text)).map(g => g.term);
                if (found.length >= 2) { figData = { text, terms: found }; break; }
            }
            let figDisplay = '', figWordBank = [];
            if (figData) {
                let d = figData.text;
                figData.terms.forEach(term => { d = d.replace(new RegExp(`(?<![\\w])(${esc(term)})(?![\\w])`, 'gi'), '▢▢▢'); });
                figDisplay = d;
                figWordBank = lcg([...figData.terms], WS_SEED + 3);
            }

            const profileLabel = selection.label || (() => {
                const student = homework?.students?.find(s => s._id?.toString() === getStudentTutorialStudentId(st));
                return student ? getProfileLabel(student) : '';
            })();
            const worksheetHeading = (libraryPreview && libraryLessonTitle) ? `Lesson ${homework?.lessonNumber ?? ''}: ${libraryLessonTitle}` : ((homework?.lessonNumber != null ? `Lesson ${homework.lessonNumber}: ` : '') + `Worksheet ${homework?.homeworkNumber != null ? homework.homeworkNumber : ''}`);
            const profileShape = selection.shapeIndex != null ? PROFILE_SHAPES[selection.shapeIndex % PROFILE_SHAPES.length] : null;

            const sections = enabledComps.map((comp, sIdx) => {
                const badge = `Section ${String.fromCharCode(65 + sIdx)}`;
                if (comp.id === 'key_terms' && shuffledDefs.length > 0) {
                    const terms = allGlossary.map((item, i) => `<div class="term-row"><span class="term-num">${i+1}</span><span class="term-blank"></span><span class="term-name">${stripMd(item.term)}</span></div>`).join('');
                    const defs = shuffledDefs.map((item, i) => `<div class="def-row"><span class="def-letter">${String.fromCharCode(65+i)}</span><span class="def-text">${stripMd(item.definition)}</span></div>`).join('');
                    return `<div class="worksheet-section"><div class="section-header"><span class="section-badge">${badge}</span><span class="section-title">Key Terms Matching</span></div><p class="section-hint">Match each term on the left with its correct definition on the right. Write the letter in the box.</p><div class="match-grid"><div>${terms}</div><div>${defs}</div></div></div>`;
                }
                if (comp.id === 'mcq' && allMcqs.length > 0) {
                    const qs = allMcqs.map((q, i) => {
                        const opts = reorderMcqOpts(q, i);
                        const optHtml = ['A','B','C','D'].filter(k => opts?.[k]).map(k => `<div class="opt"><span class="opt-key">${k}</span><span>${stripMd(opts[k])}</span></div>`).join('');
                        return `<div class="q-block"><div class="q-text"><span class="q-num">Q${i+1}</span>${stripMd(q.question)}</div><div class="opts">${optHtml}</div></div>`;
                    }).join('');
                    return `<div class="worksheet-section"><div class="section-header"><span class="section-badge">${badge}</span><span class="section-title">Questions</span></div>${qs}</div>`;
                }
                if (comp.id === 'fill_in_gaps' && figDisplay) {
                    const parts = figDisplay.split('▢▢▢');
                    const textHtml = parts.map((p, i) => p + (i < parts.length - 1 ? `<span class="blank-line" style="width:${blankLineWidth}px"></span>` : '')).join('');
                    const wb = figWordBank.map(t => `<span class="word-chip">${t}</span>`).join('');
                    return `<div class="worksheet-section"><div class="section-header"><span class="section-badge">${badge}</span><span class="section-title">Fill in the Gaps</span></div><p class="section-hint">Use the word bank to fill in the missing words.</p><div class="fig-text">${textHtml}</div><div class="word-bank"><span class="wb-label">Word bank:</span>${wb}</div></div>`;
                }
                if (comp.id === 'sequences' && shuffledSeqSteps.length > 0) {
                    const items = shuffledSeqSteps.map(step => `<div class="seq-row"><span class="term-blank"></span><span>${stripMd(step.text)}</span></div>`).join('');
                    return `<div class="worksheet-section"><div class="section-header"><span class="section-badge">${badge}</span><span class="section-title">Sequences</span></div><p class="section-hint">${allSequencing[0]?.title || 'Put the steps in the correct order'} — write the correct order number in the box next to each step.</p>${items}</div>`;
                }
                if (comp.id === 'categorisation' && shuffledCatItems.length > 0 && allCat[0]) {
                    const cat = allCat[0];
                    const items = shuffledCatItems.map((item, i) => `<span class="cat-item"><strong>${i+1}.</strong> ${stripMd(item.text)}</span>`).join('');
                    return `<div class="worksheet-section"><div class="section-header"><span class="section-badge">${badge}</span><span class="section-title">Categorisation</span></div><p class="section-hint">Sort each item into the correct category by writing its number in the right column.</p><div class="cat-grid"><div class="cat-col cat-a"><div class="cat-head">${cat.categoryA}</div><div class="cat-body"></div></div><div class="cat-col cat-b"><div class="cat-head">${cat.categoryB}</div><div class="cat-body"></div></div></div><div class="cat-items">${items}</div></div>`;
                }
                return '';
            }).filter(Boolean);

            return `<div class="ws-page${isDyslexicStudent ? ' dyslexic' : ''}" style="font-size:${printFontSize}px">
                <div class="ws-header">
                    <div class="ws-header-main">
                        <div class="ws-header-copy">
                            <div class="ws-subtitle">${homework?.title || ''}</div>
                            <div class="ws-title">${worksheetHeading}</div>
                            <div class="ws-fields">
                                ${printAllModal.supplyMode
                                    ? `<span><span class="ws-field-label">Name:</span> <span class="ws-field-line"></span></span>`
                                    : `<span><span class="ws-field-label">Student:</span> ${profileLabel}</span>`
                                }
                                <span><span class="ws-field-label">Date:</span> <span class="ws-field-line ws-date-line"></span></span>
                            </div>
                        </div>
                        <div class="ws-branding">
                            <img src="${classLabsLogo}" alt="ClassLabs" class="ws-logo" />
                            ${profileShape ? `<span class="profile-shape" style="background:${profileShape.bg};border-color:${profileShape.color}">${shapeSvgHtml(profileShape.name, profileShape.color, 26)}</span>` : ''}
                        </div>
                    </div>
                </div>
                ${sections.join('<hr class="section-divider">')}
            </div>`;
        };

        const allHtml = approvedTutorials
            .flatMap(s => Array(s.copies ?? 1).fill(buildWorksheet(s)))
            .join('<div style="page-break-after:always"></div>');

        const win = window.open('', '_blank');
        if (!win?.document) return;
        win.document.write(`<!DOCTYPE html><html><head><title>All Worksheets — ${homework?.title || ''}</title>
        <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800&family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
            @font-face { font-family: 'Open-Dyslexic'; src: url('https://fonts.cdnfonts.com/s/29616/open-dyslexic.woff') format('woff'); font-weight: normal; font-style: normal; }
            * { box-sizing: border-box; }
            body { font-family: 'Inter', sans-serif; color: #2D4042; margin: 0; background: white; font-size: 12px; line-height: 1.5; }
            .dyslexic { font-family: 'Open-Dyslexic', sans-serif !important; letter-spacing: 0.05em; line-height: 1.9; }
            .ws-page { padding: 16mm 18mm; }
            .ws-header { background: #F0FDFA; border: 2px solid #75B9BE; border-radius: 12px; padding: 14px 18px; margin-bottom: 18px; }
            .ws-header-main { display: flex; align-items: stretch; justify-content: space-between; gap: 16px; }
            .ws-header-copy { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: space-between; gap: 8px; }
            .ws-branding { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
            .ws-logo { height: 41px; }
            .profile-shape { display: inline-flex; align-items: center; justify-content: center; border: 2px solid; border-radius: 8px; padding: 4px; }
            .ws-subtitle { font-size: 0.75em; color: #5D7375; font-weight: 600; margin-bottom: 4px; letter-spacing: 0.5px; text-transform: uppercase; }
            .ws-title { font-family: 'Nunito', sans-serif; font-size: 1.35em; font-weight: 800; color: #2D4042; margin: 0 0 10px 0; }
            .ws-fields { display: flex; gap: 32px; font-size: 0.82em; font-weight: 600; color: #2D4042; }
            .ws-field-label { color: #75B9BE; margin-right: 4px; }
            .ws-field-line { border-bottom: 2px solid #2D4042; display: inline-block; width: 130px; }
            .ws-date-line { width: 90px; }
            .section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
            .section-badge { background: #75B9BE; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.75em; padding: 3px 10px; border-radius: 999px; }
            .section-title { font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 1em; color: #2D4042; }
            .section-hint { font-size: 0.75em; color: #5D7375; font-style: italic; margin: 0 0 10px 0; }
            .section-divider { border: none; border-top: 2px dashed #E5E7EB; margin: 18px 0; }
            .match-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 20px; }
            .term-row { display: flex; align-items: center; gap: 6px; margin-bottom: 7px; padding: 5px 9px; background: #F9FAFB; border: 1.5px solid #E5E7EB; border-radius: 8px; font-size: 0.88em; }
            .term-num { background: #2D4042; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.78em; min-width: 20px; height: 20px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
            .term-blank { border: 1.5px solid #75B9BE; border-radius: 6px; width: 26px; height: 20px; display: inline-block; background: white; flex-shrink: 0; }
            .term-name { font-weight: 700; }
            .def-row { display: flex; align-items: flex-start; gap: 7px; margin-bottom: 7px; font-size: 0.88em; }
            .def-letter { background: #75B9BE; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.78em; min-width: 22px; height: 22px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
            .q-block { margin-bottom: 10px; padding: 9px 12px; background: #F9FAFB; border: 1.5px solid #E5E7EB; border-radius: 10px; }
            .q-num { background: #F28482; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.78em; padding: 2px 8px; border-radius: 999px; margin-right: 7px; }
            .q-text { font-weight: 600; font-size: 0.88em; margin-bottom: 6px; }
            .opts { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 12px; }
            .opt { font-size: 0.83em; display: flex; gap: 5px; align-items: flex-start; }
            .opt-key { background: #F0F2F5; font-weight: 700; min-width: 20px; height: 20px; border-radius: 4px; display: inline-flex; align-items: center; justify-content: center; font-size: 0.85em; flex-shrink: 0; }
            .fig-text { padding: 10px 14px; background: #F9FAFB; border: 1.5px solid #E5E7EB; border-radius: 10px; font-size: 0.88em; line-height: 2; margin-bottom: 10px; }
            .blank-line { display: inline-block; border-bottom: 2px solid #2D4042; width: 90px; margin: 0 2px -4px; }
            .word-bank { display: flex; flex-wrap: wrap; gap: 6px; }
            .wb-label { font-size: 0.78em; font-weight: 700; color: #5D7375; align-self: center; }
            .word-chip { padding: 3px 10px; background: #F0FDFA; border: 1.5px solid #75B9BE; border-radius: 6px; font-size: 0.82em; font-weight: 700; }
            .seq-row { display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: #F9FAFB; border: 1.5px solid #E5E7EB; border-radius: 8px; font-size: 0.88em; margin-bottom: 5px; }
            .cat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; }
            .cat-col { border-radius: 8px; padding: 8px 10px; }
            .cat-a { background: #F0FDFA; border: 1.5px solid #75B9BE; }
            .cat-b { background: #FFF8E1; border: 1.5px solid #F2CC8F; }
            .cat-head { font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.88em; text-align: center; margin-bottom: 6px; }
            .cat-a .cat-head { color: #75B9BE; }
            .cat-b .cat-head { color: #92400E; }
            .cat-body { min-height: 50px; border-top: 1px dashed currentColor; padding-top: 6px; }
            .cat-items { display: flex; flex-wrap: wrap; gap: 6px; }
            .cat-item { padding: 4px 10px; background: #F9FAFB; border: 1.5px solid #E5E7EB; border-radius: 6px; font-size: 0.82em; font-weight: 600; }
            @media print { .ws-page { padding: 12mm 14mm; } }
        </style></head><body>${allHtml}</body></html>`);
        win.document.close();
        const printAndClose = () => { win.focus(); win.print(); win.close(); };
        if (win.document.fonts?.ready) {
            win.document.fonts.ready.then(printAndClose).catch(printAndClose);
        } else {
            setTimeout(printAndClose, 250);
        }
        activityAPI.logEvent('ACTION', 'PRINT_WORKSHEET', {
            action: 'PRINT_WORKSHEET',
            homeworkId,
            isContentLibrary: libraryPreview || isOakHomework,
            route: window.location.pathname,
        }).catch(e => console.warn('Failed to log PRINT_WORKSHEET event:', e));
    };

    const handlePrintMasterAnswerKey = (selections) => {
        const approvedTutorials = Object.values(selections).filter(s => s.selected && s.tutorial);
        if (approvedTutorials.length === 0) return;

        // All profiles share the same WS_SEED so answer positions are identical — use first as representative
        const rep = approvedTutorials[0].tutorial;

        const WS_SEED = 7919;
        const lcg = (arr, seed) => {
            const a = [...arr]; let s = seed;
            for (let i = a.length - 1; i > 0; i--) {
                s = (s * 1664525 + 1013904223) & 0xffffffff;
                [a[i], a[Math.abs(s) % (i + 1)]] = [a[Math.abs(s) % (i + 1)], a[i]];
            }
            return a;
        };
        const esc = t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const stripMd = t => String(t || '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');

        const enabledComps = worksheetComponents.length > 0
            ? worksheetComponents.filter(c => c.enabled)
            : [{ id: 'key_terms' }, { id: 'mcq' }];

        const chunks = rep.englishGeneratedContent?.length ? rep.englishGeneratedContent : (rep.generatedContent || []);
        const allGlossary = chunks.flatMap(c => c.glossary || []);
        const allMcqs = chunks.flatMap(c => getMcqList(c?.mcqs));
        const rawSequencing = chunks.flatMap(c => c.sequencing?.steps?.length > 0 ? [c.sequencing] : []);
        const rawCat = chunks.flatMap(c => c.categorisation?.itemsA?.length > 0 ? [c.categorisation] : []);
        const allSequencing = rawSequencing.length > 0 ? rawSequencing : (
            oaFallbackSequencing?.steps?.length > 0
                ? [{ title: oaFallbackSequencing.title || 'Put the steps in the correct order', steps: oaFallbackSequencing.steps }]
                : []
        );
        const allCat = rawCat.length > 0 ? rawCat : (
            (oaFallbackCategorisation && !oaFallbackCategorisation.isNA && oaFallbackCategorisation.itemsA?.length > 0)
                ? [oaFallbackCategorisation]
                : []
        );

        const shuffledDefs = lcg(allGlossary.map((item, i) => ({ ...item, _origIdx: i })), WS_SEED);
        const shuffledSeqSteps = allSequencing[0]
            ? lcg(allSequencing[0].steps.map((step, i) => ({ text: step, origIdx: i })), WS_SEED + 7)
            : [];
        const shuffledCatItems = allCat[0]
            ? lcg([...(allCat[0].itemsA || []).map(t => ({ text: t, cat: 'A' })), ...(allCat[0].itemsB || []).map(t => ({ text: t, cat: 'B' }))], WS_SEED + 13)
            : [];

        let figTerms = [];
        for (const c of chunks) {
            const text = (c.adjustedExplainer || c.explainer || '').replace(/\*\*/g, '');
            const glossary = c.glossary || [];
            const found = glossary.filter(({ term }) => term && new RegExp(`(?<![\\w])${esc(term)}(?![\\w])`, 'i').test(text)).map(g => g.term);
            if (found.length >= 2) { figTerms = found; break; }
        }

        // Profile key (shape icons inline SVG)
        const shapeSvgHtml = (name, color, size = 20) => {
            switch (name) {
                case 'circle':   return `<svg width="${size}" height="${size}" viewBox="0 0 100 100"><circle cx="50" cy="50" r="46" fill="${color}"/></svg>`;
                case 'square':   return `<svg width="${size}" height="${size}" viewBox="0 0 100 100"><rect x="8" y="8" width="84" height="84" rx="10" fill="${color}"/></svg>`;
                case 'triangle': return `<svg width="${size}" height="${size}" viewBox="0 0 100 100"><polygon points="50,6 96,92 4,92" fill="${color}"/></svg>`;
                case 'diamond':  return `<svg width="${size}" height="${size}" viewBox="0 0 100 100"><polygon points="50,4 96,50 50,96 4,50" fill="${color}"/></svg>`;
                case 'star':     return `<svg width="${size}" height="${size}" viewBox="0 0 100 100"><polygon points="50,4 61,36 95,36 68,57 79,92 50,70 21,92 32,57 5,36 39,36" fill="${color}"/></svg>`;
                case 'hexagon':  return `<svg width="${size}" height="${size}" viewBox="0 0 100 100"><polygon points="50,4 93,27 93,73 50,96 7,73 7,27" fill="${color}"/></svg>`;
                default:         return `<svg width="${size}" height="${size}" viewBox="0 0 100 100"><circle cx="50" cy="50" r="46" fill="${color}"/></svg>`;
            }
        };

        const profileKeyHtml = approvedTutorials.map(s => {
            const sd = PROFILE_SHAPES[(s.shapeIndex ?? 0) % PROFILE_SHAPES.length];
            return `<div class="pk-row">${shapeSvgHtml(sd.name, sd.color)}<span class="pk-label">${s.label || 'Profile'}</span></div>`;
        }).join('');

        // Build sections matching worksheet order
        const sections = enabledComps.map((comp, sIdx) => {
            const badge = `Section ${String.fromCharCode(65 + sIdx)}`;
            if (comp.id === 'key_terms' && shuffledDefs.length > 0) {
                const pairs = allGlossary.map((item, i) => {
                    const defIdx = shuffledDefs.findIndex(d => d._origIdx === i);
                    return `<div class="ans-pair"><span class="pair-q">${i + 1}</span><span class="arr">→</span><span class="pair-ans">${String.fromCharCode(65 + defIdx)}</span></div>`;
                }).join('');
                return `<div class="section"><div class="sec-head"><span class="sec-badge">${badge}</span><span class="sec-title">Key Terms</span></div><div class="pairs-grid">${pairs}</div></div>`;
            }
            if (comp.id === 'mcq' && allMcqs.length > 0) {
                const pairs = allMcqs.map((q, i) => {
                    const correctKey = String.fromCharCode(65 + (i % 4));
                    return `<div class="ans-pair"><span class="pair-q">Q${i + 1}</span><span class="arr">→</span><span class="pair-ans">${correctKey}</span></div>`;
                }).join('');
                return `<div class="section"><div class="sec-head"><span class="sec-badge">${badge}</span><span class="sec-title">Questions</span></div><div class="pairs-grid">${pairs}</div></div>`;
            }
            if (comp.id === 'fill_in_gaps' && figTerms.length > 0) {
                const pairs = figTerms.map((term, i) =>
                    `<div class="ans-pair"><span class="pair-q">Blank ${i + 1}</span><span class="arr">→</span><span class="pair-ans pair-ans-word">${stripMd(term)}</span></div>`
                ).join('');
                return `<div class="section"><div class="sec-head"><span class="sec-badge">${badge}</span><span class="sec-title">Fill in the Gaps</span><span class="sec-note">answers match first profile — content varies</span></div><div class="pairs-grid">${pairs}</div></div>`;
            }
            if (comp.id === 'sequences' && shuffledSeqSteps.length > 0) {
                const pairs = shuffledSeqSteps.map((step, shuffledPos) =>
                    `<div class="ans-pair"><span class="pair-q">Step ${shuffledPos + 1}</span><span class="arr">→</span><span class="pair-ans">${step.origIdx + 1}</span></div>`
                ).join('');
                return `<div class="section"><div class="sec-head"><span class="sec-badge">${badge}</span><span class="sec-title">Sequences</span></div><div class="pairs-grid">${pairs}</div></div>`;
            }
            if (comp.id === 'categorisation' && shuffledCatItems.length > 0 && allCat[0]) {
                const cat = allCat[0];
                const pairs = shuffledCatItems.map((item, i) =>
                    `<div class="ans-pair"><span class="pair-q">Item ${i + 1}</span><span class="arr">→</span><span class="pair-ans ${item.cat === 'A' ? 'pair-ans-a' : 'pair-ans-b'}">${item.cat === 'A' ? stripMd(cat.categoryA) : stripMd(cat.categoryB)}</span></div>`
                ).join('');
                return `<div class="section"><div class="sec-head"><span class="sec-badge">${badge}</span><span class="sec-title">Categorisation</span></div><div class="pairs-grid">${pairs}</div></div>`;
            }
            return '';
        }).filter(Boolean);

        const win = window.open('', '_blank');
        if (!win?.document) return;
        win.document.write(`<!DOCTYPE html><html><head><title>Master Answer Key — ${homework?.title || ''}</title>
        <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800&family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
            * { box-sizing: border-box; }
            body { font-family: 'Inter', sans-serif; color: #2D4042; margin: 0; background: white; font-size: 12px; line-height: 1.5; padding: 14mm 18mm; }
            .page-header { background: #F0FDFA; border: 2px solid #75B9BE; border-radius: 12px; padding: 14px 18px; margin-bottom: 18px; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
            .header-left .ws-subtitle { font-size: 0.75em; color: #5D7375; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 4px; }
            .header-left .ws-title { font-family: 'Nunito', sans-serif; font-size: 1.35em; font-weight: 800; color: #2D4042; margin: 0 0 12px 0; }
            .teacher-badge { background: #E07A5F; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.72em; padding: 3px 12px; border-radius: 999px; letter-spacing: 0.05em; text-transform: uppercase; white-space: nowrap; flex-shrink: 0; margin-top: 4px; }
            .pk-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
            .pk-row { display: flex; align-items: center; gap: 6px; }
            .pk-label { font-weight: 700; font-size: 0.82em; color: #2D4042; }
            .section-divider { border: none; border-top: 2px dashed #E5E7EB; margin: 16px 0; }
            .sections-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
            .section { background: #F9FAFB; border: 1.5px solid #E5E7EB; border-radius: 10px; padding: 10px 12px; }
            .sec-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
            .sec-badge { background: #75B9BE; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.72em; padding: 2px 9px; border-radius: 999px; white-space: nowrap; }
            .sec-title { font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.9em; color: #2D4042; }
            .sec-note { font-size: 0.7em; color: #9CA3AF; font-style: italic; }
            .pairs-grid { display: flex; flex-direction: column; gap: 4px; }
            .ans-pair { display: flex; align-items: center; gap: 6px; font-size: 0.88em; }
            .pair-q { color: #5D7375; font-weight: 700; min-width: 52px; flex-shrink: 0; }
            .arr { color: #9CA3AF; flex-shrink: 0; }
            .pair-ans { background: #75B9BE; color: white; font-family: 'Nunito', sans-serif; font-weight: 800; min-width: 26px; height: 22px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; padding: 0 6px; font-size: 0.88em; }
            .pair-ans-word { background: #F2CC8F; color: #2D4042; min-width: unset; padding: 0 8px; }
            .pair-ans-a { background: #F0FDFA; color: #075985; border: 1.5px solid #75B9BE; }
            .pair-ans-b { background: #FFF8E1; color: #92400E; border: 1.5px solid #F2CC8F; }
            @media print { body { padding: 10mm 14mm; } }
        </style></head><body>
        <div class="page-header">
            <div class="header-left">
                <div class="ws-subtitle">ClassCompass — Master Answer Key</div>
                <div class="ws-title">${homework?.title || ''}</div>
                <div style="font-size:0.75em;color:#5D7375;font-weight:600;margin-bottom:6px;">Valid for all profiles — answer positions are identical across all worksheets</div>
                <div class="pk-grid">${profileKeyHtml}</div>
            </div>
            <span class="teacher-badge">Teacher Only</span>
        </div>
        <div class="sections-grid">${sections.join('')}</div>
        </body></html>`);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); win.close(); }, 500);
        activityAPI.logEvent('ACTION', 'PRINT_ANSWER_KEY', {
            action: 'PRINT_ANSWER_KEY',
            homeworkId,
            isContentLibrary: libraryPreview || isOakHomework,
            route: window.location.pathname,
        }).catch(e => console.warn('Failed to log PRINT_ANSWER_KEY event:', e));
    };

    const handleApproveSupplyTutorial = async (studentTutorialId, followOnTask = '') => {
        try {
            await studentTutorialLinkAPI.approveReview(studentTutorialId, followOnTask);
            setApprovedTutorialIds(prev => ({ ...prev, [studentTutorialId]: true }));
            setReviewModal({ isOpen: false, tutorial: null, studentName: '', slideshows: [], requireApproval: false, onApprove: null, onReport: null, tutorialId: null });
        } catch {
            alert('Failed to approve tutorial.');
        }
    };

    const loadAttemptsOverview = useCallback(async (tutorials = []) => {
        try {
            if (!tutorials.length) {
                setTutorialAttemptSummaries({});
                return;
            }
            const responses = await Promise.all(
                tutorials.map(t => tutorialAttemptAPI.getAttemptsForStudentTutorial(t._id).catch(() => ({ data: [] })))
            );
            const summariesByTutorial = {};

            tutorials.forEach((tutorial, idx) => {
                const tutorialId = tutorial?._id?.toString();
                if (!tutorialId) return;
                const attempts = responses[idx]?.data || [];
                const completed = attempts.filter(a => a.status === 'completed');
                const metrics = completed.map(calculateAttemptMetrics);
                const avg = (arr, field) => arr.length ? Math.round(arr.reduce((s, x) => s + (x[field] || 0), 0) / arr.length) : 0;

                summariesByTutorial[tutorialId] = {
                    totalAttempts: attempts.length,
                    totalCompleted: completed.length,
                    avgSubstantive: avg(metrics, 'substantive'),
                    avgKeyTerms: avg(metrics, 'keyTerms'),
                    avgTime: avg(metrics, 'averageTime')
                };
            });

            setTutorialAttemptSummaries(summariesByTutorial);
        } catch (error) {
            console.error('Failed to load attempts overview:', error);
        }
    }, []);

    const closeModelModal = () => {
        setModalState({ isOpen: false, actionName: '', showImageOptions: false, callback: null });
    };

    const handleModelConfirm = (selection) => {
        if (modalState.callback) {
            modalState.callback(selection);
        }
        closeModelModal();
    };


    const reportSlideshowFailures = useCallback(async (slideshows = []) => {
        if (!Array.isArray(slideshows) || slideshows.length === 0) return;

        const failedSlideshows = slideshows.filter((ss) => ss && ss.status === 'FAILED');
        if (failedSlideshows.length === 0) return;

        for (const failure of failedSlideshows) {
            const failureReason = String(failure.failureReason || 'Unknown slideshow generation error').trim();
            const dedupeKey = `${String(failure.studentTutorialId)}:${String(failure.chunkIndex)}:${failureReason}`;
            if (reportedSlideshowFailuresRef.current.has(dedupeKey)) {
                continue;
            }
            reportedSlideshowFailuresRef.current.add(dedupeKey);

            const correlationId = createCorrelationId();
            try {
                await activityAPI.logEvent('ERROR', 'Demo Student Slideshow Generation Failed', {
                    correlationId,
                    requestId: correlationId,
                    action: 'GENERATE_STUDENT_SLIDESHOW',
                    homeworkId,
                    studentTutorialId: String(failure.studentTutorialId),
                    chunkIndex: Number(failure.chunkIndex),
                    message: failureReason,
                    route: window.location.pathname,
                });
            } catch (telemetryError) {
                console.error('Failed to report demo slideshow failure telemetry:', telemetryError);
            }
        }

    }, [homeworkId]);

    const fetchAllData = useCallback(async () => {
        try {
            setLoading(true);
            const shouldLoadClassReport = !jcqMode && !jcqEmbeddedBuildMode;
            const [homeworkRes, tutorialRes, studentTutorialsRes, costRes, classStatsRes, deliveryStatusRes, reportRes] = await Promise.all([
                homeworkAPI.getById( homeworkId),
                tutorialAPI.getForHomework(homeworkId).catch(() => ({ data: null })),
                studentTutorialAPI.getForHomework(homeworkId).catch(() => ({ data: [] })),
                costAPI.getHomeworkSummary(homeworkId).catch(() => ({ data: { totalCost: 0, breakdown: [] } })),
                studentTutorialLinkAPI.getClassStats(homeworkId).catch(() => ({ data: null })),
                studentTutorialLinkAPI.getDeliveryStatus(homeworkId).catch(() => ({ data: { statuses: {} } })),
                shouldLoadClassReport
                    ? studentTutorialLinkAPI.getTutorialReport(homeworkId).catch(() => ({ data: null }))
                    : Promise.resolve({ data: null })
            ]);
            setHomework(homeworkRes.data);

            const lessonPlanId = typeof homeworkRes.data?.lessonPlanId === 'object'
                ? homeworkRes.data?.lessonPlanId?._id
                : homeworkRes.data?.lessonPlanId;
            const isOakHomework = homeworkRes.data?.lessonPlanId?.isOakAcademy === true;
            if (lessonPlanId) {
                let analysisRes = await lessonAnalysisAPI.getStatus(lessonPlanId).catch(() => ({ data: null }));
                // For OA homeworks the user's lesson plan has no analysis — look up the system
                // lesson plan via the ContentLibrary entry and use that analysis instead.
                if (isOakHomework && !analysisRes?.data?.parsedContent) {
                    const lessonTitle = (homeworkRes.data?.sourceLessonTags || []).find(t => t && !t.startsWith('__preview__:'));
                    if (lessonTitle) {
                        const clRes = await contentLibraryAPI.list({ search: lessonTitle }).catch(() => ({ data: [] }));
                        const clEntry = (clRes.data || []).find(e => e.lessonTitle === lessonTitle);
                        if (clEntry?.keywords?.length) setOakKeywords(clEntry.keywords);
                        if (clEntry?.lessonPlanId) {
                            analysisRes = await lessonAnalysisAPI.getStatus(clEntry.lessonPlanId).catch(() => ({ data: null }));
                        }
                    }
                }
                if (libraryPreview && libraryEntryId) {
                    const previewRes = await contentLibraryAPI.preview(libraryEntryId).catch(() => ({ data: null }));
                    if (previewRes?.data?.entry?.keywords?.length) setOakKeywords(previewRes.data.entry.keywords);
                }
                const parsed = analysisRes?.data?.parsedContent || {};
                const normalizedEntries = Object.entries(parsed).map(([key, value]) => [normalizeLessonTag(key), value]);
                setAnalysisByLessonTag(Object.fromEntries(normalizedEntries));
            } else {
                setAnalysisByLessonTag({});
            }
            setTutorialContent(tutorialRes.data);
            setStudentTutorials(studentTutorialsRes.data || []);
            if (onStudentTutorialsLoaded) onStudentTutorialsLoaded(studentTutorialsRes.data || []);
            setApprovedTutorialIds(buildApprovedMap(studentTutorialsRes.data || []));
            setRejectedTutorials(buildRejectedMap(studentTutorialsRes.data || []));
            setCostSummary(costRes.data);
            await loadAttemptsOverview(studentTutorialsRes.data || []);
            if (classStatsRes.data) setClassTutorialStats(classStatsRes.data);
            if (deliveryStatusRes.data?.statuses) setTutorialDeliveryStatus(deliveryStatusRes.data.statuses);
            setTutorialReportData(shouldLoadClassReport ? (reportRes.data || { completedStudents: 0, objectiveScores: [], keyTermScores: [] }) : null);

            if (tutorialRes.data?.status === 'COMPLETED' && tutorialRes.data.phaseB_chunking?.chunks) {
                const statuses = {};
                for (let i = 0; i < tutorialRes.data.phaseB_chunking.chunks.length; i++) {
                    try {
                        const slideshowRes = await masterSlideshowAPI.getSlideshow(homeworkId, i);
                        statuses[i] = slideshowRes.data.status || 'UNKNOWN';
                    } catch (slideshowError) {
                         if (slideshowError.response?.status === 404) { statuses[i] = 'NOT_GENERATED'; }
                         else { statuses[i] = 'ERROR_FETCHING'; console.error(`Error fetching master status for chunk ${i}:`, slideshowError); }
                    }
                }
                setMasterSlideshowStatus(statuses);
            }

            if (studentTutorialsRes.data?.length > 0) {
                const completedTutorials = studentTutorialsRes.data.filter(st => st.status === 'COMPLETED');
                if (completedTutorials.length > 0) {
                    try {
                        const slideshowsResponses = await Promise.all(
                            completedTutorials.map(st => 
                                studentSlideshowAPI.getForStudentTutorial(st._id)
                                    .catch(e => {
                                        console.warn(`Failed to fetch slides for ${st._id}`, e); 
                                        return { data: [] }; 
                                    })
                            )
                        );
                        const flatSlideshows = slideshowsResponses.flatMap(r => r.data || []);
                        setStudentSlideshows(flatSlideshows);
                        if (onStudentSlideshowsLoaded) onStudentSlideshowsLoaded(flatSlideshows);
                        void reportSlideshowFailures(flatSlideshows);
                    } catch (err) {
                        console.error("Error fetching slideshows on mount:", err);
                    }
                }
            }

        } catch (err) { setError('Failed to fetch homework details.'); console.error("Fetch Error:", err); }
        finally { setLoading(false); }
    }, [homeworkId, jcqEmbeddedBuildMode, jcqMode, loadAttemptsOverview, reportSlideshowFailures]);

    const pollForUpdates = useCallback(async () => {
        try {
            const [homeworkRes, tutorialRes, studentTutorialsRes, costRes, classStatsRes, deliveryStatusRes] = await Promise.all([
                homeworkAPI.getById(homeworkId),
                tutorialAPI.getForHomework(homeworkId).catch(() => ({ data: null })),
                studentTutorialAPI.getForHomework(homeworkId).catch(() => ({ data: [] })),
                costAPI.getHomeworkSummary(homeworkId).catch(() => ({ data: null })),
                studentTutorialLinkAPI.getClassStats(homeworkId).catch(() => ({ data: null })),
                studentTutorialLinkAPI.getDeliveryStatus(homeworkId).catch(() => ({ data: { statuses: {} } }))
            ]);
            setHomework(homeworkRes.data);
            setTutorialContent(tutorialRes.data);
            setStudentTutorials(studentTutorialsRes.data || []);
            setApprovedTutorialIds(buildApprovedMap(studentTutorialsRes.data || []));
            setRejectedTutorials(buildRejectedMap(studentTutorialsRes.data || []));
            await loadAttemptsOverview(studentTutorialsRes.data || []);
            if (costRes.data) {
                setCostSummary(costRes.data);
            }
            if (classStatsRes.data) {
                setClassTutorialStats(classStatsRes.data);
            }
            if (deliveryStatusRes.data?.statuses) {
                setTutorialDeliveryStatus(deliveryStatusRes.data.statuses);
            }

            if (tutorialRes.data?.status === 'COMPLETED' && tutorialRes.data.phaseB_chunking?.chunks) {
                 const currentMasterStatuses = {}; let masterNeedsUpdate = false;
                 for (let i = 0; i < tutorialRes.data.phaseB_chunking.chunks.length; i++) {
                    const existingStatus = masterSlideshowStatus[i];
                    if (['PENDING', 'PROCESSING'].includes(existingStatus)) {
                        masterNeedsUpdate = true;
                        try {
                            const slideshowRes = await masterSlideshowAPI.getSlideshow(homeworkId, i);
                            currentMasterStatuses[i] = slideshowRes.data.status || 'UNKNOWN';
                        } catch { currentMasterStatuses[i] = 'ERROR_FETCHING'; }
                    } else { currentMasterStatuses[i] = existingStatus; }
                 }
                 if(masterNeedsUpdate) setMasterSlideshowStatus(currentMasterStatuses);
            }

            if (studentTutorialsRes.data?.length > 0) {
                const completedTutorials = studentTutorialsRes.data.filter(st => st.status === 'COMPLETED');
                if (completedTutorials.length > 0) {
                    try {
                        const slideshowsResponses = await Promise.all(
                            completedTutorials.map(st => 
                                studentSlideshowAPI.getForStudentTutorial(st._id)
                                    .catch(e => { return { data: [] }; })
                            )
                        );
                        const flatSlideshows = slideshowsResponses.flatMap(r => r.data || []);
                        setStudentSlideshows(flatSlideshows);
                        void reportSlideshowFailures(flatSlideshows);
                    } catch (err) { console.error("Critical error fetching slideshows:", err); }
                }
            } else if (studentSlideshows.length > 0) { 
                setStudentSlideshows([]); 
            }

        } catch (err) { console.error("Poll for updates failed:", err); }
    }, [homeworkId, masterSlideshowStatus, studentSlideshows, loadAttemptsOverview, reportSlideshowFailures]);


    const pollForLinkedTutorialResults = useCallback(async () => {
        try {
            const [studentTutorialsRes, classStatsRes, deliveryStatusRes] = await Promise.all([
                studentTutorialAPI.getForHomework(homeworkId).catch(() => ({ data: [] })),
                studentTutorialLinkAPI.getClassStats(homeworkId).catch(() => ({ data: null })),
                studentTutorialLinkAPI.getDeliveryStatus(homeworkId).catch(() => ({ data: { statuses: {} } }))
            ]);

            const tutorials = studentTutorialsRes.data || [];
            setStudentTutorials(tutorials);
            setApprovedTutorialIds(buildApprovedMap(tutorials));
            setRejectedTutorials(buildRejectedMap(tutorials));
            await loadAttemptsOverview(tutorials);

            if (classStatsRes.data) {
                setClassTutorialStats(classStatsRes.data);
            }
            if (deliveryStatusRes.data?.statuses) {
                setTutorialDeliveryStatus(deliveryStatusRes.data.statuses);
            }
        } catch (err) {
            console.error('Linked tutorial results polling failed:', err);
        }
    }, [homeworkId, loadAttemptsOverview]);

    // Persist automation queue to sessionStorage so navigation away and back restores exact state
    useEffect(() => {
        if (Object.keys(automationQueue).length === 0) {
            sessionStorage.removeItem(_ssQueueKey);
        } else {
            sessionStorage.setItem(_ssQueueKey, JSON.stringify(automationQueue));
        }
    }, [automationQueue, _ssQueueKey]);

    const areAnyTasksProcessing = useMemo(() =>
        (tutorialContent && ['PENDING', 'PROCESSING'].includes(tutorialContent.status)) ||
        studentTutorials.some(st => ['PENDING', 'PROCESSING'].includes(st.status)) ||
        studentSlideshows.some(ss => ['PENDING', 'PROCESSING'].includes(ss.status)) ||
        Object.keys(automationQueue).length > 0,
    [tutorialContent, studentTutorials, studentSlideshows, automationQueue]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const response = await translationAPI.getSupportedLanguages();
                const fetchedLanguages = Array.isArray(response.data?.languages) ? response.data.languages : [];
                setPrimaryLanguageOptions(fetchedLanguages.filter(Boolean));
            } catch (err) {
                console.error('Failed to fetch supported translation languages:', err);
                setPrimaryLanguageOptions([]);
            }
        };

        fetchLanguages();
    }, []);

    useEffect(() => {
        let intervalId = null;
        if (areAnyTasksProcessing) {
            intervalId = setInterval(pollForUpdates, 3000);
        }
        return () => clearInterval(intervalId);
    }, [areAnyTasksProcessing, pollForUpdates]);


    useEffect(() => {
        const intervalId = setInterval(() => {
            if (!document.hidden) {
                pollForLinkedTutorialResults();
            }
        }, 5000);

        return () => clearInterval(intervalId);
    }, [pollForLinkedTutorialResults]);

    useEffect(() => {
        // Library previews and OA homework have pre-generated slideshows (or none yet) — never trigger recovery for them.
        if (libraryPreview || isOakHomework) return;
        if (loading || !studentTutorials.length) return;

        studentTutorials.forEach(st => {
            if (st.status === 'COMPLETED') {
                const stIdStr = st._id.toString();
                const hasSlides = studentSlideshows.some(ss => ss.studentTutorialId.toString() === stIdStr);
                const isAutomating = automationQueue[st.studentId];
                const isGeneratingManually = generatingStudentSlideshows[st._id];

                if (!hasSlides && !isAutomating && !isGeneratingManually) {
                    console.log(`[Recovery] Found completed tutorial with missing slides for ${st.studentId}. Restarting automation.`);
                    setAutomationQueue(prev => ({
                        ...prev,
                        [st.studentId]: 'WAITING_FOR_CONTENT'
                    }));
                }
            }
        });
    }, [libraryPreview, studentTutorials, studentSlideshows, automationQueue, generatingStudentSlideshows, loading]);

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this tutorial?')) {
            try {
                await homeworkAPI.deleteById(homeworkId);
                navigate(libraryPreview ? `${ns}/content-library` : getClassProfilesPath(homework.homeworkScheduleId));
            } catch (err) {
                setError('Failed to delete tutorial.');
            }
        }
     };

    const launchAutomationForStudent = (studentId, selectedGlossaryMode, rememberGlossaryChoice = false) => {
        setPendingGlossaryModes(prev => ({
            ...prev,
            [studentId]: selectedGlossaryMode
        }));
        setPendingGlossaryRememberChoices(prev => ({
            ...prev,
            [studentId]: rememberGlossaryChoice === true
        }));
        setAutomationQueue(prev => ({ ...prev, [studentId]: 'INIT' }));
    };

    const openGlossaryDirectionModal = (student) => {
        const studentId = student?._id;
        if (!studentId) return;

        const existingChoice = savedGlossaryModes[studentId]
            || (student?.glossaryDirectionPrompted === true ? student.glossaryLanguageMode : null)
            || 'PRIMARY_TEXT_ENGLISH_GLOSSARY';
        setGlossaryDirectionModal({
            isOpen: true,
            student,
            selectedMode: existingChoice,
            rememberChoice: true
        });
    };

    const closeGlossaryDirectionModal = () => {
        setGlossaryDirectionModal({
            isOpen: false,
            student: null,
            selectedMode: 'PRIMARY_TEXT_ENGLISH_GLOSSARY',
            rememberChoice: true
        });
        setPendingGenerateAllEalQueue([]);
        setPendingGenerateAllLaunches([]);
    };

    const handleConfirmGlossaryDirection = () => {
        const student = glossaryDirectionModal.student;
        const studentId = student?._id;
        if (!studentId) {
            closeGlossaryDirectionModal();
            return;
        }

        setSavedGlossaryModes(prev => ({
            ...prev,
            [studentId]: glossaryDirectionModal.selectedMode
        }));

        launchAutomationForStudent(studentId, glossaryDirectionModal.selectedMode, true);

        if (pendingGenerateAllEalQueue.length > 0) {
            const [nextStudent, ...rest] = pendingGenerateAllEalQueue;
            setPendingGenerateAllEalQueue(rest);

            const nextStudentId = nextStudent?._id;
            const nextExistingChoice = savedGlossaryModes[nextStudentId]
                || nextStudent?.glossaryLanguageMode
                || glossaryDirectionModal.selectedMode
                || 'PRIMARY_TEXT_ENGLISH_GLOSSARY';

            setGlossaryDirectionModal({
                isOpen: true,
                student: nextStudent,
                selectedMode: nextExistingChoice,
                rememberChoice: glossaryDirectionModal.rememberChoice
            });
            return;
        }

        if (pendingGenerateAllLaunches.length > 0) {
            pendingGenerateAllLaunches.forEach(({ studentId, mode }) => {
                launchAutomationForStudent(studentId, mode, true);
            });
            setPendingGenerateAllLaunches([]);
        }

        closeGlossaryDirectionModal();
    };

    // For OA homework, mainstream students get an instant clone via the backend;
    // custom profiles go through the normal automation queue.
    const generateOakStudent = async (student) => {
        const studentId = student?._id;
        if (!studentId) return;
        try {
            await contentLibraryAPI.generateOakStudent({ homeworkId, studentId });
            await pollForUpdates();
            activityAPI.logEvent('ACTION', 'GENERATE_STUDENT_TUTORIAL', {
                action: 'GENERATE_STUDENT_TUTORIAL',
                homeworkId,
                studentId: String(studentId),
                model: TEXT_MODEL,
                route: window.location.pathname,
                correlationId: `${homeworkId}_${studentId}_${Date.now()}`,
                isContentLibrary: true,
            }).catch(e => console.warn('Failed to log GENERATE_STUDENT_TUTORIAL event:', e));
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to generate tutorial.');
        }
    };

    const handleSmartGenerate = async (student) => {
        const studentId = student?._id;
        if (!studentId) return;

        if (!hasClickedIndividualGenerate) {
            setHasClickedIndividualGenerate(true);
            await persistContentSelections();
        }

        if (isOakHomework) {
            if (student.noCustomScaffoldingRequired === true) {
                // Mainstream OA: instant clone
                await generateOakStudent(student);
            } else {
                // Custom OA: normal queue (TutorialContent is already COMPLETED)
                if (!student?.eal || !student?.primaryLanguage) {
                    launchAutomationForStudent(studentId, 'PRIMARY_TEXT_ENGLISH_GLOSSARY', false);
                } else {
                    const rememberedChoice = getRememberedGlossaryModeForStudent(student);
                    if (rememberedChoice) {
                        launchAutomationForStudent(studentId, rememberedChoice, true);
                    } else {
                        openGlossaryDirectionModal(student);
                    }
                }
            }
            return;
        }

        if (!student?.eal || !student?.primaryLanguage) {
            launchAutomationForStudent(studentId, 'PRIMARY_TEXT_ENGLISH_GLOSSARY', false);
            return;
        }

        const rememberedChoice = getRememberedGlossaryModeForStudent(student);
        if (rememberedChoice) {
            launchAutomationForStudent(studentId, rememberedChoice, true);
            return;
        }

        openGlossaryDirectionModal(student);
    };

    const handleGenerateAll = async () => {
        if (!hasClickedIndividualGenerate) {
            setHasClickedIndividualGenerate(true);
            await persistContentSelections();
        }

        const eligibleStudents = (homework?.students || []).filter(student => {
            const studentId = student?._id;
            if (!studentId) return false;
            const existingTutorial = studentTutorials.find(t => t.studentId === studentId);
            return !existingTutorial && !automationQueue[studentId];
        });

        if (isOakHomework) {
            // Mainstream: instant clone; custom: normal queue
            const mainstreamOak = eligibleStudents.filter(s => s.noCustomScaffoldingRequired === true);
            const customOak = eligibleStudents.filter(s => s.noCustomScaffoldingRequired !== true);
            // Fire mainstream clones in parallel
            await Promise.all(mainstreamOak.map(s => generateOakStudent(s)));
            // Queue custom profiles via normal automation
            const ealWithoutChoice = [];
            const readyToLaunch = [];
            customOak.forEach(student => {
                if (!student?.eal || !student?.primaryLanguage) {
                    readyToLaunch.push({ studentId: student._id, mode: 'PRIMARY_TEXT_ENGLISH_GLOSSARY' });
                    return;
                }
                const mode = getRememberedGlossaryModeForStudent(student);
                if (mode) { readyToLaunch.push({ studentId: student._id, mode }); return; }
                ealWithoutChoice.push(student);
            });
            if (ealWithoutChoice.length > 0) {
                const [first, ...rest] = ealWithoutChoice;
                setPendingGenerateAllEalQueue(rest);
                setPendingGenerateAllLaunches(readyToLaunch);
                openGlossaryDirectionModal(first);
                return;
            }
            readyToLaunch.forEach(({ studentId, mode }) => launchAutomationForStudent(studentId, mode, true));
            return;
        }

        const ealWithoutChoice = [];
        const readyToLaunch = [];

        eligibleStudents.forEach(student => {
            const studentId = student._id;
            if (!student?.eal || !student?.primaryLanguage) {
                readyToLaunch.push({ studentId, mode: 'PRIMARY_TEXT_ENGLISH_GLOSSARY' });
                return;
            }

            const selectedGlossaryMode = getRememberedGlossaryModeForStudent(student);
            if (selectedGlossaryMode) {
                readyToLaunch.push({ studentId, mode: selectedGlossaryMode });
                return;
            }

            ealWithoutChoice.push(student);
        });

        if (ealWithoutChoice.length > 0) {
            const [firstStudent, ...rest] = ealWithoutChoice;
            setPendingGenerateAllEalQueue(rest);
            setPendingGenerateAllLaunches(readyToLaunch);
            openGlossaryDirectionModal(firstStudent);
            return;
        }

        readyToLaunch.forEach(({ studentId, mode }) => {
            launchAutomationForStudent(studentId, mode, true);
        });
    };

    useEffect(() => {
        const processQueue = async () => {
            for (const [studentId, status] of Object.entries(automationQueue)) {
                
                if (status === 'INIT') {
                    setProgressText(prev => ({ ...prev, [studentId]: 'Creating Master Tutorial...' }));
                    if (!tutorialContent || tutorialContent.status === 'FAILED') {
                         try {
                             const res = await tutorialAPI.triggerGeneration(homeworkId, TEXT_MODEL);
                             setTutorialContent(res.data);
                             setAutomationQueue(prev => ({ ...prev, [studentId]: 'WAITING_FOR_MASTER' }));
                         } catch (err) {
                             console.error("Auto-Master failed:", err);
                             setAutomationQueue(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryModes(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryRememberChoices(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setError("Failed to auto-generate Master Tutorial.");
                         }
                    } else if (tutorialContent.status === 'COMPLETED') {
                         setAutomationQueue(prev => ({ ...prev, [studentId]: 'WAITING_FOR_CONTENT' }));
                         performGenerateStudentTutorial(studentId, TEXT_MODEL, pendingGlossaryModes[studentId], pendingGlossaryRememberChoices[studentId] === true);
                    } else {
                         setAutomationQueue(prev => ({ ...prev, [studentId]: 'WAITING_FOR_MASTER' }));
                    }
                }
                else if (status === 'WAITING_FOR_MASTER') {
                    if (tutorialContent?.status === 'COMPLETED') {
                        performGenerateStudentTutorial(studentId, TEXT_MODEL, pendingGlossaryModes[studentId], pendingGlossaryRememberChoices[studentId] === true);
                        setAutomationQueue(prev => ({ ...prev, [studentId]: 'WAITING_FOR_CONTENT' }));
                    } else if (tutorialContent?.status === 'FAILED') {
                        setAutomationQueue(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryModes(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryRememberChoices(prev => { const next = {...prev}; delete next[studentId]; return next; });
                    }
                }
                else if (status === 'WAITING_FOR_CONTENT') {
                     const st = studentTutorials.find(t => t.studentId === studentId);
                     if (st?.status === 'PROCESSING' || st?.status === 'PENDING') {
                         setProgressText(prev => ({ ...prev, [studentId]: 'Writing Content & Glossary...' }));
                     }
                     if (st?.status === 'COMPLETED') {
                         setProgressText(prev => ({ ...prev, [studentId]: 'Generating Slideshows...' }));
                         performGenerateStudentSlideshows(st._id, TEXT_MODEL, IMAGE_MODEL);
                         setAutomationQueue(prev => ({ ...prev, [studentId]: 'WAITING_FOR_SLIDES' }));
                     } else if (st?.status === 'FAILED') {
                         setAutomationQueue(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryModes(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryRememberChoices(prev => { const next = {...prev}; delete next[studentId]; return next; });
                     }
                }
                else if (status === 'WAITING_FOR_SLIDES') {
                    const st = studentTutorials.find(t => t.studentId === studentId);
                    if (st?.status === 'COMPLETED') {
                         const relevantSlideshows = studentSlideshows.filter(ss => ss.studentTutorialId.toString() === st._id.toString());
                         const expectedChunkCount = st.generatedContent?.length;
                         const allSlidesComplete = relevantSlideshows.length > 0 && relevantSlideshows.every(ss => ss.status === 'COMPLETED');

                         if (expectedChunkCount && relevantSlideshows.length === expectedChunkCount && allSlidesComplete) {
                             console.log(`Automation: Slideshows Complete for ${studentId}.`);
                             setAutomationQueue(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryModes(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryRememberChoices(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setTimeout(() => setProgressText(prev => { const next={...prev}; delete next[studentId]; return next; }), 500);
                         } else if (relevantSlideshows.some(ss => ss.status === 'FAILED')) {
                             setAutomationQueue(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryModes(prev => { const next = {...prev}; delete next[studentId]; return next; });
                             setPendingGlossaryRememberChoices(prev => { const next = {...prev}; delete next[studentId]; return next; });
                         }
                    }
                }
            }
        };
        if (Object.keys(automationQueue).length > 0) processQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [automationQueue, tutorialContent, studentTutorials, studentSlideshows, homeworkId, pendingGlossaryModes]);

    // Selections lock the moment the user clicks Generate.
    // For non-Oak, also lock when tutorials already exist (persists lock across page reloads since
    // non-Oak tutorials only exist because the user previously initiated generation).
    // For Oak, do NOT use studentTutorials.length > 0 — mainstream tutorials are pre-cloned in the
    // background before the user has clicked anything, so that would lock prematurely.
    const areObjectiveSelectionsLocked = !forceBuildControlsUnlocked && (hasClickedIndividualGenerate
        || (!isOakHomework && studentTutorials.length > 0));

    // Worksheet design is visible once at least one tutorial exists, regardless of homework type.
    const areWorksheetComponentsAvailable = hasClickedIndividualGenerate || studentTutorials.length > 0;

    const performGenerateStudentTutorial = async (studentId, model, glossaryLanguageMode, rememberGlossaryPreference = false) => {
        setError('');
        try {
            await studentTutorialAPI.triggerGeneration(homeworkId, studentId, model, glossaryLanguageMode, null, rememberGlossaryPreference, structureConfig.fillInGaps === true, structureConfig.showScore !== false);
            await pollForUpdates();
            const isContentLibrary = libraryPreview || (homework?.lessonPlanId?.isOakAcademy === true);
            activityAPI.logEvent('ACTION', 'GENERATE_STUDENT_TUTORIAL', {
                action: 'GENERATE_STUDENT_TUTORIAL',
                homeworkId,
                studentId: String(studentId),
                model,
                route: window.location.pathname,
                correlationId: `${homeworkId}_${studentId}_${Date.now()}`,
                isContentLibrary,
            }).catch(e => console.warn('Failed to log GENERATE_STUDENT_TUTORIAL event:', e));
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to start student tutorial generation.');
        }
     };

    const performGenerateStudentSlideshows = async (studentTutorialId, textModel, imageModel) => {
        setError('');
        setGeneratingStudentSlideshows(prev => ({ ...prev, [studentTutorialId]: true }));
        try {
            await studentSlideshowAPI.triggerGeneration(studentTutorialId, textModel, imageModel);
            await pollForUpdates();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to start student slideshow generation.');
        } finally {
            setGeneratingStudentSlideshows(prev => ({ ...prev, [studentTutorialId]: false }));
        }
    };

    const handlePreviewTutorial = (studentTutorialId) => {
        const returnTo = libraryPreview ? `${ns}/content-library/${libraryEntryId}/view` : `${ns}/homework/${homeworkId}`;
        navigate(`${ns}/student-tutorial-player/${studentTutorialId}?previewMode=1`, {
            state: { returnTo, previewMode: true }
        });
    };

    const copyTutorialLink = async (studentTutorialId, studentName = '', studentEmail = '') => {
        try {
            const res = await studentTutorialLinkAPI.create(studentTutorialId);
            const token = res.data?.token;
            const shareUrl = res.data?.url || (token ? `${window.location.origin}/share/${token}` : '');
            await navigator.clipboard.writeText(shareUrl);

            setLinkModal({
                isOpen: true,
                url: shareUrl,
                studentName,
                studentTutorialId: String(studentTutorialId || ''),
                email: String(studentEmail || '').trim().toLowerCase(),
                sentEmail: '',
                sendingEmail: false,
                error: ''
            });

            setTutorialDeliveryStatus(prev => ({
                ...prev,
                [String(studentTutorialId)]: {
                    ...(prev[String(studentTutorialId)] || {}),
                    sent: true
                }
            }));
        } catch {
            setLinkModal({
                isOpen: true,
                url: '',
                studentName,
                studentTutorialId: String(studentTutorialId || ''),
                email: String(studentEmail || '').trim().toLowerCase(),
                sentEmail: '',
                sendingEmail: false,
                error: 'Failed to generate or copy tutorial link. Please try again.'
            });
        }
    };

    const handleEmailTutorialLinkFromModal = async () => {
        const studentTutorialId = String(linkModal.studentTutorialId || '');
        const normalizedEmail = String(linkModal.email || '').trim().toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!studentTutorialId) {
            setLinkModal(prev => ({ ...prev, emailError: 'Tutorial link context is missing. Please close and reopen this popup.' }));
            return;
        }

        if (!normalizedEmail || !emailRegex.test(normalizedEmail)) {
            setLinkModal(prev => ({ ...prev, emailError: 'Please enter a valid email address.' }));
            return;
        }

        setLinkModal(prev => ({ ...prev, sendingEmail: true, emailError: '' }));
        try {
            await studentTutorialLinkAPI.emailLink(studentTutorialId, normalizedEmail, linkModal.studentName || 'Student');
            setLinkModal(prev => ({
                ...prev,
                email: normalizedEmail,
                sentEmail: normalizedEmail,
                sendingEmail: false,
                emailError: ''
            }));

            setTutorialDeliveryStatus(prev => ({
                ...prev,
                [studentTutorialId]: {
                    ...(prev[studentTutorialId] || {}),
                    sent: true
                }
            }));
        } catch (err) {
            setLinkModal(prev => ({
                ...prev,
                sendingEmail: false,
                emailError: err.response?.data?.error || 'Failed to email tutorial link. Please try again.'
            }));
        }
    };

    const handleApproveAndSend = async (studentTutorialId, studentName = '', studentEmail = '', followOnTask = '') => {
        try {
            await studentTutorialLinkAPI.approveReview(studentTutorialId, followOnTask);
            setApprovedTutorialIds(prev => ({ ...prev, [studentTutorialId]: true }));
            setReviewModal({ isOpen: false, tutorial: null, studentName: '', slideshows: [], requireApproval: false, onApprove: null, onReport: null, tutorialId: null });
            await copyTutorialLink(studentTutorialId, studentName, studentEmail);
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to save review approval. Please try again.');
        }
    };

    const handleReportAndReject = async (studentTutorialId, reason) => {
        const trimmedReason = (reason || '').trim();
        if (!trimmedReason) {
            alert('Please provide a reason for reporting this content.');
            return;
        }

        try {
            await studentTutorialLinkAPI.rejectReview(studentTutorialId, trimmedReason);
            setApprovedTutorialIds(prev => {
                const next = { ...prev };
                delete next[studentTutorialId];
                return next;
            });
            setRejectedTutorials(prev => ({
                ...prev,
                [studentTutorialId]: {
                    reason: trimmedReason,
                    rejectedAt: new Date().toISOString()
                }
            }));
            setReviewModal({ isOpen: false, tutorial: null, studentName: '', slideshows: [], requireApproval: false, onApprove: null, onReport: null, tutorialId: null });
            alert('Content reported and marked as rejected. You can regenerate and review again.');
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to report this content. Please try again.');
        }
    };

    const handleSendTutorial = async (studentTutorialId, studentName, studentEmail, tutorial, slideshows = []) => {
        const tutorialId = String(studentTutorialId);
        if (approvedTutorialIds[tutorialId]) {
            await copyTutorialLink(tutorialId, studentName, studentEmail);
            return;
        }

        setReviewModal({
            isOpen: true,
            tutorial,
            studentName,
            slideshows,
            requireApproval: true,
            onApprove: (followOnTask) => handleApproveAndSend(tutorialId, studentName, studentEmail, followOnTask),
            onReport: (reason) => handleReportAndReject(tutorialId, reason),
            tutorialId
        });
    };

    const handleReviewAttempts = async (studentTutorialId, studentName) => {
        try {
            const res = await tutorialAttemptAPI.getAttemptsForStudentTutorial(studentTutorialId);
            setAttemptsModal({ isOpen: true, studentName, attempts: res.data || [], studentTutorialId });
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to fetch attempts.');
        }
    };



    const openScaffoldingModal = (student, studentTutorial = null) => {
        const resolvedStudent = studentTutorial?.studentId && typeof studentTutorial.studentId === 'object'
            ? { ...student, ...studentTutorial.studentId }
            : student;

        const age = resolvedStudent?.age ?? 'N/A';
        const readingAge = resolvedStudent?.readingAge ?? 'N/A';
        const senConditions = Array.isArray(resolvedStudent?.senConditions) && resolvedStudent.senConditions.length > 0
            ? resolvedStudent.senConditions
            : (resolvedStudent?.senCondition && resolvedStudent.senCondition !== 'None' ? [resolvedStudent.senCondition] : []);
        const sen = senConditions.length > 0 ? senConditions.join(', ') : 'None';
        const eal = !!resolvedStudent?.eal;
        const primaryLanguage = resolvedStudent?.primaryLanguage || 'English';
        const audioNarration = resolvedStudent?.audioNarrationEnabled ? 'Enabled' : 'Disabled';

        const mode = studentTutorial?.glossaryLanguageMode || resolvedStudent?.glossaryLanguageMode || 'PRIMARY_TEXT_ENGLISH_GLOSSARY';
        const languageDirection = eal
            ? (mode === 'ENGLISH_TEXT_PRIMARY_GLOSSARY'
                ? `English with ${primaryLanguage} Glossary`
                : `${primaryLanguage} with English Glossary`)
            : 'English with English Glossary';

        setScaffoldingModal({
            isOpen: true,
            title: `Scaffolding: ${resolvedStudent?.name || 'Student'}`,
            content: {
                studentId: resolvedStudent?._id || student?._id,
                name: resolvedStudent?.name || 'N/A',
                age,
                readingAge,
                email: resolvedStudent?.email || '',
                sen,
                senConditions,
                hasEal: eal,
                ealRaw: eal,
                eal: eal ? 'Yes' : 'No',
                primaryLanguage: eal ? primaryLanguage : 'N/A',
                glossaryLanguageMode: mode,
                noCustomScaffoldingRequired: resolvedStudent?.noCustomScaffoldingRequired === true,
                audioNarrationEnabled: resolvedStudent?.audioNarrationEnabled === true,
                audioNarrationSpeed: [0.5, 0.75, 1].includes(Number(resolvedStudent?.audioNarrationSpeed)) ? Number(resolvedStudent.audioNarrationSpeed) : 1,
                audioNarration,
                languageDirection
            }
        });
    };

    const handleSaveScaffoldingGlossaryMode = async () => {
        const content = scaffoldingModal?.content;
        if (!content?.studentId) return;

        setSavingScaffolding(true);
        try {
            const payload = {
                age: Number(content.age),
                email: content.email || '',
                readingAge: String(content.readingAge),
                senCondition: (content.senConditions && content.senConditions[0]) || 'None',
                senConditions: content.senConditions || [],
                eal: content.ealRaw === true,
                primaryLanguage: content.ealRaw === true ? content.primaryLanguage : '',
                glossaryLanguageMode: content.ealRaw === true ? (content.glossaryLanguageMode || 'PRIMARY_TEXT_ENGLISH_GLOSSARY') : 'PRIMARY_TEXT_ENGLISH_GLOSSARY',
                audioNarrationEnabled: content.audioNarrationEnabled === true,
                audioNarrationSpeed: [0.5, 0.75, 1].includes(Number(content.audioNarrationSpeed)) ? Number(content.audioNarrationSpeed) : 1,
                noCustomScaffoldingRequired: content.noCustomScaffoldingRequired === true
            };

            const res = await studentAPI.updateScaffolding(content.studentId, payload);
            const updatedStudent = res.data;

            setHomework((prev) => {
                if (!prev?.students) return prev;
                return {
                    ...prev,
                    students: prev.students.map((s) => (
                        String(s._id) === String(updatedStudent._id)
                            ? { ...s, ...updatedStudent }
                            : s
                    ))
                };
            });

            const languageDirection = updatedStudent.eal
                ? (updatedStudent.glossaryLanguageMode === 'ENGLISH_TEXT_PRIMARY_GLOSSARY'
                    ? `English with ${updatedStudent.primaryLanguage} Glossary`
                    : `${updatedStudent.primaryLanguage} with English Glossary`)
                : 'English with English Glossary';

            setScaffoldingModal((prev) => ({
                ...prev,
                content: {
                    ...prev.content,
                    primaryLanguage: updatedStudent.eal ? updatedStudent.primaryLanguage : 'N/A',
                    glossaryLanguageMode: updatedStudent.glossaryLanguageMode,
                    languageDirection
                }
            }));
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update student scaffolding.');
        } finally {
            setSavingScaffolding(false);
        }
    };


    const sortedSourceTags = useMemo(() => {
        if (!homework?.sourceLessonTags) return [];
        return homework.sourceLessonTags
            .filter(tag => tag && typeof tag === 'string' && !tag.toLowerCase().includes('null') && !tag.startsWith('__preview__:'))
            .sort((a, b) => {
                const numA = parseInt(a.replace(/Lesson /i, ''), 10);
                const numB = parseInt(b.replace(/Lesson /i, ''), 10);
                if (isNaN(numA) || isNaN(numB)) return a.localeCompare(b);
                return numA - numB;
            });
     }, [homework]);

    const selectionOptionsByTag = useMemo(() => {
        const sourceTags = (homework?.sourceLessonTags || [])
            .filter(tag => tag && typeof tag === 'string' && !tag.toLowerCase().includes('null') && !tag.startsWith('__preview__:'));

        return sourceTags.reduce((acc, tag) => {
            const normalizedTag = normalizeLessonTag(tag);
            // For OA homework the analysis is keyed as "Lesson 1", not by the lesson title.
            // Fall back to the first available entry when there's no direct match.
            const content = analysisByLessonTag[normalizedTag]
                || (Object.keys(analysisByLessonTag).length > 0 ? Object.values(analysisByLessonTag)[0] : null);
            const objectiveOptions = (Array.isArray(content?.objectives) && content.objectives.length > 0
                ? content.objectives
                : parseFallbackObjectives(content?.learningObjectives || '')
            )
                .slice(0, 4)
                .map(item => ({
                    objective: sanitizeInlineText(item?.objective),
                    misconception: sanitizeInlineText(item?.misconception)
                }))
                .filter(item => item.objective || item.misconception);

            // Merge vocab from three sources (Oak first, then Gemini, then tutorial glossary)
            // deduplicating by term name at each stage.
            const seenTermNames = new Set();
            const mergedVocab = [];

            const addVocabEntry = (formatted) => {
                if (!formatted) return;
                const termName = formatted.split(/\s*[:\-–—]\s*/)[0].toLowerCase().trim();
                if (!seenTermNames.has(termName)) {
                    seenTermNames.add(termName);
                    mergedVocab.push(formatted);
                }
            };

            // 1. Oak keywords (primary source)
            oakKeywords.forEach(k => addVocabEntry(
                formatTier3VocabularyEntry(k.term && k.definition ? `${k.term}: ${k.definition}` : k.term || '')
            ));

            // 2. Gemini tier3Vocabulary from LessonAnalysis
            const geminiRaw = Array.isArray(content?.tier3Vocabulary) && content.tier3Vocabulary.length > 0
                ? content.tier3Vocabulary
                : parseFallbackTier3Vocabulary(content?.learningObjectives || '');
            geminiRaw.forEach(term => addVocabEntry(formatTier3VocabularyEntry(term)));

            // 3. Saved selectedVocabularyByLesson on the homework (copied from system homework in add-to-dashboard)
            if (mergedVocab.length < 8) {
                const savedVocab = homework?.selectedVocabularyByLesson?.[normalizedTag] || homework?.selectedVocabularyByLesson?.[tag] || [];
                savedVocab.forEach(term => addVocabEntry(formatTier3VocabularyEntry(term)));
            }

            // 4. Tutorial glossary terms (fallback when above sources don't fill 8)
            if (mergedVocab.length < 8) {
                const tutorialForTag = studentTutorials.find(st => st.status === 'COMPLETED' && st.generatedContent?.length > 0);
                (tutorialForTag?.generatedContent || []).forEach(chunk => {
                    (chunk.glossary || []).forEach(g => {
                        if (g.term) addVocabEntry(formatTier3VocabularyEntry(
                            g.definition ? `${g.term}: ${g.definition}` : g.term
                        ));
                    });
                });
            }

            const vocabularyOptions = mergedVocab.slice(0, 8);

            const tutorialForFallback = studentTutorials.find(st => st.status === 'COMPLETED' && st.generatedContent?.length > 0);

            const sequencingOptions = content?.sequencing?.steps?.length > 0
                ? content.sequencing
                : ((tutorialForFallback?.generatedContent || []).find(c => c.sequencing?.steps?.length > 0)?.sequencing || null);

            const categorisationOptions = (content?.categorisation && !content.categorisation.isNA && content.categorisation.itemsA?.length > 0)
                ? content.categorisation
                : ((tutorialForFallback?.generatedContent || []).find(c => c.categorisation && !c.categorisation.isNA && c.categorisation.itemsA?.length > 0)?.categorisation || null);

            acc[normalizedTag] = { objectiveOptions, vocabularyOptions, sequencingOptions, categorisationOptions };
            return acc;
        }, {});
    }, [homework, analysisByLessonTag, oakKeywords, studentTutorials]);

    // OA student tutorials don't include seq/cat in their generatedContent.
    // Derive fallbacks from selectionOptionsByTag (which already pulls from lesson analysis + completed tutorials).
    const firstTagOptions = Object.values(selectionOptionsByTag)[0] || {};
    const oaFallbackSequencing = isOakHomework ? (firstTagOptions.sequencingOptions || null) : null;
    const oaFallbackCategorisation = isOakHomework ? (firstTagOptions.categorisationOptions || null) : null;

    const hasAnyCatData = useMemo(() => {
        if (Object.values(selectionOptionsByTag).some(o => o.categorisationOptions?.itemsA?.length > 0)) return true;
        if (libraryPreview || isOakHomework) {
            return studentTutorials.some(st =>
                (st.generatedContent || []).some(chunk => chunk.categorisation?.itemsA?.length > 0)
            );
        }
        return false;
    }, [selectionOptionsByTag, libraryPreview, isOakHomework, studentTutorials]);

    const hasAnySeqData = useMemo(() => {
        if (Object.values(selectionOptionsByTag).some(o => o.sequencingOptions?.steps?.length > 0)) return true;
        if (libraryPreview || isOakHomework) {
            return studentTutorials.some(st =>
                (st.generatedContent || []).some(chunk => chunk.sequencing?.steps?.length > 0)
            );
        }
        return false;
    }, [selectionOptionsByTag, libraryPreview, isOakHomework, studentTutorials]);

    // IDs of worksheet components that are actually available for this homework
    const availableWorksheetIds = useMemo(() => {
        const ids = new Set(['fill_in_gaps', 'mcq', 'key_terms']);
        if (hasAnySeqData) ids.add('sequences');
        if (hasAnyCatData) ids.add('categorisation');
        return ids;
    }, [hasAnySeqData, hasAnyCatData]);

    useEffect(() => {
        if (!homework) return;

        const nextObjectives = {};
        const nextVocabulary = {};
        const nextSequencing = {};
        const nextCategorisation = {};

        sortedSourceTags.forEach(tag => {
            const normalizedTag = normalizeLessonTag(tag);
            const options = selectionOptionsByTag[normalizedTag] || { objectiveOptions: [], vocabularyOptions: [] };

            const savedObjectivesRaw = homework?.selectedObjectivesByLesson?.[normalizedTag] || homework?.selectedObjectivesByLesson?.[tag] || [];
            const savedObjectives = Array.isArray(savedObjectivesRaw)
                ? savedObjectivesRaw
                    .map(item => ({
                        objective: sanitizeInlineText(item?.objective),
                        misconception: sanitizeInlineText(item?.misconception)
                    }))
                    .filter(item => item.objective || item.misconception)
                : [];

            const resolvedObjectives = savedObjectives.length > 0
                ? savedObjectives
                : options.objectiveOptions;

            nextObjectives[normalizedTag] = resolvedObjectives;
            nextVocabulary[normalizedTag] = options.vocabularyOptions;

            if (options.sequencingOptions?.steps?.length > 0) {
                const savedSequencing = homework?.selectedSequencingByLesson?.[normalizedTag] ?? homework?.selectedSequencingByLesson?.[tag];
                nextSequencing[normalizedTag] = savedSequencing !== undefined ? savedSequencing !== false : !_isWilliamFordAccount;
            }
            if (options.categorisationOptions && !options.categorisationOptions.isNA) {
                const savedCategorisation = homework?.selectedCategorisationByLesson?.[normalizedTag] ?? homework?.selectedCategorisationByLesson?.[tag];
                nextCategorisation[normalizedTag] = savedCategorisation !== undefined ? savedCategorisation !== false : !_isWilliamFordAccount;
            }
        });

        setContentSelections({ objectivesByLesson: nextObjectives, vocabularyByLesson: nextVocabulary, sequencingByLesson: nextSequencing, categorisationByLesson: nextCategorisation });
    }, [homework, sortedSourceTags, selectionOptionsByTag, _isWilliamFordAccount]);

    // Wire Tutorial Content <details> toggle to React state
    useEffect(() => {
        const el = detailsRef.current;
        if (!el) return;
        const handler = () => setIsContentExpanded(el.open);
        el.addEventListener('toggle', handler);
        return () => el.removeEventListener('toggle', handler);
    }, []);

    // Auto-expand tutorial rows on library preview — runs only once on first completed load
    useEffect(() => {
        if (!libraryPreview || jcqEmbeddedBuildMode || hasAutoExpandedRef.current) return;
        const completed = studentTutorials.filter(st => st.status === 'COMPLETED');
        if (completed.length === 0) return;
        hasAutoExpandedRef.current = true;
        setExpandedTutorialRows(prev => {
            const next = { ...prev };
            completed.forEach(st => { next[st.studentId] = true; });
            return next;
        });
    }, [libraryPreview, jcqEmbeddedBuildMode, studentTutorials]);

    useEffect(() => {
        if (!libraryPreview || !jcqEmbeddedBuildMode) return;
        const completed = studentTutorials.filter(st => st.status === 'COMPLETED');
        if (completed.length === 0) return;
        setExpandedTutorialRows(prev => {
            const next = { ...prev };
            completed.forEach(st => {
                const studentId = st.studentId?._id || st.studentId;
                if (studentId) next[studentId] = true;
            });
            return next;
        });
    }, [homeworkId, libraryPreview, jcqEmbeddedBuildMode, studentTutorials]);



    const toggleObjectiveSelection = (lessonTag, option) => {
        setContentSelections(prev => {
            const current = prev.objectivesByLesson?.[lessonTag] || [];
            const exists = current.some(item => item?.objective === option?.objective && item?.misconception === option?.misconception);

            let updated = [];
            if (exists) {
                updated = current.length > 1
                    ? current.filter(item => !(item?.objective === option?.objective && item?.misconception === option?.misconception))
                    : current;
            } else {
                updated = [...current, option];
            }

            return {
                ...prev,
                objectivesByLesson: {
                    ...prev.objectivesByLesson,
                    [lessonTag]: updated
                }
            };
        });
    };

    const toggleSequencingInclusion = (lessonTag) => {
        setContentSelections(prev => ({ ...prev, sequencingByLesson: { ...prev.sequencingByLesson, [lessonTag]: !prev.sequencingByLesson?.[lessonTag] } }));
    };

    const toggleCategorisationInclusion = (lessonTag) => {
        setContentSelections(prev => ({ ...prev, categorisationByLesson: { ...prev.categorisationByLesson, [lessonTag]: !prev.categorisationByLesson?.[lessonTag] } }));
    };

    const persistContentSelections = useCallback(async () => {
        try {
            setSavingSelections(true);
            const res = await homeworkAPI.updateContentSelections(homeworkId, {
                selectedObjectivesByLesson: contentSelections.objectivesByLesson,
                selectedVocabularyByLesson: contentSelections.vocabularyByLesson,
                selectedSequencingByLesson: contentSelections.sequencingByLesson,
                selectedCategorisationByLesson: contentSelections.categorisationByLesson
            });
            setHomework(res.data);
        } catch (saveErr) {
            console.error('Failed to save content selections:', saveErr);
            setError('Failed to save learning objective and vocabulary selections.');
        } finally {
            setSavingSelections(false);
        }
    }, [homeworkId, contentSelections]);



    const abbreviateSenLabel = (value = '') => {
        const text = String(value || '').trim();
        if (!text) return 'None';

        const replacements = [
            [/working\s*memory\s*difficulties?/ig, 'Memory'],
            [/attention\s*deficit\s*hyperactivity\s*disorder/ig, 'ADHD'],
            [/autism\s*spectrum\s*disorder/ig, 'ASD'],
            [/oppositional\s*defiant\s*disorder/ig, 'ODD'],
            [/specific\s*learning\s*difficulty/ig, 'SpLD'],
            [/speech\s*and\s*language\s*(needs|difficulties)?/ig, 'S&L'],
            [/social,?\s*emotional\s*and\s*mental\s*health/ig, 'SEMH']
        ];

        let formatted = text;
        replacements.forEach(([pattern, shortLabel]) => {
            formatted = formatted.replace(pattern, shortLabel);
        });

        if (formatted.length > 36) {
            return `${formatted.slice(0, 33)}...`;
        }

        return formatted;
    };

    const getSummaryData = useCallback((apiCallTypes) => {
        const types = Array.isArray(apiCallTypes) ? apiCallTypes : [apiCallTypes];
        let totalCost = 0; let totalInput = 0; let totalOutput = 0; let totalImage = 0; let totalChar = 0; let models = new Map();
        const dataItems = costSummary.breakdown.filter(b => types.includes(b.apiCallType));

        if (dataItems.length === 0) { return { totalCost: 0, totalInput: 0, totalOutput: 0, totalImage: 0, totalChar: 0, models: [] }; }

        for (const item of dataItems) {
            totalCost += item.totalCost;
            for (const modelItem of item.modelBreakdown) {
                const existing = models.get(modelItem.model) || { inputTokens: 0, outputTokens: 0, imageCount: 0, characterCount: 0, count: 0 };
                models.set(modelItem.model, {
                    inputTokens: existing.inputTokens + modelItem.inputTokens,
                    outputTokens: existing.outputTokens + modelItem.outputTokens,
                    imageCount: existing.imageCount + (modelItem.imageCount || 0),
                    characterCount: existing.characterCount + (modelItem.characterCount || 0),
                    count: existing.count + modelItem.count
                });
            }
        }
        const modelArray = [];
        for (const [modelName, data] of models.entries()) {
            totalInput += data.inputTokens; totalOutput += data.outputTokens; totalImage += data.imageCount; totalChar += data.characterCount;
            modelArray.push({ model: modelName, ...data });
        }
        return { totalCost, totalInput, totalOutput, totalImage, totalChar, models: modelArray };
    }, [costSummary]);

    const masterTutorialSummary = useMemo(() => getSummaryData('generateTutorialContent'), [getSummaryData]);
    const studentTutorialSummary = useMemo(() => getSummaryData('generatePersonalizedChunk'), [getSummaryData]);
    const scriptSummary = useMemo(() => getSummaryData('generateStudentSlideshowScript'), [getSummaryData]);
    const imageSummary = useMemo(() => getSummaryData('generateImageFromPrompt'), [getSummaryData]);
    const audioSummary = useMemo(() => getSummaryData('generateAudioFromText'), [getSummaryData]);

    if (loading) return <div style={{padding:'40px', textAlign:'center', fontFamily:'Inter, sans-serif'}}>Loading...</div>;
    if (error && !homework) return <p style={{ color: BRAND.danger, padding: '20px' }}>{error}</p>;

    return (
        <>
        <div style={{
            fontFamily: "'Inter', sans-serif",
            backgroundColor: BRAND.cream,
            minHeight: guideExtendedHeight ? `${guideExtendedHeight}px` : '100vh',
            color: BRAND.charcoal,
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* ... [Rest of JSX unchanged] ... */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');
                    h1, h2, h3, h4 { font-family: 'Nunito', sans-serif; }

                    .pattern-bg {
                        background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                        background-size: 60px 60px;
                        background-position: 0 0, 30px 30px;
                        opacity: 0.1;
                        position: absolute;
                        top: 0; left: 0; right: 0; bottom: 0;
                        z-index: 0;
                        pointer-events: none;
                        mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                        -webkit-mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                    }

                    .dash-card {
                        background: ${BRAND.white};
                        border-radius: 16px;
                        border: 2px solid ${BRAND.charcoal};
                        box-shadow: 4px 4px 0px rgba(45, 64, 66, 0.1);
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                        overflow: hidden;
                    }
                    
                    .btn-base {
                        border: none;
                        border-radius: 10px;
                        padding: 10px 20px;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        cursor: pointer;
                        transition: all 0.1s ease;
                        font-size: 0.95rem;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        border-bottom: 4px solid rgba(0,0,0,0.1); 
                    }
                    .btn-base:active {
                        transform: translateY(2px);
                        border-bottom: 2px solid rgba(0,0,0,0.1);
                        margin-bottom: 2px;
                    }
                    .btn-base:disabled { opacity: 0.6; cursor: not-allowed; }

                    .btn-teal { background-color: ${BRAND.teal}; color: white; }
                    .btn-coral { background-color: ${BRAND.coral}; color: white; }
                    
                    .btn-logout {
                        background-color: white;
                        color: ${BRAND.charcoal};
                        border: 2px solid ${BRAND.charcoal};
                        border-bottom: 4px solid ${BRAND.charcoal};
                        padding: 6px 16px;
                        font-size: 0.85rem;
                    }
                    .btn-logout:active { border-bottom: 2px solid ${BRAND.charcoal}; }
                    .btn-logout:hover { background-color: #F9F9F9; }

                    /* Custom Radio Select */
                    .model-option {
                        display: block; padding: 10px; 
                        border: 2px solid #eee; border-radius: 12px; 
                        cursor: pointer; transition: all 0.2s;
                        background: #F9FAFB;
                        margin-bottom: 8px;
                    }
                    .model-option:hover { border-color: ${BRAND.teal}; background: white; }
                    .model-option.selected { 
                        border-color: ${BRAND.teal}; 
                        background: #F0FDFA; 
                        box-shadow: 0 0 0 2px ${BRAND.teal} inset;
                    }

                    /* CUSTOM SUMMARY & ROTATING ICON */
                    details > summary {
                        list-style: none; /* Hide default marker */
                    }
                    details > summary::-webkit-details-marker {
                        display: none; /* Chrome/Safari */
                    }

                    .summary-with-icon {
                        display: flex;
                        align-items: center;
                        cursor: pointer;
                        outline: none;
                        user-select: none;
                    }

                    .summary-chevron {
                        display: inline-block;
                        margin-right: 12px;
                        transition: transform 0.2s ease;
                        font-size: 0.9em;
                        color: ${BRAND.teal};
                    }

                    details[open] > .summary-with-icon .summary-chevron {
                        transform: rotate(90deg);
                    }

                    /* PULSING LOADING BAR */
                    .progress-bar-container {
                        width: 100%;
                        height: 8px;
                        background-color: #eee;
                        border-radius: 4px;
                        overflow: hidden;
                        margin-top: 10px;
                    }
                    .progress-bar-fill {
                        height: 100%;
                        background-color: ${BRAND.teal};
                        animation: progress-pulse 1.5s infinite ease-in-out;
                        width: 60%; 
                        border-radius: 4px;
                    }
                    @keyframes progress-pulse {
                        0% { width: 40%; transform: translateX(-10%); }
                        50% { width: 70%; transform: translateX(20%); }
                        100% { width: 40%; transform: translateX(100%); }
                    }
                    .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
                    .toggle-switch input { opacity: 0; width: 0; height: 0; }
                    .slider {
                        position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
                        background-color: #E0E0E0; transition: .3s; border-radius: 34px;
                    }
                    .slider:before {
                        position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px;
                        background-color: white; transition: .3s; border-radius: 50%;
                    }
                    input:checked + .slider { background-color: ${BRAND.teal}; }
                    input:checked + .slider:before { transform: translateX(20px); }
                    @media (max-width: 900px) { .user-guide-label { display: none; } }
                `}
            </style>

            <ModelSelectionModal
                isOpen={modalState.isOpen}
                onClose={closeModelModal}
                onConfirm={handleModelConfirm}
                actionName={modalState.actionName}
                showImageOptions={modalState.showImageOptions}
                isDemoMode={IS_DEMO}
            />

            <div className="pattern-bg"></div>

            <div style={{ position: 'relative', zIndex: 1, padding: '40px 20px', maxWidth: '1000px', margin: '0 auto' }}>
                
                {/* HEADER */}
                {!jcqEmbeddedBuildMode && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '30px' }}>
                    {libraryPreview ? (
                        <Link to={`${ns}/content-library`} style={{textDecoration:'none'}}>
                            <button className="btn-base" style={{background:'white', color: BRAND.charcoal, border:`2px solid ${BRAND.charcoal}`}}>
                                ← Content Library
                            </button>
                        </Link>
                    ) : (
                        <Link to={getClassProfilesPath(homework?.homeworkScheduleId)} style={{textDecoration:'none'}}>
                            <button className="btn-base" style={{background:'white', color: BRAND.charcoal, border:`2px solid ${BRAND.charcoal}`}}>
                                ← Back to {ns === '/lite' ? 'Profiles' : 'Students'}
                            </button>
                        </Link>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {!IS_DEMO && (
                                <Link to={`/homework/${homeworkId}/what-if`} style={{ textDecoration: 'none' }}>
                                    <button className="btn-base" style={{background: BRAND.warning, color:'black'}}>
                                        Cost "What-If"
                                    </button>
                                </Link>
                            )}
                            <button onClick={handleDelete} className="btn-base" style={{background:'white', color: BRAND.danger, border:`2px solid ${BRAND.danger}`}}>
                                Delete Tutorial
                            </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label className="user-guide-label" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#2D4042', fontWeight: '800', fontFamily: 'Nunito, sans-serif', margin: 0 }}>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked={guideConfig.isOpen} onChange={(e) => setGuideConfig({ isOpen: e.target.checked, mode: 'manual', autoStep: null })} />
                                    <span className="slider"></span>
                                </label>
                                User Guide
                            </label>
                            <button onClick={onLogout} className="btn-base btn-logout">
                                Log Out
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {!jcqEmbeddedBuildMode && (
                <div style={{marginBottom:'30px'}}>
                    <h1 style={{ marginTop: '0', color: BRAND.charcoal, fontWeight: '800', fontSize: '2.5rem' }}>
                        {(() => {
                            const displayTitle = (libraryPreview && libraryLessonTitle) ? libraryLessonTitle : null;
                            return displayTitle
                                ? `Lesson ${homework?.lessonNumber} : ${displayTitle}`
                                : `Lesson ${homework?.lessonNumber} : Tutorial ${homework?.homeworkNumber}`;
                        })()}
                    </h1>
                </div>
                )}

                {error && <p style={{ color: BRAND.danger, background: '#FFF5F5', padding: '15px', borderRadius: '10px', border:`1px solid ${BRAND.danger}` }}>{error}</p>}

                {/* CLASS REPORT DASHBOARD */}
                {!jcqMode && !jcqEmbeddedBuildMode && !isContentExpanded && (() => {
                    const rd = tutorialReportData || { completedStudents: 0, objectiveScores: [], keyTermScores: [] };

                    // Build fallback rows from contentSelections when API has no data yet
                    const fallbackObjectives = Object.values(contentSelections.objectivesByLesson || {})
                        .flat()
                        .map(obj => ({ objective: obj?.objective || obj, understanding: 0, status: 'NEEDS_RETEACH', assessedStudents: 0, studentBreakdown: [] }))
                        .filter(o => o.objective);
                    const fallbackKeyTerms = Object.values(contentSelections.vocabularyByLesson || {})
                        .flat()
                        .filter(Boolean)
                        .map(term => ({ term, understanding: 0, status: 'NEEDS_RETEACH', assessedStudents: 0, studentBreakdown: [] }));

                    const objectiveScores = rd.objectiveScores?.length > 0 ? rd.objectiveScores : fallbackObjectives;
                    const keyTermScores = rd.keyTermScores?.length > 0 ? rd.keyTermScores : fallbackKeyTerms;
                    const avgObj = objectiveScores.length > 0
                        ? Math.round(objectiveScores.reduce((s, i) => s + (Number(i?.understanding) || 0), 0) / objectiveScores.length)
                        : 0;
                    const avgKey = keyTermScores.length > 0
                        ? Math.round(keyTermScores.reduce((s, i) => s + (Number(i?.understanding) || 0), 0) / keyTermScores.length)
                        : 0;

                    const renderBadge = (status, score) => {
                        const ok = status === 'UNDERSTOOD' || score >= 80;
                        const warn = score >= 50 && score < 80;
                        const bg = ok ? '#D1FAE5' : (warn ? '#FEF3C7' : '#FFEDD5');
                        const color = ok ? '#065F46' : (warn ? '#92400E' : '#9A3412');
                        const text = ok ? 'Understood' : (warn ? 'Reviewing' : 'Needs Reteach');
                        return (
                            <span style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: '800', letterSpacing: '0.4px', color, background: bg, padding: '4px 8px', borderRadius: '999px' }}>
                                {text}
                            </span>
                        );
                    };

                    return (
                        <details open className="dash-card" style={{ padding: '20px 15px', marginBottom: '20px', cursor: 'pointer', background: 'white' }}>
                            <summary className="summary-with-icon" style={{ fontSize: '1.1em', fontWeight: '800', color: BRAND.charcoal, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span className="summary-chevron">▶</span>
                                Class Report
                                {studentTutorials.length > 0 && (
                                    <span style={{ marginLeft: 'auto', fontSize: '0.65em', fontWeight: '800', color: BRAND.teal, background: '#F0FDFA', border: `1px solid ${BRAND.teal}`, padding: '3px 9px', borderRadius: '999px', letterSpacing: '0.5px' }}>
                                        CONTENT LOCKED
                                    </span>
                                )}
                            </summary>

                            <div style={{ marginTop: '20px', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                                    <p style={{ margin: 0, color: '#64748B', fontWeight: '600', fontSize: '0.9em' }}>
                                        Analysis based on <strong>{rd.completedStudents || 0}</strong> completed student tutorials.
                                    </p>
                                </div>

                                {/* Top Summary Cards */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                                    <div style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <div style={{ background: 'white', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', fontSize: '1.5em' }}>🎯</div>
                                        <div>
                                            <div style={{ fontSize: '0.75rem', fontWeight: '800', letterSpacing: '0.6px', textTransform: 'uppercase', color: '#64748B' }}>Core Objectives</div>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                                <div style={{ fontSize: '1.8rem', fontWeight: '800', color: BRAND.charcoal, lineHeight: 1 }}>{avgObj}%</div>
                                                <div style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>avg mastery</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <div style={{ background: 'white', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', fontSize: '1.5em' }}>🔑</div>
                                        <div>
                                            <div style={{ fontSize: '0.75rem', fontWeight: '800', letterSpacing: '0.6px', textTransform: 'uppercase', color: '#64748B' }}>Vocabulary Terms</div>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                                <div style={{ fontSize: '1.8rem', fontWeight: '800', color: BRAND.charcoal, lineHeight: 1 }}>{avgKey}%</div>
                                                <div style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>avg mastery</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                                        <div style={{ background: '#F8FAFC', padding: '10px 12px', fontWeight: '800', color: BRAND.charcoal }}>Learning Objectives</div>
                                        <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                            {objectiveScores.length === 0 ? (
                                                <p style={{ padding: '12px', margin: 0, color: '#64748B', fontStyle: 'italic' }}>No objective data available yet.</p>
                                            ) : objectiveScores.map((item) => {
                                                const isSel = selectedBreakdown?.type === 'objective' && selectedBreakdown?.label === item.objective;
                                                const hasData = item.assessedStudents > 0;
                                                const pct = hasData ? (item.understanding || 0) : 0;
                                                const barColor = !hasData ? BRAND.teal : pct >= 80 ? '#4ADE80' : pct >= 50 ? '#FBBF24' : '#F87171';
                                                const trackColor = !hasData ? '#D0EBEE' : '#F1F5F9';
                                                return (
                                                    <button key={item.objective} type="button" onClick={() => setSelectedBreakdown({ type: 'objective', label: item.objective, students: item.studentBreakdown || [] })}
                                                        style={{ width: '100%', textAlign: 'left', padding: '10px 12px', borderTop: '1px solid #F1F5F9', borderLeft: 'none', borderRight: 'none', borderBottom: 'none', background: isSel ? '#EEF6FF' : 'white', cursor: 'pointer' }}>
                                                        <div style={{ fontWeight: '700', color: BRAND.charcoal }}>{item.objective}</div>
                                                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div style={{ flex: 1, height: '6px', borderRadius: '999px', background: trackColor, overflow: 'hidden' }}>
                                                                <div style={{ width: `${pct}%`, height: '100%', borderRadius: '999px', background: barColor, transition: 'width 0.3s ease' }} />
                                                            </div>
                                                            <span style={{ fontSize: '0.8rem', fontWeight: '800', color: hasData ? '#334155' : BRAND.teal, minWidth: '32px', textAlign: 'right' }}>
                                                                {pct}%
                                                            </span>
                                                        </div>
                                                        <div style={{ marginTop: '4px', color: '#64748B', fontSize: '0.82rem' }}>Assessed students: {item.assessedStudents}</div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ background: '#F8FAFC', padding: '10px 12px', fontWeight: '800', color: BRAND.charcoal }}>Key Terms</div>
                                        <div style={{ padding: '10px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            {keyTermScores.length === 0 ? (
                                                <p style={{ padding: '4px', margin: 0, color: '#64748B', fontStyle: 'italic' }}>No key term data available yet.</p>
                                            ) : (
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridAutoRows: '1fr', gap: '5px', flex: 1 }}>
                                                    {keyTermScores.map((item) => {
                                                        const isSel = selectedBreakdown?.type === 'term' && selectedBreakdown?.label === item.term;
                                                        const hasData = item.assessedStudents > 0;
                                                        const pct = hasData ? (item.understanding || 0) : 0;
                                                        const bg = !hasData ? '#D0EBEE'
                                                            : pct >= 80 ? '#BBFBD0'
                                                            : pct >= 60 ? '#D9F99D'
                                                            : pct >= 40 ? '#FEF9C3'
                                                            : pct >= 20 ? '#FED7AA'
                                                            : '#FECACA';
                                                        const textColor = !hasData ? '#3D7A80'
                                                            : pct >= 80 ? '#14532D'
                                                            : pct >= 60 ? '#365314'
                                                            : pct >= 40 ? '#713F12'
                                                            : pct >= 20 ? '#7C2D12'
                                                            : '#7F1D1D';
                                                        return (
                                                            <button key={item.term} type="button"
                                                                onClick={() => setSelectedBreakdown({ type: 'term', label: item.term, students: item.studentBreakdown || [] })}
                                                                style={{ background: bg, border: isSel ? `2px solid ${BRAND.charcoal}` : '2px solid transparent', borderRadius: '6px', padding: '4px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', boxShadow: isSel ? `0 2px 8px rgba(45,64,66,0.15)` : '0 1px 3px rgba(45,64,66,0.06)' }}>
                                                                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '1.05em', color: textColor, lineHeight: '1.3' }}>{item.term.split(':')[0].trim()}</div>
                                                                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '1.4em', color: textColor }}>{pct}%</div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Deep Dive Breakdown (Bottom Panel) */}
                                <div style={{ marginTop: '16px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px' }}>
                                {!selectedBreakdown ? (
                                    <p style={{ margin: 0, color: '#64748B', fontStyle: 'italic' }}>Click a learning objective or key term to see student breakdown.</p>
                                ) : (
                                    <div style={{ background: 'white', border: `2px solid ${BRAND.teal}`, borderRadius: '12px', padding: '16px', position: 'relative', overflow: 'hidden' }}>
                                        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '6px', background: BRAND.teal }} />

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: '10px' }}>
                                            <div>
                                                <div style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                    {selectedBreakdown.type === 'objective' ? 'Learning objective' : 'Key term'} deep dive
                                                </div>
                                                <div style={{ marginTop: '4px', fontWeight: '800', color: BRAND.charcoal, fontSize: '1.1em' }}>{selectedBreakdown.label}</div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: '800', textTransform: 'uppercase' }}>Students Assessed</div>
                                                    <div style={{ fontWeight: '800', color: BRAND.charcoal, fontSize: '1.1em' }}>{Array.isArray(selectedBreakdown.students) ? selectedBreakdown.students.length : 0}</div>
                                                </div>
                                                <button type="button" onClick={() => setSelectedBreakdown(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '1.2em', lineHeight: 1, padding: '2px', fontWeight: '700' }}>✕</button>
                                            </div>
                                        </div>

                                        <div style={{ marginTop: '16px', paddingLeft: '10px' }}>
                                            {(!Array.isArray(selectedBreakdown.students) || selectedBreakdown.students.length === 0) ? (
                                                <p style={{ margin: 0, color: '#64748B', fontStyle: 'italic', fontSize: '0.9em' }}>No student attempts recorded yet for this item.</p>
                                            ) : (
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                                                    {selectedBreakdown.students.map((student) => (
                                                        <div key={`${selectedBreakdown.label}-${student.studentId || student.studentName}`} style={{ padding: '10px 14px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span style={{ fontWeight: '700', color: BRAND.charcoal, fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{student.studentName || 'Unknown Student'}</span>
                                                            <span style={{ fontWeight: '800', fontSize: '0.9em', color: student.passed ? '#065F46' : '#9A3412', background: student.passed ? '#D1FAE5' : '#FFEDD5', padding: '2px 8px', borderRadius: '999px' }}>
                                                                {student.score}%
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                </div>
                            </div>
                        </details>
                    );
                })()}

                {/* COST SUMMARY ROW (CONDITIONAL) */}
                {!IS_DEMO && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '40px' }}>
                        <CostSummaryBox title="Master Tutorial Costs" summary={masterTutorialSummary} />
                        <CostSummaryBox title="Student Tutorials Costs" summary={studentTutorialSummary} />
                        <SlideshowCostSummaryBox 
                            script={scriptSummary} 
                            image={imageSummary} 
                            audio={audioSummary} 
                        />
                    </div>
                )}

                {jcqEmbeddedBuildMode && (
                    <div className="dash-card" style={{ padding: '20px 15px', marginBottom: '20px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                        <div className="summary-with-icon" style={{ fontSize: '1.1em', fontWeight: '800', color: BRAND.charcoal, display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span className="summary-chevron" style={{ visibility: 'hidden' }}>▶</span>
                            Test Length
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: BRAND.charcoal, fontWeight: '800' }}>
                                <input
                                    type="number"
                                    min="5"
                                    max="180"
                                    step="5"
                                    value={testLengthMinutes}
                                    onChange={(e) => handleTestLengthChange(e.target.value)}
                                    style={{ width: '72px', border: '1px solid #CBD5E1', borderRadius: '8px', padding: '7px 8px', fontWeight: '800', color: BRAND.charcoal }}
                                />
                                minutes
                            </label>
                            <span style={{ fontSize: '0.82rem', fontWeight: '900', color: BRAND.teal, background: '#F0FDFA', border: `1px solid ${BRAND.teal}`, padding: '6px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                                +25% extra time: {Math.ceil(testLengthMinutes * 1.25)} minutes
                            </span>
                        </div>
                    </div>
                )}

                {/* TUTORIAL COMPOSITION TOGGLE */}
                <details ref={detailsRef} data-guide="hd-composition" className="dash-card" style={{ padding: '20px 15px', marginBottom: '20px', cursor:'pointer', background:'white' }}>
                     <summary className="summary-with-icon" style={{ fontSize: '1.1em', fontWeight: '800', color: BRAND.charcoal }}>
                         <span className="summary-chevron">▶</span>
                         {jcqEmbeddedBuildMode ? 'Test Content' : 'Tutorial Content'}
                     </summary>
                     <div style={{ marginTop: '15px', display: 'grid', gap: '14px' }}>

                         {sortedSourceTags.map(tag => {
                             const normalizedTag = normalizeLessonTag(tag);
                             const options = selectionOptionsByTag[normalizedTag] || { objectiveOptions: [], vocabularyOptions: [], sequencingOptions: null, categorisationOptions: null };
                             const selectedObjectives = contentSelections.objectivesByLesson?.[normalizedTag] || [];

                             return (
                                 <div key={tag} className="dash-card" style={{ padding:'18px', background: 'white', border: '1px solid #E5E7EB', boxShadow: 'none' }}>

                                     <div style={{ marginBottom: '14px' }}>
                                         <div style={{ fontWeight: '800', fontSize: '0.82em', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '10px', color: '#5D7375' }}>{jcqEmbeddedBuildMode ? 'Test Content' : 'Tutorial Content'} <span style={{ fontWeight: '600', fontSize: '0.85em', textTransform: 'none', letterSpacing: '0', color: '#94A3B8' }}>(mapped to National Curriculum 2014)</span></div>
                                         {options.objectiveOptions.length > 0 ? options.objectiveOptions.map((item, idx) => {
                                             const checked = selectedObjectives.some(opt => opt?.objective === item?.objective && opt?.misconception === item?.misconception);
                                             return (
                                                 <label key={`${normalizedTag}-obj-${idx}`} style={{ display: 'block', marginBottom: '10px', cursor: 'pointer' }}>
                                                     <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', background: checked ? '#F0FDFA' : '#F9FAFB', border: checked ? `1px solid ${BRAND.teal}` : '1px solid #E5E7EB', borderRadius: '10px', padding: '10px 12px' }}>
                                                         <input
                                                             type="checkbox"
                                                             checked={checked}
                                                             onChange={() => toggleObjectiveSelection(normalizedTag, item)}
                                                             disabled={areObjectiveSelectionsLocked}
                                                             style={{ marginTop: '3px' }}
                                                         />
                                                         <div>
                                                             <div style={{ fontWeight: '700', color: BRAND.charcoal, lineHeight: 1.4 }}>{item.objective || 'Objective not provided.'}</div>
                                                             <div style={{ marginTop: '4px', color: '#64748B', fontSize: '0.84em', lineHeight: 1.4 }}><strong>Misconception:</strong> {sanitizeInlineText(item.misconception) || 'Not provided.'}</div>
                                                         </div>
                                                     </div>
                                                 </label>
                                             );
                                         }) : <p style={{ margin: 0, color: '#666', fontStyle: 'italic' }}>No parsed objectives available.</p>}
                                     </div>

                                     <div style={{ marginBottom: '14px' }}>
                                         {(() => {
                                             const vocabTerms = contentSelections.vocabularyByLesson?.[normalizedTag] || [];
                                             const atMax = vocabTerms.length >= 8;
                                             const atMin = vocabTerms.length <= 4;
                                             const locked = areObjectiveSelectionsLocked;
                                             return (<>
                                         <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                             <div style={{ fontWeight: '800', fontSize: '0.82em', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#5D7375' }}>Tier 3 Vocabulary</div>
                                             {addingVocabTag !== normalizedTag && !atMax && !locked && (
                                                 <button
                                                     onClick={() => setAddingVocabTag(normalizedTag)}
                                                     style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', borderRadius: '999px', padding: '3px 9px', fontSize: '0.75em', fontWeight: '700', background: 'white', color: BRAND.teal, border: `1.5px dashed ${BRAND.teal}`, cursor: 'pointer' }}>
                                                     + Add term
                                                 </button>
                                             )}
                                             {atMax && <span style={{ fontSize: '0.72em', color: '#9CA3AF', fontStyle: 'italic' }}>Max 8 terms</span>}
                                         </div>
                                         <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                             {vocabTerms.map((term, idx) => (
                                                 editingVocab?.tag === normalizedTag && editingVocab?.index === idx ? (
                                                     <input
                                                         key={`${normalizedTag}-edit-${idx}`}
                                                         autoFocus
                                                         value={editingVocab.value}
                                                         onChange={e => setEditingVocab(prev => ({ ...prev, value: e.target.value }))}
                                                         onBlur={() => {
                                                             const trimmed = editingVocab.value.trim();
                                                             if (trimmed) {
                                                                 setContentSelections(prev => {
                                                                     const terms = [...(prev.vocabularyByLesson?.[normalizedTag] || [])];
                                                                     terms[idx] = trimmed;
                                                                     return { ...prev, vocabularyByLesson: { ...prev.vocabularyByLesson, [normalizedTag]: terms } };
                                                                 });
                                                             }
                                                             setEditingVocab(null);
                                                         }}
                                                         onKeyDown={e => {
                                                             if (e.key === 'Enter') e.target.blur();
                                                             if (e.key === 'Escape') setEditingVocab(null);
                                                         }}
                                                         style={{ borderRadius: '999px', padding: '6px 12px', fontSize: '0.86em', fontWeight: '700', border: `2px solid ${BRAND.teal}`, outline: 'none', width: `${Math.max(editingVocab.value.length, 4) + 2}ch` }}
                                                     />
                                                 ) : (
                                                     <span key={`${normalizedTag}-${term}-${idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', borderRadius: '999px', padding: '7px 12px', fontSize: '0.86em', fontWeight: '700', background: '#F3F4F6', color: BRAND.charcoal, border: '1px solid #E5E7EB', cursor: locked ? 'default' : 'text' }}
                                                         onClick={() => !locked && setEditingVocab({ tag: normalizedTag, index: idx, value: term })}>
                                                         {term}
                                                         {!atMin && !locked && <button
                                                             onClick={e => { e.stopPropagation(); setContentSelections(prev => { const terms = (prev.vocabularyByLesson?.[normalizedTag] || []).filter((_, i) => i !== idx); return { ...prev, vocabularyByLesson: { ...prev.vocabularyByLesson, [normalizedTag]: terms } }; }); }}
                                                             style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', lineHeight: 1, color: '#9CA3AF', fontSize: '1em', fontWeight: '900' }}>×</button>}
                                                     </span>
                                                 )
                                             ))}
                                             {addingVocabTag === normalizedTag && !areObjectiveSelectionsLocked && (
                                                 <input
                                                     autoFocus
                                                     value={newVocabValue}
                                                     onChange={e => setNewVocabValue(e.target.value)}
                                                     placeholder="New term..."
                                                     onBlur={() => {
                                                         const trimmed = newVocabValue.trim();
                                                         if (trimmed) {
                                                             setContentSelections(prev => {
                                                                 const terms = [...(prev.vocabularyByLesson?.[normalizedTag] || []), trimmed];
                                                                 return { ...prev, vocabularyByLesson: { ...prev.vocabularyByLesson, [normalizedTag]: terms } };
                                                             });
                                                         }
                                                         setAddingVocabTag(null);
                                                         setNewVocabValue('');
                                                     }}
                                                     onKeyDown={e => {
                                                         if (e.key === 'Enter') e.target.blur();
                                                         if (e.key === 'Escape') { setAddingVocabTag(null); setNewVocabValue(''); }
                                                     }}
                                                     style={{ borderRadius: '999px', padding: '6px 12px', fontSize: '0.86em', fontWeight: '700', border: `2px solid ${BRAND.teal}`, outline: 'none', width: `${Math.max(newVocabValue.length, 8) + 2}ch` }}
                                                 />
                                             )}
                                         </div>
                                             </>);
                                         })()}
                                     </div>

                                     {options.sequencingOptions && (() => {
                                         const seqIncluded = contentSelections.sequencingByLesson?.[normalizedTag] ?? true;
                                         return (
                                             <div style={{ marginBottom: '14px' }}>
                                                 <div style={{ fontWeight: '800', fontSize: '0.82em', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '10px', color: '#5D7375' }}>Sequencing</div>
                                                 <div style={{ background: seqIncluded ? '#F0FDFA' : '#F9FAFB', border: `1px solid ${seqIncluded ? BRAND.teal : '#E5E7EB'}`, borderRadius: '8px', padding: '10px 12px', opacity: seqIncluded ? 1 : 0.55 }}>
                                                     <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: options.sequencingOptions.title || options.sequencingOptions.steps.length ? '8px' : 0 }}>
                                                         <input type="checkbox" checked={seqIncluded} onChange={() => toggleSequencingInclusion(normalizedTag)} disabled={areObjectiveSelectionsLocked} />
                                                         {options.sequencingOptions.title
                                                             ? <span style={{ fontWeight: '700', fontSize: '0.85em', color: BRAND.teal }}>{options.sequencingOptions.title}</span>
                                                             : <span style={{ fontWeight: '700', fontSize: '0.85em', color: BRAND.teal }}>Sequence</span>}
                                                     </label>
                                                     <ol style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                         {options.sequencingOptions.steps.map((step, idx) => (
                                                             <li key={`${normalizedTag}-seq-${idx}`} style={{ fontSize: '0.85em', color: BRAND.charcoal, lineHeight: 1.4 }}>{step}</li>
                                                         ))}
                                                     </ol>
                                                 </div>
                                             </div>
                                         );
                                     })()}

                                     {options.categorisationOptions && (() => {
                                         const catIncluded = contentSelections.categorisationByLesson?.[normalizedTag] ?? true;
                                         return (
                                             <div>
                                                 <div style={{ fontWeight: '800', fontSize: '0.82em', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '10px', color: '#5D7375' }}>Categorisation</div>
                                                 <div style={{ background: catIncluded ? '#F0FDFA' : '#F9FAFB', border: `1px solid ${catIncluded ? BRAND.teal : '#E5E7EB'}`, borderRadius: '8px', padding: '10px 12px', opacity: catIncluded ? 1 : 0.55 }}>
                                                     <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '10px' }}>
                                                         <input type="checkbox" checked={catIncluded} onChange={() => toggleCategorisationInclusion(normalizedTag)} disabled={areObjectiveSelectionsLocked} />
                                                         <span style={{ fontWeight: '700', fontSize: '0.85em', color: BRAND.teal }}>Include Categorisation</span>
                                                     </label>
                                                     <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                         {[
                                                             { label: options.categorisationOptions.categoryA, items: options.categorisationOptions.itemsA },
                                                             { label: options.categorisationOptions.categoryB, items: options.categorisationOptions.itemsB }
                                                         ].map((cat, ci) => (
                                                             <div key={`${normalizedTag}-cat-${ci}`} style={{ background: 'white', border: '1px solid #BDE0FE', borderRadius: '6px', padding: '8px 10px' }}>
                                                                 <div style={{ fontWeight: '700', fontSize: '0.85em', color: BRAND.teal, marginBottom: '6px' }}>{cat.label || `Category ${ci === 0 ? 'A' : 'B'}`}</div>
                                                                 <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                     {(cat.items || []).map((item, ii) => (
                                                                         <li key={ii} style={{ fontSize: '0.85em', color: BRAND.charcoal, lineHeight: 1.4 }}>{item}</li>
                                                                     ))}
                                                                 </ul>
                                                             </div>
                                                         ))}
                                                     </div>
                                                 </div>
                                             </div>
                                         );
                                     })()}
                                 </div>
                             );
                         })}
                     </div>
                </details>

                {/* TUTORIAL STRUCTURE TOGGLE */}
                {(() => {
                    const generatedPreviewChunk = studentTutorials[0]?.generatedContent?.[0] || {};
                    const previewChunk = buildStructurePreviewChunk({
                        contentSelections,
                        sortedSourceTags,
                        fallbackChunk: generatedPreviewChunk
                    }) || {};
                    const previewSlideshows = studentTutorials[0]
                        ? studentSlideshows.filter(ss => ss.studentTutorialId?.toString() === studentTutorials[0]._id?.toString())
                        : [];
                    return (
                <>
                <details key={`structure-${homeworkId}-${jcqEmbeddedBuildMode ? 'build' : 'standard'}`} defaultOpen={jcqEmbeddedBuildMode} className="dash-card" style={{ padding: '20px 15px', marginBottom: '20px', cursor:'pointer', background:'white' }}>
                     <summary className="summary-with-icon" style={{ fontSize: '1.1em', fontWeight: '800', color: BRAND.charcoal }}>
                         <span className="summary-chevron">▶</span>
                         {jcqEmbeddedBuildMode ? 'Test Structure' : 'Tutorial Structure'}
                     </summary>
                     <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>

                         {/* Slideshow */}
                         <div
                             onMouseEnter={() => setHoveredStructure('slideshow')}
                             onMouseLeave={() => setHoveredStructure(null)}
                             onMouseMove={e => setStructureHoverPos({ x: e.clientX, y: e.clientY })}
                         >
                         <div style={{ background: '#FCFCFC', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                 <div>
                                     <div style={{ fontWeight: '800', color: BRAND.charcoal, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                         <span style={{ fontSize: '1.2rem' }}>🖼️</span> Slideshow
                                     </div>
                                     <div style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '4px', lineHeight: 1.4 }}>Visual presentation with generated images and audio narration.</div>
                                 </div>
                                 <label className="toggle-switch">
                                     <input type="checkbox" checked={structureConfig.slideshow} onChange={() => handleStructureToggle('slideshow')} />
                                     <span className="slider"></span>
                                 </label>
                             </div>
                         </div>
                         </div>

                         {/* Narrative Explainer */}
                         <div
                             onMouseEnter={() => setHoveredStructure(structureConfig.fillInGaps ? 'fill_in_gaps' : 'narrative')}
                             onMouseLeave={() => setHoveredStructure(null)}
                             onMouseMove={e => setStructureHoverPos({ x: e.clientX, y: e.clientY })}
                         >
                         <div style={{ background: structureConfig.fillInGaps ? '#FFF7ED' : '#F0FDFA', border: `1px solid ${structureConfig.fillInGaps ? BRAND.coral : BRAND.teal}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                 <div>
                                     <div style={{ fontWeight: '800', color: BRAND.charcoal, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                         <span style={{ fontSize: '1.2rem' }}>{structureConfig.fillInGaps ? '✏️' : '📝'}</span> {structureConfig.fillInGaps ? 'Narrative Explainer + Fill in the Gaps' : 'Narrative Explainer'}
                                     </div>
                                     <div style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '4px', lineHeight: 1.4 }}>{structureConfig.fillInGaps ? 'Text explanation with vocabulary gap-fill task.' : 'Text-based core concept explanations.'}</div>
                                 </div>
                                 <label className="toggle-switch">
                                     <input type="checkbox" checked={structureConfig.narrativeExplainer} onChange={() => handleStructureToggle('narrativeExplainer')} />
                                     <span className="slider"></span>
                                 </label>
                             </div>
                             {structureConfig.narrativeExplainer && (
                                 <div
                                     onMouseEnter={() => setHoveredStructure(structureConfig.fillInGaps ? 'fill_in_gaps' : 'narrative')}
                                     onMouseMove={e => setStructureHoverPos({ x: e.clientX, y: e.clientY })}
                                     style={{ marginTop: '12px', padding: '10px 12px 0', borderTop: `1px dashed ${structureConfig.fillInGaps ? BRAND.coral : '#E5E7EB'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: structureConfig.fillInGaps ? 'rgba(242,132,130,0.07)' : 'transparent', borderRadius: structureConfig.fillInGaps ? '0 0 8px 8px' : 0 }}
                                 >
                                     <span style={{ fontSize: '0.9rem', color: BRAND.charcoal, fontWeight: '800' }}>{structureConfig.fillInGaps ? '✏️ ' : ''}Make Fill-in-the-gaps</span>
                                     <label className="toggle-switch" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
                                         <input type="checkbox" checked={structureConfig.fillInGaps} onChange={() => handleStructureToggle('fillInGaps')} />
                                         <span className="slider"></span>
                                     </label>
                                 </div>
                             )}
                         </div>
                         </div>

                         {/* Key Terms */}
                         <div
                             onMouseEnter={() => setHoveredStructure('keyterms')}
                             onMouseLeave={() => setHoveredStructure(null)}
                             onMouseMove={e => setStructureHoverPos({ x: e.clientX, y: e.clientY })}
                         >
                         <div style={{ background: '#F0FDFA', border: `1px solid ${BRAND.teal}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                 <div>
                                     <div style={{ fontWeight: '800', color: BRAND.charcoal, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                         <span style={{ fontSize: '1.2rem' }}>🔑</span> Key-Terms Matching
                                     </div>
                                     <div style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '4px', lineHeight: 1.4 }}>Vocabulary pairing exercises.</div>
                                 </div>
                                 <span style={{ fontSize: '0.75rem', fontWeight: '800', background: BRAND.teal, color: 'white', padding: '4px 8px', borderRadius: '6px', letterSpacing: '0.5px' }}>ALWAYS ON</span>
                             </div>
                         </div>
                         </div>

                         {/* MCQs */}
                         <div
                             onMouseEnter={() => setHoveredStructure('mcq')}
                             onMouseLeave={() => setHoveredStructure(null)}
                             onMouseMove={e => setStructureHoverPos({ x: e.clientX, y: e.clientY })}
                         >
                         <div style={{ background: '#F0FDFA', border: `1px solid ${BRAND.teal}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                 <div>
                                     <div style={{ fontWeight: '800', color: BRAND.charcoal, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                         <span style={{ fontSize: '1.2rem' }}>✅</span> Multiple Choice
                                     </div>
                                     <div style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '4px', lineHeight: 1.4 }}>Core knowledge assessment.</div>
                                 </div>
                                 <span style={{ fontSize: '0.75rem', fontWeight: '800', background: BRAND.teal, color: 'white', padding: '4px 8px', borderRadius: '6px', letterSpacing: '0.5px' }}>ALWAYS ON</span>
                             </div>
                         </div>
                         </div>

                         {/* Categorisation — only shown if lesson analysis found categorisation data */}
                         {hasAnyCatData && (
                         <div
                             onMouseEnter={() => setHoveredStructure('categorisation')}
                             onMouseLeave={() => setHoveredStructure(null)}
                             onMouseMove={e => setStructureHoverPos({ x: e.clientX, y: e.clientY })}
                         >
                         <div style={{ background: '#FCFCFC', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                 <div>
                                     <div style={{ fontWeight: '800', color: BRAND.charcoal, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                         <span style={{ fontSize: '1.2rem' }}>🗂️</span> Categorisation
                                     </div>
                                     <div style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '4px', lineHeight: 1.4 }}>Sorting concepts into groups.</div>
                                 </div>
                                 <label className="toggle-switch">
                                     <input type="checkbox" checked={structureConfig.categorisation} onChange={() => handleStructureToggle('categorisation')} />
                                     <span className="slider"></span>
                                 </label>
                             </div>
                         </div>
                         </div>
                         )}

                         {/* Sequences — only shown if lesson analysis found sequencing data */}
                         {hasAnySeqData && (
                         <div
                             onMouseEnter={() => setHoveredStructure('sequences')}
                             onMouseLeave={() => setHoveredStructure(null)}
                             onMouseMove={e => setStructureHoverPos({ x: e.clientX, y: e.clientY })}
                         >
                         <div style={{ background: '#FCFCFC', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                 <div>
                                     <div style={{ fontWeight: '800', color: BRAND.charcoal, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                         <span style={{ fontSize: '1.2rem' }}>🔢</span> Sequences
                                     </div>
                                     <div style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '4px', lineHeight: 1.4 }}>Ordering steps or events chronologically.</div>
                                 </div>
                                 <label className="toggle-switch">
                                     <input type="checkbox" checked={structureConfig.sequences} onChange={() => handleStructureToggle('sequences')} />
                                     <span className="slider"></span>
                                 </label>
                             </div>
                         </div>
                         </div>
                         )}

                         {/* Score Visibility */}
                         <div
                             onMouseEnter={() => setHoveredStructure('score')}
                             onMouseLeave={() => setHoveredStructure(null)}
                             onMouseMove={e => setStructureHoverPos({ x: e.clientX, y: e.clientY })}
                         >
                         <div style={{ background: '#FFF8E1', border: '1px solid #F2CC8F', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                 <div>
                                     <div style={{ fontWeight: '800', color: BRAND.charcoal, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                         <span style={{ fontSize: '1.2rem' }}>🏆</span> Show Scores
                                     </div>
                                     <div style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '4px', lineHeight: 1.4 }}>Can students see their final performance?</div>
                                 </div>
                                 <label className="toggle-switch">
                                     <input type="checkbox" checked={structureConfig.showScore} onChange={() => handleStructureToggle('showScore')} />
                                     <span className="slider"></span>
                                 </label>
                             </div>
                         </div>
                         </div>

                     </div>
                </details>
                {hoveredStructure && (
                    <div style={{
                        position: 'fixed',
                        left: structureHoverPos.x + 16,
                        top: structureHoverPos.y + 16,
                        zIndex: 9999,
                        width: '420px',
                        background: 'white',
                        borderRadius: '18px',
                        border: `2px solid ${BRAND.charcoal}`,
                        boxShadow: '8px 8px 0 rgba(45,64,66,0.18)',
                        padding: '12px',
                        pointerEvents: 'none',
                        transform: structureHoverPos.x + 436 > window.innerWidth ? 'translateX(calc(-100% - 32px))' : 'none',
                    }}>
                        <StructurePreview componentId={hoveredStructure} chunk={previewChunk} slideshows={previewSlideshows} />
                    </div>
                )}
                </>
                    );
                })()}

                {/* WORKSHEET DESIGN TOGGLE */}
                {!jcqEmbeddedBuildMode && (
                <details className="dash-card" style={{ padding: '20px 15px', marginBottom: '20px', cursor: 'pointer', background: 'white' }}>
                    <summary className="summary-with-icon" style={{ fontSize: '1.1em', fontWeight: '800', color: BRAND.charcoal }}>
                        <span className="summary-chevron">▶</span>
                        Worksheet Design
                    </summary>
                    <div style={{ marginTop: '15px' }} onClick={e => e.stopPropagation()}>
                        {!areWorksheetComponentsAvailable ? (
                            <p style={{ margin: 0, color: '#64748B', fontStyle: 'italic', fontSize: '0.9em' }}>
                                Worksheet components will appear here once tutorials have been generated.
                            </p>
                        ) : (() => {
                            // A4: 210×297mm. Display width 175px → PAGE_H = 175*(297/210) = 248px
                            // Inner height: 248 - 16(v-padding) - 15(page label) = 217px
                            // Bottom margin: 20mm/297mm * 248px ≈ 17px — reduces usable content height to 200px
                            // Top margin already represented by padding+label (~23px from top of page)
                            // WORKING_H = 200px: content fills to here, then 17px visible bottom margin, then 8px padding
                            const PAGE_W = 175, PAGE_H = 248, WORKING_H = 200, HEADER_H = 25, GAP = 4;
                            const COMP_META = {
                                // Fill in the Gaps: sentence lines with word-blocks and underline gaps
                                fill_in_gaps: { height: 48, color: '#FFFBEB', border: '#F2CC8F', preview: () => (
                                    <div>
                                        {[[[8,'w'],[14,'g'],[10,'w'],[7,'w']],[[9,'w'],[6,'w'],[16,'g'],[8,'w']],[[13,'g'],[11,'w'],[7,'w'],[12,'g']]].map((line,i)=>(
                                            <div key={i} style={{display:'flex',alignItems:'flex-end',gap:'2px',marginBottom:'3px'}}>
                                                {line.map(([sz,t],j)=> t==='g'
                                                    ? <div key={j} style={{width:sz+'px',borderBottom:'1.5px solid #C9870A',height:'6px',flexShrink:0}}/>
                                                    : <div key={j} style={{height:'4px',width:sz+'px',background:'#F0C84A',borderRadius:'1px',marginBottom:'1px',flexShrink:0}}/>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )},
                                // Sequences: numbered items in shuffled order (shows it's a sorting task)
                                sequences: { height: 52, color: '#F5F3FF', border: '#A78BFA', preview: () => (
                                    <div>
                                        {[[3,72],[1,85],[4,60],[2,78],[5,68]].map(([n,w],i)=>(
                                            <div key={i} style={{display:'flex',alignItems:'center',gap:'3px',marginBottom:'2px'}}>
                                                <div style={{width:'8px',height:'8px',border:'1.5px solid #A78BFA',borderRadius:'2px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,background:'#EDE9FE'}}>
                                                    <span style={{fontSize:'5px',fontWeight:'900',color:'#7C3AED',lineHeight:1}}>{n}</span>
                                                </div>
                                                <div style={{height:'4px',background:'#C4B5FD',borderRadius:'1px',width:w+'%'}}/>
                                            </div>
                                        ))}
                                    </div>
                                )},
                                // Categorisation: two labelled columns with items sorted beneath
                                categorisation: { height: 56, color: '#F0FDF4', border: '#4ADE80', preview: () => (
                                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'2px 5px'}}>
                                        <div style={{height:'6px',background:'#22C55E',borderRadius:'2px',marginBottom:'1px'}}/>
                                        <div style={{height:'6px',background:'#86EFAC',borderRadius:'2px',marginBottom:'1px'}}/>
                                        {[70,65,80,75,60,72].map((w,i)=>(
                                            <div key={i} style={{height:'4px',background:'#BBF7D0',borderRadius:'1px',width:w+'%',marginBottom:'2px'}}/>
                                        ))}
                                    </div>
                                )},
                                // MCQ: question stems with A/B/C/D radio options
                                mcq: { height: 115, color: '#FFF3F3', border: BRAND.coral, preview: () => (
                                    <div>
                                        {[[88,78,65,82,70],[82,72,80,60,75],[90,68,76,84,62]].map((ws,q)=>(
                                            <div key={q} style={{marginBottom: q<2 ? '5px' : '0'}}>
                                                <div style={{height:'4px',background:'#F4A9A8',borderRadius:'1px',width:ws[0]+'%',marginBottom:'3px'}}/>
                                                {[1,2,3,4].map(o=>(
                                                    <div key={o} style={{display:'flex',alignItems:'center',gap:'3px',marginBottom:'2px'}}>
                                                        <div style={{width:'5px',height:'5px',borderRadius:'50%',border:'1.5px solid #F28482',flexShrink:0,background:'white'}}/>
                                                        <div style={{height:'3px',background:'#FAD9D8',borderRadius:'1px',width:ws[o]+'%'}}/>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )},
                                // Key Terms Matching: terms on left, dashed connector, definitions on right
                                key_terms: { height: 108, color: '#EAF6F7', border: BRAND.teal, preview: () => (
                                    <div>
                                        {[[42,68],[38,74],[45,62],[40,70],[36,76],[43,65],[41,71],[37,69]].map(([tw,dw],i)=>(
                                            <div key={i} style={{display:'flex',alignItems:'center',gap:'3px',marginBottom:'4px'}}>
                                                <div style={{height:'4px',width:tw+'%',background:'#2DD4BF',borderRadius:'1px',flexShrink:0}}/>
                                                <div style={{width:'8px',borderTop:'1px dashed #94A3B8',flexShrink:0}}/>
                                                <div style={{height:'4px',flex:1,background:'#A5F3FC',borderRadius:'1px'}}/>
                                            </div>
                                        ))}
                                    </div>
                                )},
                            };

                            const enabledComps = worksheetComponents.filter(c => c.enabled && availableWorksheetIds.has(c.id));
                            const disabledComps = worksheetComponents.filter(c => !c.enabled && availableWorksheetIds.has(c.id));

                            const doInsert = (dragId, beforeId) => {
                                setWorksheetDragIndex(null); setWorksheetDragOverIndex(null);
                                setWorksheetComponents(prev => {
                                    const dragComp = { ...prev.find(c => c.id === dragId), enabled: true };
                                    const rest = prev.filter(c => c.id !== dragId);
                                    if (beforeId === '__end__') return [...rest, dragComp];
                                    const idx = rest.findIndex(c => c.id === beforeId);
                                    if (idx === -1) return [...rest, dragComp];
                                    return [...rest.slice(0, idx), dragComp, ...rest.slice(idx)];
                                });
                            };

                            const doRemove = (compId) => {
                                setWorksheetComponents(prev => prev.map(c => c.id === compId ? { ...c, enabled: false } : c));
                            };

                            // Build pages with genuine cross-page splits.
                            // Each page is an array of segments: { comp, showHeight, clipTop, isContinuation, isContinued }
                            const pages = [[]];
                            let usedH = HEADER_H + GAP; // page 1 starts with header
                            enabledComps.forEach(comp => {
                                const totalH = COMP_META[comp.id]?.height || 52;
                                let remaining = totalH;
                                let clipTop = 0;
                                while (remaining > 0) {
                                    let available = WORKING_H - usedH;
                                    // If barely any space left (< 12px), start a new page
                                    if (available < 12) {
                                        pages.push([]);
                                        usedH = 0;
                                        available = WORKING_H;
                                    }
                                    const showH = Math.min(remaining, available);
                                    const isContinued = showH < remaining;
                                    pages[pages.length - 1].push({ comp, showHeight: showH, clipTop, isContinuation: clipTop > 0, isContinued });
                                    usedH += showH + (isContinued ? 0 : GAP);
                                    remaining -= showH;
                                    clipTop += showH;
                                    if (remaining > 0) { pages.push([]); usedH = 0; }
                                }
                            });

                            // calcInsertId: ignores continuation segments (can't insert mid-split)
                            const calcInsertId = (e, pageSegs, pIdx) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const relY = e.clientY - rect.top - 8;
                                let y = 15;
                                if (pIdx === 0) y += HEADER_H + GAP;
                                for (let i = 0; i < pageSegs.length; i++) {
                                    const seg = pageSegs[i];
                                    const segH = seg.showHeight + GAP;
                                    if (!seg.isContinuation && relY < y + seg.showHeight / 2) return seg.comp.id;
                                    y += segH;
                                }
                                return `__page_${pIdx}_end__`;
                            };

                            const mkDropBar = (dropId) => {
                                const active = worksheetDragOverIndex === dropId;
                                return (
                                    <div
                                        onDrop={e => { e.preventDefault(); e.stopPropagation(); const id = e.dataTransfer.getData('compId'); if (id) doInsert(id, dropId); }}
                                        style={{
                                            height: active ? '20px' : '0px',
                                            overflow: 'hidden',
                                            borderRadius: '4px',
                                            background: active ? `${BRAND.teal}18` : 'transparent',
                                            border: active ? `2px dashed ${BRAND.teal}` : 'none',
                                            margin: active ? '2px 0' : '0',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'height 0.08s ease, margin 0.08s ease',
                                            pointerEvents: active ? 'auto' : 'none',
                                        }}
                                    >
                                        {active && <span style={{ fontSize: '0.55em', fontWeight: '800', color: BRAND.teal, letterSpacing: '0.03em' }}>▾ insert here</span>}
                                    </div>
                                );
                            };

                            let sectionLetter = 0;

                            return (
                                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>

                                    {/* Pages centered side by side */}
                                    <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', flex: 1, justifyContent: 'center', alignItems: 'flex-start' }}>
                                        {pages.map((pageSegs, pIdx) => {
                                            const pageDropId = `__page_${pIdx}_end__`;
                                            const isPageHovered = worksheetDragOverIndex && (
                                                worksheetDragOverIndex === pageDropId ||
                                                pageSegs.some(s => s.comp.id === worksheetDragOverIndex)
                                            );
                                            return (
                                            <div key={pIdx}
                                                onDragOver={e => { e.preventDefault(); setWorksheetDragOverIndex(calcInsertId(e, pageSegs, pIdx)); }}
                                                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setWorksheetDragOverIndex(null); }}
                                                onDrop={e => { e.preventDefault(); const id = e.dataTransfer.getData('compId'); if (id) doInsert(id, calcInsertId(e, pageSegs, pIdx)); }}
                                                style={{ width: `${PAGE_W}px`, height: `${PAGE_H}px`, flexShrink: 0, background: 'white', border: `1px solid ${isPageHovered ? BRAND.teal : '#D1D5DB'}`, borderRadius: '3px', boxShadow: '3px 4px 12px rgba(0,0,0,0.13)', padding: '8px 9px', boxSizing: 'border-box', overflow: 'hidden', transition: 'border-color 0.1s' }}>
                                                <div style={{ fontSize: '0.5em', color: '#94A3B8', fontWeight: '700', textAlign: 'right', marginBottom: '3px', letterSpacing: '0.04em' }}>PAGE {pIdx + 1}</div>
                                                {pIdx === 0 && (
                                                    <div style={{ height: `${HEADER_H}px`, background: '#F0FDFA', border: `1.5px solid ${BRAND.teal}`, borderRadius: '4px', marginBottom: `${GAP}px`, display: 'flex', alignItems: 'center', padding: '0 6px', gap: '4px' }}>
                                                        <div style={{ flex: 1 }}><div style={{ height: '4px', background: BRAND.teal, borderRadius: '2px', width: '55%', marginBottom: '3px' }} /><div style={{ height: '3px', background: '#B0D8DC', borderRadius: '2px', width: '38%' }} /></div>
                                                        <div style={{ width: '16px', height: '16px', background: '#E0F2F4', borderRadius: '3px' }} />
                                                    </div>
                                                )}
                                                {pageSegs.length === 0 ? (
                                                    <div style={{ minHeight: '80px', borderRadius: '6px', border: `2px dashed ${isPageHovered ? BRAND.teal : '#CBD5E1'}`, background: isPageHovered ? `${BRAND.teal}10` : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s' }}>
                                                        <span style={{ fontSize: '0.6em', color: isPageHovered ? BRAND.teal : '#CBD5E1', fontWeight: '700' }}>{isPageHovered ? '▾ insert here' : 'Drop module here'}</span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {pageSegs.map((seg, sIdx) => {
                                                            const { comp, showHeight, clipTop, isContinuation, isContinued } = seg;
                                                            const meta = COMP_META[comp.id] || { height: 52, color: '#F8F9FA', border: '#E0E0E0', preview: () => null };
                                                            const letter = isContinuation ? null : String.fromCharCode(65 + sectionLetter++);
                                                            const isDragging = worksheetDragIndex === comp.id;
                                                            // Next drop point: first non-continuation after this, or page end
                                                            const nextFresh = pageSegs.slice(sIdx + 1).find(s => !s.isContinuation);
                                                            const endDropId = nextFresh ? nextFresh.comp.id : pageDropId;
                                                            // Border radius: flat on cut edges
                                                            const brTop = isContinuation ? '0' : '5px';
                                                            const brBot = isContinued ? '0' : '5px';
                                                            return (
                                                                <React.Fragment key={`${comp.id}-${pIdx}-${sIdx}`}>
                                                                    {!isContinuation && mkDropBar(comp.id)}
                                                                    {/* Outer: carries the border — never overflow:hidden so border is always fully painted */}
                                                                    <div
                                                                        draggable={!isContinuation}
                                                                        onDragStart={!isContinuation ? (e => { e.dataTransfer.setData('compId', comp.id); e.dataTransfer.effectAllowed = 'move'; setWorksheetDragIndex(comp.id); setWorksheetDragOverIndex(null); }) : undefined}
                                                                        onDragEnd={!isContinuation ? (() => { setWorksheetDragIndex(null); setWorksheetDragOverIndex(null); }) : undefined}
                                                                        style={{
                                                                            border: `1.5px solid ${isDragging ? '#D1D5DB' : meta.border}`,
                                                                            borderRadius: `${brTop} ${brTop} ${brBot} ${brBot}`,
                                                                            background: isDragging ? '#F0F2F5' : meta.color,
                                                                            cursor: isContinuation ? 'default' : (isDragging ? 'grabbing' : 'grab'),
                                                                            opacity: isDragging ? 0.3 : 1,
                                                                            userSelect: 'none',
                                                                            marginBottom: isContinued ? '0' : `${GAP}px`,
                                                                        }}
                                                                    >
                                                                        {/* Inner viewport: clips content to showHeight without touching the border */}
                                                                        <div style={{ height: `${showHeight}px`, overflow: 'hidden', ...(isContinued ? { WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 10px), transparent 100%)', maskImage: 'linear-gradient(to bottom, black calc(100% - 10px), transparent 100%)' } : {}) }}>
                                                                            <div style={{ marginTop: `-${clipTop}px`, height: `${meta.height}px`, padding: '5px 6px' }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                                                                                    {letter && <span style={{ background: meta.border, color: 'white', fontFamily: 'Nunito,sans-serif', fontWeight: '800', fontSize: '0.52em', padding: '1px 5px', borderRadius: '999px' }}>{letter}</span>}
                                                                                    {!isContinuation && <><span style={{ flex: 1, fontSize: '0.58em', fontWeight: '800', color: BRAND.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{comp.label}</span>
                                                                                    <button type="button" onClick={() => doRemove(comp.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '0.7em', padding: '0 1px', lineHeight: 1, fontWeight: '700' }}>✕</button></>}
                                                                                </div>
                                                                                <div style={{ opacity: 0.65 }}>{meta.preview()}</div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {!isContinued && mkDropBar(endDropId)}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>

                                    {/* Side panel: removed/disabled components */}
                                    <div
                                        onDragEnter={e => { e.preventDefault(); setWorksheetDragOverIndex('__panel__'); }}
                                        onDragOver={e => { e.preventDefault(); setWorksheetDragOverIndex('__panel__'); }}
                                        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setWorksheetDragOverIndex(null); }}
                                        onDrop={e => { e.preventDefault(); const id = e.dataTransfer.getData('compId'); if (id) doRemove(id); setWorksheetDragIndex(null); setWorksheetDragOverIndex(null); }}
                                        style={{ width: '100px', flexShrink: 0, background: worksheetDragOverIndex === '__panel__' ? '#FEF2F2' : '#F8F9FA', border: worksheetDragOverIndex === '__panel__' ? `2px dashed ${BRAND.coral}` : '2px dashed #E2E8F0', borderRadius: '10px', padding: '8px 6px', minHeight: '120px', transition: 'all 0.15s' }}>
                                        <div style={{ fontSize: '0.65em', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', textAlign: 'center' }}>Not used</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            {disabledComps.map(comp => {
                                                const meta = COMP_META[comp.id] || { color: '#F8F9FA', border: '#E0E0E0' };
                                                return (
                                                    <div key={comp.id} draggable
                                                        onDragStart={e => { e.dataTransfer.setData('compId', comp.id); e.dataTransfer.effectAllowed = 'move'; setWorksheetDragIndex(comp.id); setWorksheetDragOverIndex(null); }}
                                                        onDragEnd={() => { setWorksheetDragIndex(null); setWorksheetDragOverIndex(null); }}
                                                        style={{ background: meta.color, border: `1.5px solid ${meta.border}`, borderRadius: '6px', padding: '5px 6px', cursor: 'grab', userSelect: 'none', opacity: worksheetDragIndex === comp.id ? 0.4 : 1 }}>
                                                        <div style={{ fontSize: '0.65em', fontWeight: '800', color: BRAND.charcoal, lineHeight: '1.3' }}>{comp.icon} {comp.label}</div>
                                                    </div>
                                                );
                                            })}
                                            {disabledComps.length === 0 && (
                                                <div style={{ fontSize: '0.65em', color: '#CBD5E1', textAlign: 'center', fontStyle: 'italic', paddingTop: '4px' }}>Drag here to remove</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </details>
                )}

                {/* SEN TUTORIALS SECTION */}
                {homework?.students && homework.students.length > 0 && (
                    <div>
                        {jcqMode && libraryPreview && !hideLibraryAddAction && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                    <button
                                        className="btn-base btn-coral"
                                        onClick={handleAddToDashboard}
                                        disabled={addToDashboardState.loading || addToDashboardState.done}
                                        style={{ opacity: addToDashboardState.loading || addToDashboardState.done ? 0.7 : 1 }}
                                        title="Add this lesson to the Extra Time Test"
                                    >
                                        {addToDashboardState.loading
                                            ? 'Adding...'
                                            : addToDashboardState.done
                                                ? '✅ Added to Test'
                                                : <><span style={{ color: 'white' }}>+</span> Add to Extra Time Test</>
                                        }
                                    </button>
                                    {addToDashboardState.error && (
                                        <span style={{ fontSize: '0.78rem', color: '#E07A5F' }}>{addToDashboardState.error}</span>
                                    )}
                                </div>
                            </div>
                        )}
                        {!jcqMode && !jcqEmbeddedBuildMode && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom:'12px', flexWrap:'wrap' }}>
                            <h2 style={{ color: BRAND.charcoal, fontWeight: '800', margin: 0 }}>Tutorials</h2>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                {!hasClickedIndividualGenerate && (studentTutorials.length === 0 || isOakHomework) && (homework.students || []).length > 1 && (homework.students || []).some(student => {
                                    const studentId = student?._id;
                                    if (!studentId) return false;
                                    const studentTutorial = studentTutorials.find(t => t.studentId === studentId);
                                    return !studentTutorial && !automationQueue[studentId];
                                }) && (
                                    <button
                                        className="btn-base btn-coral"
                                        data-guide="hd-generate-all"
                                        onClick={handleGenerateAll}
                                    >
                                        Generate All
                                    </button>
                                )}
                                {libraryPreview && !hideLibraryAddAction && studentTutorials.some(t => t.status === 'COMPLETED') && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                        <button
                                            className="btn-base btn-coral"
                                            onClick={handleAddToDashboard}
                                            disabled={addToDashboardState.loading || addToDashboardState.done}
                                            style={{ opacity: addToDashboardState.loading || addToDashboardState.done ? 0.7 : 1 }}
                                            title="Add this lesson plan to your dashboard"
                                        >
                                            {addToDashboardState.loading
                                                ? 'Adding...'
                                                : addToDashboardState.done
                                                    ? '✅ Added to Dashboard'
                                                    : <><span style={{ color: 'white' }}>+</span> Add to Dashboard</>
                                            }
                                        </button>
                                        {addToDashboardState.error && (
                                            <span style={{ fontSize: '0.78rem', color: '#E07A5F' }}>{addToDashboardState.error}</span>
                                        )}
                                    </div>
                                )}
                                {isSupplyTeacherMode && !libraryPreview && studentTutorials.some(t => t.status === 'COMPLETED') && (
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <button
                                            className="btn-base btn-teal"
                                            onClick={handleGetClassLink}
                                            title="Generate one shareable link for the whole class"
                                        >
                                            🔗 Tutorial Link
                                        </button>
                                        <button
                                            className="btn-base"
                                            onClick={handleOpenPrintAllModal}
                                            title="Print worksheets for all approved students"
                                            style={{ background: '#F0F2F5', color: BRAND.charcoal, minWidth: '140px' }}
                                        >
                                            🖨 Print All
                                        </button>
                                    </div>
                                )}
                                {!isSupplyTeacherMode && studentTutorials.some(t => t.status === 'COMPLETED') && (
                                    <button
                                        className="btn-base"
                                        onClick={handleOpenNonSupplyPrintAllModal}
                                        title="Print worksheets for all students"
                                        style={{ background: '#F0F2F5', color: BRAND.charcoal, minWidth: '140px' }}
                                    >
                                        🖨 Print All Worksheets
                                    </button>
                                )}
                            </div>
                        </div>
                        )}

                        {homework.students.map(student => {
                            const studentTutorial = studentTutorials.find(t => t.studentId?.toString() === student._id?.toString());
                            const studentSummary = studentTutorial ? tutorialAttemptSummaries[studentTutorial._id?.toString()] : null;
                            const deliveryStatus = studentTutorial ? tutorialDeliveryStatus[studentTutorial._id?.toString()] : null;
                            const isSent = !!deliveryStatus?.sent;
                            const isOpened = !!deliveryStatus?.opened;
                            
                            // Check automation status
                            const isAutomating = automationQueue[student._id];
                            
                            // Check Slideshow Status for Preview
                            const relevantSlideshows = studentSlideshows.filter(ss => ss.studentTutorialId.toString() === studentTutorial?._id.toString());
                            const expectedChunkCount = studentTutorial?.generatedContent?.length;
                            const isProcessingSlides = relevantSlideshows.some(ss => ['PENDING', 'PROCESSING'].includes(ss.status));
                            
                            // Determine current text
                            let currentProgressText = progressText[student._id] || 'Generating Tutorial...';
                            if (!isAutomating && isProcessingSlides) {
                                currentProgressText = 'Generating Slideshows...';
                            }
                            
                            let canPreview = false;

                            if (studentTutorial?.status === 'COMPLETED') {
                                if (libraryPreview) {
                                    // Library preview tutorials are always pipeline-generated and complete
                                    canPreview = true;
                                } else {
                                    const allSlidesComplete = relevantSlideshows.length > 0 && relevantSlideshows.every(ss => ss.status === 'COMPLETED');
                                    // Only enable preview if we have the expected number of slideshows and all are done
                                    if (expectedChunkCount && relevantSlideshows.length === expectedChunkCount && allSlidesComplete) {
                                        canPreview = true;
                                    }
                                }
                            }


                            const isExpanded = !!expandedTutorialRows[student._id];
                            const chunks = studentTutorial?.generatedContent || [];
                            const basePreviewSteps = buildPreviewStepShortcuts(studentTutorial, homework);
                            const rawPreviewSteps = jcqEmbeddedBuildMode
                                ? basePreviewSteps
                                : (previewStepPlans[student._id] || basePreviewSteps);
                            const selectedPreviewSteps = jcqEmbeddedBuildMode
                                ? filterPreviewStepsByStructure(rawPreviewSteps, structureConfig)
                                : rawPreviewSteps;
                            const enabledPreviewStepsParam = encodeURIComponent(selectedPreviewSteps.join(','));
                            const inlineTutorialPreview = libraryPreview && isExpanded && studentTutorial && (
                                <div style={{ border: '2px solid #75B9BE', borderTop: jcqEmbeddedBuildMode ? '1px solid #D6EDEF' : 'none', borderRadius: jcqEmbeddedBuildMode ? '0 0 12px 12px' : '0 0 12px 12px', overflow: 'hidden', marginTop: jcqEmbeddedBuildMode ? '15px' : 0 }}>
                                    {/* Step breadcrumbs — fast track preview */}
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap', padding: '15px 20px', backgroundColor: BRAND.cream, borderBottom: '1px solid #D6EDEF' }}>
                                        {selectedPreviewSteps.map((step, i, filtered) => {
                                            const requestedStep = previewJumpSteps[student._id];
                                            const currentStep = requestedStep && filtered.includes(requestedStep) ? requestedStep : filtered[0];
                                            const isCurrent = step === currentStep;
                                            return (
                                                <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <button
                                                        onClick={() => handleBreadcrumbClick(student._id, step)}
                                                        style={{ fontSize: '0.72em', fontWeight: '800', padding: '4px 10px', borderRadius: '999px', background: isCurrent ? BRAND.teal : '#F1F5F9', color: isCurrent ? 'white' : '#94A3B8', border: isCurrent ? `2px solid ${BRAND.teal}` : '2px solid transparent', cursor: 'pointer', transition: 'all 0.15s' }}>
                                                        {getStepLabel(step)}
                                                    </button>
                                                    {i < filtered.length - 1 && <span style={{ color: '#CBD5E1', fontSize: '0.8em' }}>›</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <iframe
                                        key={jcqEmbeddedBuildMode ? `preview-${studentTutorial._id}-${enabledPreviewStepsParam}` : studentTutorial._id}
                                        ref={el => iframeRefs.current[student._id] = el}
                                        src={`${ns}/student-tutorial-player/${studentTutorial._id}?previewMode=1&embedded=1${jcqEmbeddedBuildMode ? `&enabledSteps=${enabledPreviewStepsParam}` : ''}`}
                                        style={{ width: '100%', height: '800px', border: 'none', display: 'block' }}
                                        title="Tutorial Preview"
                                    />
                                </div>
                            );
                            const inlineWorksheetPreview = libraryPreview && expandedWorksheetRows[student._id] && studentTutorial && (
                                <WorksheetModal
                                    isOpen={true}
                                    inline={true}
                                    onClose={() => setExpandedWorksheetRows(prev => ({ ...prev, [student._id]: false }))}
                                    profileLabel={getProfileLabel(student)}
                                    homeworkTitle={homework?.title || ''}
                                    worksheetTitle={libraryLessonTitle ? `Lesson ${homework?.lessonNumber ?? ''}: ${libraryLessonTitle}` : ''}
                                    lessonNumber={homework?.lessonNumber}
                                    homeworkNumber={homework?.homeworkNumber}
                                    shapeIndex={profileShapeMap[student._id] ?? null}
                                    senCondition={getStudentSenCondition(student)}
                                    studentTutorial={studentTutorial}
                                    worksheetComponents={worksheetComponents.filter(c => availableWorksheetIds.has(c.id))}
                                    fallbackSequencing={oaFallbackSequencing}
                                    fallbackCategorisation={oaFallbackCategorisation}
                                    homeworkId={homeworkId}
                                    isContentLibrary={libraryPreview || isOakHomework}
                                />
                            );

                            if (jcqEmbeddedBuildMode && libraryPreview) {
                                return (
                                    <details
                                        key={student._id}
                                        open={isExpanded}
                                        onToggle={(e) => {
                                            const nextOpen = e.currentTarget.open;
                                            setExpandedTutorialRows(prev => ({ ...prev, [student._id]: nextOpen }));
                                            if (nextOpen) setExpandedWorksheetRows(prev => ({ ...prev, [student._id]: false }));
                                        }}
                                        className="dash-card"
                                        style={{ padding: '20px 15px', marginBottom: '20px', cursor: 'pointer', background: 'white' }}
                                    >
                                        <summary className="summary-with-icon" style={{ fontSize: '1.1em', fontWeight: '800', color: BRAND.charcoal }}>
                                            <span className="summary-chevron">▶</span>
                                            Test Preview
                                        </summary>
                                        <div style={{ marginTop: '15px', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
                                            {studentTutorial && chunks.length > 0 ? inlineTutorialPreview : (
                                                <p style={{ margin: 0, color: '#64748B', fontWeight: '700' }}>Test preview will appear once content has generated.</p>
                                            )}
                                            {inlineWorksheetPreview}
                                        </div>
                                    </details>
                                );
                            }

                            return (
                                <div key={student._id} style={{ marginBottom: '10px' }}>
                                    {/* Student row */}
                                    <div data-guide="hd-student-row" className="dash-card" style={{ padding: '12px 14px', marginBottom: 0, borderRadius: isExpanded ? '12px 12px 0 0' : undefined, background: isOpened ? '#DFF6E4' : (isSent ? '#E3F4F6' : 'white'), border: isOpened ? '2px solid #81C784' : (isSent ? '2px solid #75B9BE' : undefined), display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px' }}>
                                        <div style={{minWidth:0, flex:1}}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap:'wrap' }}>
                                                <span style={{fontSize:'1rem', fontWeight:'800', color: BRAND.charcoal}}>
                                                    {isSupplyTeacherMode ? getProfileLabel(student) : student.name}
                                                </span>
                                                {isSupplyTeacherMode && profileShapeMap[student._id] !== undefined && (() => {
                                                    const shapeData = PROFILE_SHAPES[profileShapeMap[student._id] % PROFILE_SHAPES.length];
                                                    return (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: shapeData.bg, border: `2px solid ${shapeData.color}`, borderRadius: '8px', padding: '3px', flexShrink: 0 }}>
                                                            <ShapeSVG name={shapeData.name} color={shapeData.color} size={22} />
                                                        </span>
                                                    );
                                                })()}
                                            </div>

                                            {studentTutorial && canPreview && !libraryPreview && (
                                                <div style={{ marginTop: '8px' }}>
                                                    <span style={{fontSize:'0.72rem', fontWeight:'800', color:'#475569', background:'#F1F5F9', border:'1px solid #CBD5E1', borderRadius:'999px', padding:'4px 10px', whiteSpace:'nowrap', display:'inline-block'}}>
                                                        {studentSummary?.totalCompleted > 0
                                                            ? 'COMPLETED'
                                                            : (isOpened
                                                                ? 'ATTEMPTED'
                                                                : (isSent ? 'SENT' : 'NOT SENT'))}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div style={{display:'flex', alignItems:'center', gap:'6px', justifyContent:'flex-end', flexWrap:'wrap', flexShrink:0}}>
                                            {!studentTutorial && !isAutomating && (
                                                <button className="btn-base btn-coral" onClick={() => handleSmartGenerate(student)}>
                                                    Generate
                                                </button>
                                            )}

                                            {(isAutomating || (studentTutorial && ['PENDING', 'PROCESSING'].includes(studentTutorial.status)) || isProcessingSlides) && !canPreview && (
                                                <div style={{ minWidth: '220px' }}>
                                                    <div style={{ marginBottom: '6px', fontWeight: '700', color: BRAND.teal, fontSize:'0.85em', height:'16px', lineHeight:'16px', whiteSpace:'nowrap' }}>{currentProgressText}</div>
                                                    <div className="progress-bar-container"><div className="progress-bar-fill"></div></div>
                                                </div>
                                            )}

                                            {studentTutorial && studentTutorial.status === 'FAILED' && (
                                                <button className="btn-base" onClick={() => handleSmartGenerate(student)} style={{background:'white', border:`2px solid ${BRAND.danger}`, color:BRAND.danger}}>
                                                    Retry
                                                </button>
                                            )}

                                            {studentTutorial && studentTutorial.status === 'COMPLETED' && !canPreview && !isProcessingSlides && !isAutomating && relevantSlideshows.some(ss => ss.status === 'FAILED') && (
                                                <button className="btn-base" onClick={() => performGenerateStudentSlideshows(studentTutorial._id, TEXT_MODEL, IMAGE_MODEL)} style={{ minWidth:'140px', background: BRAND.coral, color: 'white' }}>
                                                    Retry
                                                </button>
                                            )}

                                            {studentTutorial && studentTutorial.status === 'COMPLETED' && canPreview && (
                                                <>
                                                    {isSupplyTeacherMode && !libraryPreview && (
                                                        <button
                                                            className="btn-base"
                                                            onClick={() => setReviewModal({
                                                                isOpen: true,
                                                                tutorial: studentTutorial,
                                                                studentName: getProfileLabel(student),
                                                                slideshows: relevantSlideshows,
                                                                requireApproval: true,
                                                                onApprove: (followOnTask) => handleApproveSupplyTutorial(studentTutorial._id?.toString(), followOnTask),
                                                                onReport: null,
                                                                tutorialId: studentTutorial._id?.toString()
                                                            })}
                                                            style={{
                                                                background: approvedTutorialIds[studentTutorial._id?.toString()] ? '#ECFDF5' : '#F0F2F5',
                                                                color: approvedTutorialIds[studentTutorial._id?.toString()] ? '#10B981' : BRAND.charcoal,
                                                                border: approvedTutorialIds[studentTutorial._id?.toString()] ? '2px solid #10B981' : undefined,
                                                                minWidth: '120px'
                                                            }}
                                                        >
                                                            {approvedTutorialIds[studentTutorial._id?.toString()] ? '✓ Approved' : 'Approve'}
                                                        </button>
                                                    )}
                                                    {!isSupplyTeacherMode && (
                                                        <button data-guide="hd-link" className="btn-base" onClick={() => handleSendTutorial(studentTutorial._id, student.name, student.email, studentTutorial, relevantSlideshows)} style={{background:'#FFF8E1', color: BRAND.charcoal, minWidth:'120px'}}>
                                                            Link
                                                        </button>
                                                    )}
                                                    {!libraryPreview && (
                                                        <button data-guide="hd-preview" className="btn-base btn-teal" onClick={() => handlePreviewTutorial(studentTutorial._id)} style={{minWidth:'120px'}}>
                                                            Tutorial
                                                        </button>
                                                    )}
                                                    {libraryPreview && chunks.length > 0 && (
                                                        <button
                                                            className="btn-base btn-teal"
                                                            onClick={() => {
                                                                setExpandedTutorialRows(prev => ({ ...prev, [student._id]: !prev[student._id] }));
                                                                setExpandedWorksheetRows(prev => ({ ...prev, [student._id]: false }));
                                                            }}
                                                            style={{ minWidth: '120px' }}
                                                        >
                                                            {isExpanded ? 'Hide Tutorial ▲' : 'Tutorial ▼'}
                                                        </button>
                                                    )}
                                                    {!isSupplyTeacherMode && (
                                                        <button data-guide="hd-progress" className="btn-base" onClick={() => handleReviewAttempts(studentTutorial._id, student.name)} style={{background:'#F0F2F5', color: BRAND.charcoal, minWidth:'120px'}}>
                                                            Progress
                                                        </button>
                                                    )}
                                                    {libraryPreview ? (
                                                        <button
                                                            className="btn-base"
                                                            onClick={() => {
                                                                setExpandedWorksheetRows(prev => ({ ...prev, [student._id]: !prev[student._id] }));
                                                                setExpandedTutorialRows(prev => ({ ...prev, [student._id]: false }));
                                                            }}
                                                            style={{background:'#F0F2F5', color: BRAND.charcoal, minWidth:'120px'}}
                                                        >
                                                            {expandedWorksheetRows[student._id] ? 'Hide Worksheet ▲' : 'Worksheet ▼'}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn-base"
                                                            onClick={() => setWorksheetModal({ isOpen: true, profileLabel: isSupplyTeacherMode ? getProfileLabel(student) : student.name, studentTutorial, shapeIndex: isSupplyTeacherMode ? (profileShapeMap[student._id] ?? null) : null, senCondition: getStudentSenCondition(student) })}
                                                            style={{background:'#F0F2F5', color: BRAND.charcoal, minWidth:'120px'}}
                                                        >
                                                            Worksheet
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Inline tutorial player — library preview only */}
                                    {inlineTutorialPreview}

                                    {/* Inline worksheet — library preview only */}
                                    {inlineWorksheetPreview}
                                </div>
                            );
                        })}
                    </div>
                )}

                {scaffoldingModal.isOpen && (
                    <div
                        onClick={() => setScaffoldingModal({ isOpen: false, title: '', content: null })}
                        style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(45, 64, 66, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                        }}
                    >
                        <div
                            className="dash-card"
                            onClick={(e) => e.stopPropagation()}
                            style={{ padding: '22px', width: scaffoldingModal.content?.hasEal ? '520px' : '420px', maxWidth: '94%', background: 'white' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h3 style={{ margin: 0, color: BRAND.charcoal }}>{scaffoldingModal.title}</h3>
                                <button className="btn-base" onClick={() => setScaffoldingModal({ isOpen: false, title: '', content: null })} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>Close</button>
                            </div>

                            <div style={{ background: BRAND.cream, border: `2px solid ${BRAND.charcoal}`, borderRadius: '12px', padding: '14px' }}>
                                <div style={{ marginBottom: '8px', color: BRAND.charcoal }}><strong>Name:</strong> {scaffoldingModal.content?.name}</div>
                                <div style={{ marginBottom: '8px', color: BRAND.charcoal }}><strong>Age:</strong> {scaffoldingModal.content?.age}</div>
                                <div style={{ marginBottom: '8px', color: BRAND.charcoal }}><strong>Reading Age:</strong> {scaffoldingModal.content?.readingAge}</div>
                                <div style={{ marginBottom: '8px', color: BRAND.charcoal }}><strong>SEN Conditions:</strong> {scaffoldingModal.content?.sen}</div>
                                {scaffoldingModal.content?.hasEal && (
                                    <>
                                        <div style={{ marginBottom: '8px', color: BRAND.charcoal }}><strong>EAL Student:</strong> {scaffoldingModal.content?.eal}</div>
                                        <div style={{ marginBottom: '8px', color: BRAND.charcoal }}><strong>Primary Language:</strong> {scaffoldingModal.content?.primaryLanguage}</div>
                                        <div style={{ marginBottom: '8px', color: BRAND.charcoal }}><strong>Translation Direction:</strong> {scaffoldingModal.content?.languageDirection}</div>
                                    </>
                                )}
                                <div style={{ marginBottom: '0', color: BRAND.charcoal }}><strong>Audio Narration:</strong> {scaffoldingModal.content?.audioNarration}</div>
                            </div>
                        </div>
                    </div>
                )}

                <GlossaryDirectionModal
                    isOpen={glossaryDirectionModal.isOpen}
                    student={glossaryDirectionModal.student}
                    selection={glossaryDirectionModal.selectedMode}
                    onSelectionChange={(value) => setGlossaryDirectionModal(prev => ({ ...prev, selectedMode: value }))}
                    onCancel={closeGlossaryDirectionModal}
                    onConfirm={handleConfirmGlossaryDirection}
                />

                <WorksheetModal
                    isOpen={worksheetModal.isOpen}
                    onClose={() => setWorksheetModal({ isOpen: false, profileLabel: '', studentTutorial: null, shapeIndex: null, senCondition: '' })}
                    profileLabel={worksheetModal.profileLabel}
                    homeworkTitle={homework?.title || ''}
                    worksheetTitle={(libraryPreview && libraryLessonTitle) ? `Lesson ${homework?.lessonNumber ?? ''}: ${libraryLessonTitle}` : ''}
                    lessonNumber={homework?.lessonNumber}
                    homeworkNumber={homework?.homeworkNumber}
                    shapeIndex={worksheetModal.shapeIndex}
                    senCondition={worksheetModal.senCondition}
                    studentTutorial={worksheetModal.studentTutorial}
                    worksheetComponents={worksheetComponents.filter(c => availableWorksheetIds.has(c.id))}
                    fallbackSequencing={oaFallbackSequencing}
                    fallbackCategorisation={oaFallbackCategorisation}
                    homeworkId={homeworkId}
                    isContentLibrary={libraryPreview || isOakHomework}
                />

                {classLinkModal.isOpen && (
                    <div
                        onClick={() => setClassLinkModal({ isOpen: false, url: '', loading: false })}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45,64,66,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    >
                        <div
                            onClick={e => e.stopPropagation()}
                            className="dash-card"
                            style={{ padding: '28px', width: '520px', maxWidth: '94%', background: 'white' }}
                        >
                            <h3 style={{ margin: '0 0 6px 0', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif' }}>🔗 Tutorial Link</h3>
                            <p style={{ margin: '0 0 20px 0', fontSize: '0.88em', color: '#5D7375' }}>
                                Share this single URL with the whole class. Students pick their shape and go straight to their tutorial. Only approved tutorials are accessible.
                            </p>

                            {/* Shape legend */}
                            {homework?.students && (() => {
                                const sorted = [...homework.students].sort((a, b) => {
                                    const ka = getProfileKey(a), kb = getProfileKey(b);
                                    if (ka === '__mainstream__') return -1;
                                    if (kb === '__mainstream__') return 1;
                                    return ka.localeCompare(kb);
                                });
                                return (
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
                                        {sorted.map((student, i) => {
                                            const shapeData = PROFILE_SHAPES[i % PROFILE_SHAPES.length];
                                            const isApproved = approvedTutorialIds[studentTutorials.find(t => t.studentId === student._id)?._id?.toString()];
                                            return (
                                                <div key={student._id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: shapeData.bg, border: `2px solid ${isApproved ? shapeData.color : '#D1D5DB'}`, borderRadius: '10px', opacity: isApproved ? 1 : 0.5 }}>
                                                    <ShapeSVG name={shapeData.name} color={isApproved ? shapeData.color : '#9CA3AF'} size={18} />
                                                    <span style={{ fontSize: '0.82em', fontWeight: '700', color: isApproved ? BRAND.charcoal : '#9CA3AF' }}>{getProfileLabel(student)}</span>
                                                    {!isApproved && <span style={{ fontSize: '0.72em', color: '#9CA3AF' }}>not approved</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}

                            {classLinkModal.loading ? (
                                <p style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Generating link...</p>
                            ) : (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        readOnly
                                        value={classLinkModal.url}
                                        style={{ flex: 1, padding: '10px 12px', border: `2px solid ${BRAND.teal}`, borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.85em', background: '#F0FDFA', color: BRAND.charcoal }}
                                    />
                                    <button
                                        className="btn-base btn-teal"
                                        onClick={() => { navigator.clipboard.writeText(classLinkModal.url); }}
                                    >
                                        Copy
                                    </button>
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                                <button className="btn-base" onClick={() => setClassLinkModal({ isOpen: false, url: '', loading: false })} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>Close</button>
                            </div>
                        </div>
                    </div>
                )}

                {printAllModal.isOpen && (
                    <div
                        onClick={() => setPrintAllModal({ isOpen: false, selections: {}, answerKeySelected: false, answerKeyCopies: 1 })}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45,64,66,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    >
                        <div
                            onClick={e => e.stopPropagation()}
                            className="dash-card"
                            style={{ padding: '28px', width: '540px', maxWidth: '94%', background: 'white' }}
                        >
                            <h3 style={{ margin: '0 0 6px 0', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif' }}>🖨 Print All Worksheets</h3>
                            <p style={{ margin: '0 0 16px 0', fontSize: '0.88em', color: '#5D7375' }}>
                                {printAllModal.supplyMode
                                    ? 'Select which approved worksheets to print and how many copies of each.'
                                    : 'Select which students\' worksheets to print.'}
                            </p>

                            {printAllModal.supplyMode && homework?.students && (() => {
                                const sorted = [...homework.students].sort((a, b) => {
                                    const ka = getProfileKey(a), kb = getProfileKey(b);
                                    if (ka === '__mainstream__') return -1;
                                    if (kb === '__mainstream__') return 1;
                                    return ka.localeCompare(kb);
                                });
                                return (
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                                        {sorted.map((student, i) => {
                                            const shapeData = PROFILE_SHAPES[i % PROFILE_SHAPES.length];
                                            const isApproved = approvedTutorialIds[studentTutorials.find(t => t.studentId === student._id)?._id?.toString()];
                                            return (
                                                <div key={student._id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: shapeData.bg, border: `2px solid ${isApproved ? shapeData.color : '#D1D5DB'}`, borderRadius: '10px', opacity: isApproved ? 1 : 0.5 }}>
                                                    <ShapeSVG name={shapeData.name} color={isApproved ? shapeData.color : '#9CA3AF'} size={18} />
                                                    <span style={{ fontSize: '0.82em', fontWeight: '700', color: isApproved ? BRAND.charcoal : '#9CA3AF' }}>{getProfileLabel(student)}</span>
                                                    {!isApproved && <span style={{ fontSize: '0.72em', color: '#9CA3AF' }}>not approved</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}

                            {printAllModal.supplyMode && <hr style={{ border: 'none', borderTop: '1.5px solid #E5E7EB', margin: '0 0 16px 0' }} />}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', maxHeight: '340px', overflowY: 'auto', paddingRight: '4px' }}>
                                {Object.entries(printAllModal.selections).map(([key, group]) => {
                                    const { label, tutorial, shapeIndex, selected, copies } = group;
                                    const hasContent = !!tutorial;
                                    const shapeData = printAllModal.supplyMode ? PROFILE_SHAPES[(shapeIndex ?? 0) % PROFILE_SHAPES.length] : null;
                                    const toggle = () => {
                                        if (!hasContent) return;
                                        setPrintAllModal(prev => ({ ...prev, selections: { ...prev.selections, [key]: { ...group, selected: !selected } } }));
                                    };
                                    const setCopies = (delta) => {
                                        const next = Math.max(0, Math.min(30, (copies ?? 1) + delta));
                                        setPrintAllModal(prev => ({ ...prev, selections: { ...prev.selections, [key]: { ...group, copies: next } } }));
                                    };
                                    return (
                                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: hasContent ? (selected ? '#F0FDFA' : '#F9FAFB') : '#F9FAFB', border: `1.5px solid ${hasContent && selected ? BRAND.teal : '#E5E7EB'}`, borderRadius: '10px', opacity: hasContent ? 1 : 0.45 }}>
                                            <input type="checkbox" checked={hasContent && selected} onChange={toggle} disabled={!hasContent} style={{ width: '16px', height: '16px', cursor: hasContent ? 'pointer' : 'not-allowed', accentColor: BRAND.teal, flexShrink: 0 }} />
                                            {printAllModal.supplyMode && shapeData && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: shapeData.bg, border: `2px solid ${hasContent ? shapeData.color : '#D1D5DB'}`, borderRadius: '8px', padding: '3px', flexShrink: 0 }}>
                                                <ShapeSVG name={shapeData.name} color={hasContent ? shapeData.color : '#9CA3AF'} size={18} />
                                            </span>
                                            )}
                                            <span style={{ flex: 1, fontWeight: '700', fontSize: '0.9em', color: hasContent ? BRAND.charcoal : '#9CA3AF' }}>
                                                {label}
                                                {!hasContent && <span style={{ fontWeight: '500', fontSize: '0.82em', color: '#9CA3AF', marginLeft: '6px' }}>not generated</span>}
                                            </span>
                                            {printAllModal.supplyMode && hasContent && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                                    <span style={{ fontSize: '0.78em', color: '#5D7375', fontWeight: '600' }}>Copies:</span>
                                                    <button onClick={() => setCopies(-1)} className="btn-base" style={{ padding: '2px 8px', background: '#F0F2F5', color: BRAND.charcoal, minWidth: 'unset', fontSize: '1em', lineHeight: 1 }}>−</button>
                                                    <span style={{ fontWeight: '800', fontSize: '0.9em', minWidth: '20px', textAlign: 'center' }}>{copies ?? 1}</span>
                                                    <button onClick={() => setCopies(1)} className="btn-base" style={{ padding: '2px 8px', background: '#F0F2F5', color: BRAND.charcoal, minWidth: 'unset', fontSize: '1em', lineHeight: 1 }}>+</button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Master Answer Key row */}
                                {(() => {
                                    const anySelected = Object.values(printAllModal.selections).some(s => s.selected);
                                    const anyApproved = anySelected;
                                    const akSelected = printAllModal.answerKeySelected;
                                    const akCopies = printAllModal.answerKeyCopies || 1;
                                    const toggleAk = () => {
                                        if (!anySelected) return;
                                        setPrintAllModal(prev => ({ ...prev, answerKeySelected: !prev.answerKeySelected }));
                                    };
                                    const setAkCopies = (delta) => {
                                        const next = Math.max(0, Math.min(30, akCopies + delta));
                                        setPrintAllModal(prev => ({ ...prev, answerKeyCopies: next }));
                                    };
                                    return (
                                        <>
                                            <hr style={{ border: 'none', borderTop: '1.5px solid #E5E7EB', margin: '4px 0' }} />
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: anySelected ? (akSelected ? '#FFFBEB' : '#F9FAFB') : '#F9FAFB', border: `1.5px solid ${anySelected && akSelected ? '#F2CC8F' : '#E5E7EB'}`, borderRadius: '10px', opacity: anySelected ? 1 : 0.45 }}>
                                                <input type="checkbox" checked={anySelected && akSelected} onChange={toggleAk} disabled={!anySelected} style={{ width: '16px', height: '16px', cursor: anySelected ? 'pointer' : 'not-allowed', accentColor: '#F2CC8F', flexShrink: 0 }} />
                                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#FFFBEB', border: `2px solid ${anySelected ? '#F2CC8F' : '#D1D5DB'}`, borderRadius: '8px', padding: '3px', flexShrink: 0, fontSize: '1em', width: '26px', height: '26px' }}>🗝</span>
                                                <span style={{ flex: 1, fontWeight: '700', fontSize: '0.9em', color: anySelected ? BRAND.charcoal : '#9CA3AF' }}>
                                                    Master Answer Key
                                                    <span style={{ display: 'block', fontWeight: '500', fontSize: '0.82em', color: '#5D7375' }}>{printAllModal.supplyMode ? 'Valid for all profiles' : 'Valid for all students'}</span>
                                                </span>
                                                {anySelected && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                                        <span style={{ fontSize: '0.78em', color: '#5D7375', fontWeight: '600' }}>Copies:</span>
                                                        <button onClick={() => setAkCopies(-1)} className="btn-base" style={{ padding: '2px 8px', background: '#F0F2F5', color: BRAND.charcoal, minWidth: 'unset', fontSize: '1em', lineHeight: 1 }}>−</button>
                                                        <span style={{ fontWeight: '800', fontSize: '0.9em', minWidth: '20px', textAlign: 'center' }}>{akCopies}</span>
                                                        <button onClick={() => setAkCopies(1)} className="btn-base" style={{ padding: '2px 8px', background: '#F0F2F5', color: BRAND.charcoal, minWidth: 'unset', fontSize: '1em', lineHeight: 1 }}>+</button>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                <button className="btn-base" onClick={() => setPrintAllModal({ isOpen: false, selections: {}, answerKeySelected: false, answerKeyCopies: 1 })} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>Close</button>
                                <button
                                    className="btn-base btn-teal"
                                    onClick={() => {
                                        handlePrintAllWorksheets(printAllModal.selections);
                                        if (printAllModal.answerKeySelected) {
                                            const copies = printAllModal.answerKeyCopies || 1;
                                            for (let i = 0; i < copies; i++) handlePrintMasterAnswerKey(printAllModal.selections);
                                        }
                                        setPrintAllModal({ isOpen: false, selections: {}, answerKeySelected: false, answerKeyCopies: 1 });
                                    }}
                                    disabled={!Object.values(printAllModal.selections).some(s => s.selected)}
                                    style={{ opacity: Object.values(printAllModal.selections).some(s => s.selected) ? 1 : 0.5, cursor: Object.values(printAllModal.selections).some(s => s.selected) ? 'pointer' : 'not-allowed' }}
                                >
                                    🖨 Print {Object.values(printAllModal.selections).filter(s => s.selected).reduce((sum, s) => sum + (s.copies ?? 1), 0)} worksheet(s){printAllModal.answerKeySelected ? ` + ${printAllModal.answerKeyCopies ?? 1} answer key(s)` : ''}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {linkModal.isOpen && (
                    <div
                        onClick={() => setLinkModal({ isOpen: false, url: '', studentName: '', studentTutorialId: '', email: '', sentEmail: '', sendingEmail: false, error: '', emailError: '' })}
                        style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(45, 64, 66, 0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                        }}
                    >
                        <div
                            className="dash-card"
                            onClick={(e) => e.stopPropagation()}
                            style={{ padding: '22px', width: '560px', maxWidth: '94%', background: 'white' }}
                        >
                            <h3 style={{ marginTop: 0, marginBottom: '10px', color: BRAND.charcoal }}>
                                {linkModal.error ? 'Could not create link' : 'Tutorial Link Ready'}
                            </h3>
                            <p style={{ marginTop: 0, color: '#4B5563' }}>
                                {linkModal.error
                                    ? linkModal.error
                                    : `Link copied for ${linkModal.studentName || 'student'}. You can also copy it again below, or email it directly.`}
                            </p>

                            {!linkModal.error && (
                                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '12px', wordBreak: 'break-all', color: BRAND.charcoal, marginBottom: '12px' }}>
                                    {linkModal.url}
                                </div>
                            )}

                            {!linkModal.error && (
                                <div style={{ marginBottom: '14px' }}>
                                    <div style={{ marginBottom: '6px', color: '#4B5563', fontWeight: '700' }}>Student Email</div>
                                    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '12px', color: BRAND.charcoal, marginBottom: '10px' }}>
                                        <input
                                            type="email"
                                            value={linkModal.email}
                                            onChange={(e) => setLinkModal(prev => ({ ...prev, email: e.target.value, sentEmail: '', emailError: '' }))}
                                            placeholder="student@example.com"
                                            style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', color: BRAND.charcoal, fontSize: '1rem' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                                        <button
                                            className="btn-base btn-coral"
                                            onClick={handleEmailTutorialLinkFromModal}
                                            disabled={linkModal.sendingEmail}
                                            style={{ whiteSpace: 'nowrap', minWidth: '108px' }}
                                        >
                                            {linkModal.sendingEmail ? 'Sending...' : 'Email Link'}
                                        </button>
                                    </div>
                                    {linkModal.emailError && (
                                        <div style={{ marginTop: '6px', color: BRAND.danger, fontWeight: '700', fontSize: '0.85rem' }}>{linkModal.emailError}</div>
                                    )}
                                    {linkModal.sentEmail && (
                                        <div style={{ marginTop: '6px', color: '#2F6D3A', fontWeight: '700', fontSize: '0.85rem' }}>Link emailed to {linkModal.sentEmail}.</div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                {!linkModal.error && (
                                    <button
                                        className="btn-base"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(linkModal.url);
                                            } catch (e) {
                                                console.error('Failed to copy link from modal:', e);
                                            }
                                        }}
                                        style={{ background: '#F0F2F5', color: BRAND.charcoal }}
                                    >
                                        Copy Again
                                    </button>
                                )}
                                <button
                                    className="btn-base btn-teal"
                                    onClick={() => setLinkModal({ isOpen: false, url: '', studentName: '', studentTutorialId: '', email: '', sentEmail: '', sendingEmail: false, error: '', emailError: '' })}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <TutorialReviewModal
                    isOpen={reviewModal.isOpen}
                    onClose={() => setReviewModal({ isOpen: false, tutorial: null, studentName: '', slideshows: [], requireApproval: false, onApprove: null, onReport: null, tutorialId: null })}
                    tutorial={reviewModal.tutorial}
                    studentName={reviewModal.studentName}
                    slideshows={reviewModal.slideshows}
                    requireApproval={reviewModal.requireApproval}
                    alreadyApproved={!!approvedTutorialIds[reviewModal.tutorialId]}
                    existingRejectionReason={rejectedTutorials[reviewModal.tutorialId]?.reason || ''}
                    existingFollowOnTask={reviewModal.tutorial?.followOnTask || ''}
                    onApprove={reviewModal.onApprove}
                    onReport={reviewModal.onReport}
                />

                <AttemptsReviewModal
                    isOpen={attemptsModal.isOpen}
                    onClose={() => setAttemptsModal({ isOpen: false, studentName: '', attempts: [], studentTutorialId: null })}
                    studentName={attemptsModal.studentName}
                    attempts={attemptsModal.attempts}
                    tutorial={studentTutorials.find(st => String(st._id) === String(attemptsModal.studentTutorialId)) || null}
                    lessonNumber={homework?.lessonNumber}
                    homeworkNumber={homework?.homeworkNumber}
                    homeworkId={homeworkId}
                    isContentLibrary={libraryPreview || isOakHomework}
                />
            </div>
        </div>
        {!jcqEmbeddedBuildMode && guideConfig.isOpen && (
            <HomeworkDetailGuideOverlay
                onClose={handleGuideClose}
                mode={guideConfig.mode}
                autoStep={guideConfig.autoStep}
                hasTutorials={hasTutorialsGenerated}
                onHeightChange={setGuideExtendedHeight}
            />
        )}
        </>
    );
};

export default DemoHomeworkDetail;
